import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import {
  AbstractMesh,
  Animation,
  ArcRotateCamera,
  Color3,
  Color4,
  CubeTexture,
  DynamicTexture,
  Engine,
  HemisphericLight,
  Mesh,
  InstancedMesh,
  MeshBuilder,
  Material,
  PointerEventTypes,
  Quaternion,
  Scene,
  SceneLoader,
  StandardMaterial,
  Tools,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import { Subscription, firstValueFrom, of } from 'rxjs';
import { ThreeIntegrationService } from '../../../core/services/three-js-building.service';
import { BuildingModelAssetsService } from '../../../core/services/building-model-assets.service';
import { BuildingModelAssetBundle } from '../../../core/models/building-model-assets.model';
import { AlertSpritePlacement, BabylonVisualizeService } from '../../../core/services/babylon-visualize.service';
import { LiveUserAssetConfig, LiveUserAssetService, RoomOccupancy } from '../../../core/services/live-user-asset.service';
import {
  ALERT_ROOM_COLOR_HEX,
  mergeRoomStatusColors,
  resolveRoomStatusTier,
  RoomStatusTier,
  RoomTintTier,
  ViewerRoomStatusColors,
} from '../../../core/utils/room-status-color.resolver';
import { SessionOptionsService } from '../../../core/services/session-option.service';
import { BuildingGeoPlacement, GeoAnchor } from '../../../core/utils/building-georef.util';
import { AssetPlacementUtil, FloorBounds, AnchorCategory } from '../../../core/utils/asset-placement.util';
import {
  BuildingModelMetadata,
  FloorMeshGroup,
  RoomMeshGroup,
  BeaconMeshGroup,
  ViewerUserRecord,
  ViewerRoomStatusCounts,
  ViewerLiveUserData,
  ViewerAlertRecord,
  ViewerVisualConfig,
  PlaceholderAssetMapping,
  LoadedAsset,
} from '../../../core/interfaces/building-3d-viewer-interface.interface';

@Component({
  selector: 'app-building-3d-viewer',
  standalone: true,
  imports: [],
  templateUrl: './building-3d-viewer.component.html',
  styleUrl: './building-3d-viewer.component.css',
  providers: [LiveUserAssetService],

})

export class Building3dViewerComponent implements AfterViewInit, OnDestroy, OnChanges, OnInit {

  @Input() embedded = false;
  /** Transparent canvas/scene clear color without embedded map-overlay behavior. */
  @Input() transparentBackground = false;
  @Input() showHeader = true;
  @Input() active = true;
  @Input() liveUserData: ViewerLiveUserData | null = null;
  @Input() alertRecords: ViewerAlertRecord[] = [];
  /** When set, skips `BuildingModelAssetsService` and uses this bundle (GLB only). */
  @Input() assetBundle: BuildingModelAssetBundle | null = null;
  /** Passed to asset service when `assetBundle` is null (e.g. perimeter / facility id) */
  @Input() assetResolutionKey: string | null = null;
  /** Multiple asset bundles for showing all buildings together */
  @Input() assetBundles: BuildingModelAssetBundle[] = [];
  /** Multiple resolution keys for showing all buildings together */
  @Input() assetResolutionKeys: string[] = [];
  /** Cesium map anchor — same as Three.js buildingGeographicCenter. */
  @Input() mapSyncAnchor: GeoAnchor | null = null;
  /** Per-building ENU offsets keyed by perimeter id. */
  @Input() buildingGeoPlacements: BuildingGeoPlacement[] = [];
  /** Optional overrides for live user human asset rendering */
  @Input() liveUserAssetConfig: Partial<LiveUserAssetConfig> | null = null;
  /** Optional hex colors for danger / safe / unknown room tints (defaults from mergeRoomStatusColors) */
  @Input() roomStatusColors: ViewerRoomStatusColors | null = null;
  /** When true, flip the 3D view vertically (upside down) for the floor overlay. */
  @Input() flipVertical = false;
  /** When true, renders beacon hardware models and floating Bluetooth badges on the 3D floor. */
  @Input() showBeacons = false;
  /** Floor map entity containing perimeters and beacon configurations. */
  @Input() floorMapEntity: any = null;
  /** Optional direct beacon list override. */
  @Input() beacons: any[] = [];
  @Output() metadataParsed = new EventEmitter<BuildingModelMetadata>();
  @Output() loadingChange = new EventEmitter<boolean>();
  @Output() floorHighlighted = new EventEmitter<{ floorId: string; highlighted: boolean }>();
  @Output() floorClicked = new EventEmitter<{ floorId: string; floorNumber?: number; mesh: AbstractMesh }>();
  @Output() roomClicked = new EventEmitter<{ roomId: string; floorId: string; mesh: AbstractMesh; roomName?: string; roomType?: string; metadata?: Record<string, unknown> }>();
  @Output() userClicked = new EventEmitter<{ userId: string; roomId: string; mesh: AbstractMesh }>();
  roomHighlightBox: any;
  /**
   * Public method to isolate and focus on a specific floor in 3D view
   * Called from parent component when floor is selected
   */

  public isolateFloor(floorId: string): void {
    this.isolateAndFocusFloor(floorId);
  }

  public isolateFloorByReference(floorRef: string): void {
    const floorKey = this.resolveCanonicalFloorKeyForHighlight(floorRef) ?? floorRef;
    this.isolateAndFocusFloor(floorKey);

  }

  /**
   * Public method to restore all floors to visibility
   * Called from parent component to undo floor isolation
   */
  public restoreFloors(): void {
    this.restoreAllFloors();

  }

  /** Resize Babylon canvas to match host container (sidebar overlay mode). */
  public refreshViewport(): void {
    this.fitCanvasToContainer();
    this.ensureCrispResize();
  }

  private fitCanvasToContainer(): void {
    if (!this.transparentBackground || this.embedded) {
      return;
    }
    const canvas = this.renderCanvas?.nativeElement;
    const shell = canvas?.parentElement;
    if (!canvas || !shell) {
      return;
    }
    const rect = shell.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) {
      return;
    }
    const dpr = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = Math.max(1, Math.round(rect.width * dpr));
    canvas.height = Math.max(1, Math.round(rect.height * dpr));
  }

  /** Load a bundle that was already fetched elsewhere (e.g. sidebar → fullscreen). */
  public reloadWithAssetBundle(bundle: BuildingModelAssetBundle): void {
    this.assetBundle = bundle;
    this.resolveAndLoadAssets();
  }

  /** Sidebar / fullscreen blender floor view — keep loader until floor is framed. */
  private isBlenderFloorPresentation(): boolean {
    return this.transparentBackground && !this.embedded;
  }

  private finishBlenderFloorPresentation(): void {
    if (!this.isBlenderFloorPresentation() || this.isDestroyed) {
      return;
    }
    this.ensureCrispResize();
    this.scene?.render();
    this.setModelLoading(false);
  }

  public completeFloorPresentation(): void {
    this.renderAlertAssets();
  }

  /**

   * Public method to isolate a specific room by ID

   */

  public isolateRoom(roomId: string): void {
    if (!this.scene) return;
    const normalizedRoomId = this.normalizeEntityId(roomId);
    const roomMeshes = new Set<AbstractMesh>();
    Object.values(this.parsedMetadata.roomMeshes || {}).forEach((group) => {
      const groupIds = [group.roomId, ...(group.perimeterIds || [])]
        .map((id) => this.normalizeEntityId(id))
        .filter((id) => !!id);
      if (!groupIds.includes(normalizedRoomId)) {
        return;
      }
      group.meshNames.forEach((meshName) => {
        const mesh = this.scene?.getMeshByName(meshName);
        if (mesh) {
          roomMeshes.add(mesh);
        }
      });
    });
    this.scene.meshes.forEach((m) => {
      const meta = this.readGltfMeshFloorRoomIds(m);
      const metaIds = [meta.roomId, ...(meta.roomPerimeterIds || [])]
        .map((id) => this.normalizeEntityId(id))
        .filter((id) => !!id);
      const name = m.name || '';
      const leafName = name.split('/').pop() || '';
      const normalizedName = this.normalizeEntityId(leafName.replace(/^Room_/i, ''));
      if (metaIds.includes(normalizedRoomId) || normalizedName === normalizedRoomId || this.normalizeEntityId(name).includes(normalizedRoomId)) {
        roomMeshes.add(m);
      }
    });
    if (roomMeshes.size) {
      const displayMeshes = new Set<AbstractMesh>();
      const roomBoundaryMeshes: AbstractMesh[] = [];
      let roomFloorId: string | null = null;
      roomMeshes.forEach((mesh) => {
        const name = mesh.name || '';
        const leafName = name.split('/').pop() || '';

        // Keep room boundary meshes (Room_*) as they are - they provide the floor/walls
        if (leafName.toLowerCase().startsWith('room_')) {
          displayMeshes.add(mesh);
          roomBoundaryMeshes.push(mesh);
          if (!roomFloorId) {
            roomFloorId = this.extractFloorIdFromRoomMesh(mesh);
          }
        } else {

          // Replace furniture placeholders with actual furniture assets
          const asset = this.loadedAssets.get(`furniture_${mesh.uniqueId}`);
          displayMeshes.add(asset?.mesh || mesh);
        }
      });

      // Clear any previous room highlights before applying new ones
      this.restoreHighlightedBabylonRooms();
      // Apply cutaway for the room's floor (hide roofs, transparent walls)
      if (roomFloorId) {
        this.applyFloorCutaway(roomFloorId);
      }

      // Apply layer masks for floor + room
      this.applyRoomAndSliceLayerMasks(roomFloorId, roomId);
      this.isolateRoomMeshesForCutout(displayMeshes);
      this.focusCameraOnMeshes(roomBoundaryMeshes);

      // Create wireframe bounding box highlight for the room
      this.createRoomBoundingBoxHighlight(roomBoundaryMeshes);
    } else {
    }
    this.finishBlenderFloorPresentation();
  }

  private focusCameraOnMesh(mesh: AbstractMesh): void {
    this.focusCameraOnMeshes([mesh]);

  }

  private focusCameraOnMeshes(meshes: AbstractMesh[]): void {
    if (this.isEmbeddedMapOverlay()) {
      return;
    }
    if (!this.scene?.activeCamera || !meshes.length) return;
    const camera = this.scene.activeCamera as ArcRotateCamera;
    const bounds = meshes
      .filter((mesh) => mesh.getBoundingInfo())
      .map((mesh) => mesh.getBoundingInfo().boundingBox);
    if (!bounds.length) return;
    const min = bounds.reduce((acc, box) => Vector3.Minimize(acc, box.minimumWorld), bounds[0].minimumWorld.clone());
    const max = bounds.reduce((acc, box) => Vector3.Maximize(acc, box.maximumWorld), bounds[0].maximumWorld.clone());
    const center = min.add(max).scale(0.5);
    const extend = max.subtract(min).scale(0.5);
    camera.setTarget(center);
    camera.radius = Math.max(extend.length() * 1.2, 3); // Zoom in more for room popup

  }

  /** Public method to isolate a specific room for screenshot/cutout */

  public isolateRoomForCutout(mesh: AbstractMesh): void {
    this.isolateRoomMeshesForCutout(new Set<AbstractMesh>([mesh]));

  }

  private isolateRoomMeshesForCutout(roomMeshes: Set<AbstractMesh>): void {
    if (!this.scene) return;
    const visibleMeshes = new Set<AbstractMesh>();
    roomMeshes.forEach((mesh) => {
      visibleMeshes.add(mesh);
      const children = mesh.getChildMeshes ? mesh.getChildMeshes(false) : [];
      children.forEach((child) => {
        if (!this.isRoomCutoutStructuralMesh(child)) {
          visibleMeshes.add(child);
        }
      });
    });
    const meshStates = new Map<AbstractMesh, boolean>();
    this.scene.meshes.forEach((m) => {
      meshStates.set(m, m.isEnabled());
      const meshName = m.name || '';
      const upper = meshName.toUpperCase();
      const isFloorPlate = upper.includes('FLOOR_PLATE') || upper.includes('_PLATE');
      const isFloorLike = isFloorPlate || upper.includes('FLOOR');
      const isAsset = this.isBeaconMesh(meshName) || meshName.toUpperCase().includes('CAMERA') || meshName.toUpperCase().includes('USER') || meshName.toUpperCase().includes('PERSON');

      // Hide structural/wall meshes completely, but keep room meshes visible
      if (visibleMeshes.has(m)) {

        // Always show meshes in the visible set (room meshes and their children)
        m.setEnabled(true);
        m.isVisible = true;
      } else if (this.isRoomCutoutStructuralMesh(m) && !isAsset) {

        // Hide other structural meshes
        m.setEnabled(false);
        m.isVisible = false;
        m.isPickable = false;
      } else {
        m.setEnabled(false);
      }

      // Ensure assets are always opaque when enabled
      if (isAsset && m.isEnabled()) {
        m.isVisible = true;
        if (m.material) {
          (m.material as any).alpha = 1.0;
        }
      }
    });
    this.previousMeshStates = meshStates;

  }

  /** Restore all meshes to their previous visibility state */

  public restoreRoomCutout(): void {
    if (!this.scene || !this.previousMeshStates) return;
    this.previousMeshStates.forEach((enabled, mesh) => {
      mesh.setEnabled(enabled);
    });
    this.previousMeshStates = null;

    // Clear room highlights
    this.restoreHighlightedBabylonRooms();

  }

  private isChildOf(mesh: AbstractMesh, parent: AbstractMesh): boolean {
    let current: any = mesh;
    while (current) {
      if (current === parent) return true;
      current = current.parent;
    }
    return false;

  }

  /** Capture screenshot of current scene */

  public captureScreenshot(callback: (data: string) => void): void {
    if (!this.scene || !this.engine || !this.scene.activeCamera) {
      callback('');
      return;
    }
    try {
      Tools.CreateScreenshot(this.engine, this.scene.activeCamera, { precision: 2 }, callback);
    } catch (error) {
      callback('');
    }

  }

  @HostBinding('class.enableMonitor') @Input() enableMonitor = false;

  @HostBinding('class.enablePlayback') @Input() enablePlayback = false;

  @HostBinding('class.enableInteraction') @Input() enableInteraction = false;

  @HostBinding('class.embedded-map-mode')

  get embeddedMapModeClass(): boolean {
    return this.isEmbeddedMapOverlay();

  }
  @HostBinding('class.viewer-transparent-host')
  get transparentHostClass(): boolean {
    return this.transparentBackground && !this.embedded;
  }

  @ViewChild('renderCanvas', { static: true })
  renderCanvas!: ElementRef<HTMLCanvasElement>;
  isModelLoading = true;

  private engine?: Engine;

  private scene?: Scene;

  private orbitCamera?: ArcRotateCamera;

  private isDestroyed = false;

  private loadGeneration = 0;

  private parsedMetadata: BuildingModelMetadata = {
    floorMeshes: {},
    roomMeshes: {},
    beaconMeshes: {},
    unassignedMeshNames: [],

  };

  private latestLiveUserData: ViewerLiveUserData | null = null;

  private beaconMarkers: (AbstractMesh | TransformNode)[] = [];

  private roomMeshIndex = new Map<string, AbstractMesh[]>();

  private floorMaterialMap = new Map<AbstractMesh, Material | null>();

  private highlightedFloorMeshes = new Set<AbstractMesh>();

  private highlightedFloorId: string | null = null;

  private floorSlices = new Map<string, AbstractMesh[]>(); // floorId -> slice meshes
  private floorLayoutGroups = new Map<string, TransformNode>(); // floorId -> common rotation group

  private originalGlbMaterials = new Map<AbstractMesh, Material | null>();

  private floorHighlightSubscription: Subscription | null = null;

  private alertFloorHighlightSubscription: Subscription | null = null;

  private floorMappingTable: Map<string, FloorMeshGroup> = new Map(); // Floor ID -> Floor data with actual elevations

  private roomHighlightSubscription: Subscription | null = null;

  private roomMaterialMap = new Map<AbstractMesh, Material | null>();

  private highlightedRoomMeshes = new Set<AbstractMesh>();
  private alertRoomMaterialMap = new Map<AbstractMesh, Material | null>();
  private alertHighlightedMeshes = new Set<AbstractMesh>();
  private roomHighlightBoxes: AbstractMesh[] = [];
  private activeAssetBundle: BuildingModelAssetBundle | null = null;

  private previousMeshStates: Map<AbstractMesh, boolean> | null = null;

  private isRoomClickObserverSetup = false;

  private userMeshToUserRecord = new Map<string, ViewerUserRecord>();

  /** Shared material assigned in {@link applyFloorVisualStyle}; neutral status reuses it */

  private floorSliceSharedMaterial: StandardMaterial | null = null;

  private resolvedRoomStatusHex = mergeRoomStatusColors(null);

  private syncLiveUserAssetsTimer: any = null;

  private loadedSceneNodeIds = new Set<number>();

  private initialCameraState: { alpha: number; beta: number; radius: number; target: Vector3 } | null = null;

  private readonly visualConfig: ViewerVisualConfig = {
    sliceOverlapScale: 1.0,
    showEdges: true,
    fillAlpha: 0.75,
    fillColorHex: '#a9a9a9',
    emissiveColorHex: '#999999',
    edgeColorHex: '#000000',
    edgeWidth: 3.0,
    clearColorHex: '#d3d3d3',
    hemiIntensity: 0.82,
    dirIntensity: 0.58,
    beaconModelScale: 0.25,
    useOriginalMaterials: true,

  };

  private readonly furnitureAssetMapping: Record<string, PlaceholderAssetMapping> = {
    // 'placeholder': { placeholderType: 'placeholder', assetId: 'room/common_room', assetName: 'room/common_room', scalingBehavior: 'non-uniform' },
    // 'placeholder_lab': { placeholderType: 'placeholder_lab', assetId: 'room/cubicle_lab', assetName: 'room/cubicle_lab', scalingBehavior: 'non-uniform' },
    // 'placeholder_common_room': { placeholderType: 'placeholder_common_room', assetId: 'room/common_room', assetName: 'room/common_room', scalingBehavior: 'non-uniform' },
    // 'placeholder_storage': { placeholderType: 'placeholder_storage', assetId: 'room/storage', assetName: 'room/storage', scalingBehavior: 'non-uniform' },
    // 'placeholder_storeroom': { placeholderType: 'placeholder_storeroom', assetId: 'room/Storeroom', assetName: 'room/Storeroom', scalingBehavior: 'non-uniform' },
    // 'placeholder_office': { placeholderType: 'placeholder_office', assetId: 'room/office', assetName: 'room/office', scalingBehavior: 'non-uniform' },
    // 'placeholder_bedroom': { placeholderType: 'placeholder_bedroom', assetId: 'room/bedroom', assetName: 'room/bedroom', scalingBehavior: 'non-uniform' },
    // 'placeholder_kitchen': { placeholderType: 'placeholder_kitchen', assetId: 'room/Kitchen', assetName: 'room/Kitchen', scalingBehavior: 'non-uniform' },
    // 'placeholder_laundry': { placeholderType: 'placeholder_laundry', assetId: 'room/Laundary_room', assetName: 'room/Laundary_room', scalingBehavior: 'non-uniform' },
    // 'placeholder_vr_gallery': { placeholderType: 'placeholder_vr_gallery', assetId: 'room/vr_gallery', assetName: 'room/vr_gallery', scalingBehavior: 'non-uniform' },
    'bed': { placeholderType: 'bed', assetId: 'bed', assetName: 'bed', scalingBehavior: 'uniform' },
    'nightstand': { placeholderType: 'nightstand', assetId: 'nightstand', assetName: 'nightstand', scalingBehavior: 'uniform' },
    'lamp': { placeholderType: 'lamp', assetId: 'lamp', assetName: 'lamp', scalingBehavior: 'uniform' },
    'wardrobe': { placeholderType: 'wardrobe', assetId: 'wardrobe', assetName: 'wardrobe', scalingBehavior: 'uniform' },
    'desk': { placeholderType: 'desk', assetId: 'desk', assetName: 'desk', scalingBehavior: 'uniform' },
    'chair': { placeholderType: 'chair', assetId: 'chair', assetName: 'chair', scalingBehavior: 'uniform' },
    'cabinet': { placeholderType: 'cabinet', assetId: 'cabinet', assetName: 'cabinet', scalingBehavior: 'uniform' },
    'meeting_table': { placeholderType: 'meeting_table', assetId: 'meeting_table', assetName: 'meeting_table', scalingBehavior: 'uniform' },
    'sofa': { placeholderType: 'sofa', assetId: 'sofa', assetName: 'sofa', scalingBehavior: 'uniform' },
    'counter': { placeholderType: 'counter', assetId: 'counter', assetName: 'counter', scalingBehavior: 'uniform' },
    'fixture': { placeholderType: 'fixture', assetId: 'fixture', assetName: 'fixture', scalingBehavior: 'uniform' },
    'storage_rack': { placeholderType: 'storage_rack', assetId: 'storage_rack', assetName: 'storage_rack', scalingBehavior: 'uniform' },
    'tv': { placeholderType: 'tv', assetId: 'tv', assetName: 'tv', scalingBehavior: 'uniform' },
    'Bookshelf': { placeholderType: 'Bookshelf', assetId: 'Bookshelf', assetName: 'Bookshelf', scalingBehavior: 'uniform' },
    'air_purifier': { placeholderType: 'air_purifier', assetId: 'air_purifier', assetName: 'air_purifier', scalingBehavior: 'uniform' },
    'glass_table': { placeholderType: 'glass_table', assetId: 'glass_table', assetName: 'glass_table', scalingBehavior: 'uniform' },
    'marble_table': { placeholderType: 'marble_table', assetId: 'marble_table', assetName: 'marble_table', scalingBehavior: 'uniform' },
    'marble_tea_table': { placeholderType: 'marble_tea_table', assetId: 'marble_tea_table', assetName: 'marble_tea_table', scalingBehavior: 'uniform' },
    'modern_purple_glass_table': { placeholderType: 'modern_purple_glass_table', assetId: 'modern_purple_glass_table', assetName: 'modern_purple_glass_table', scalingBehavior: 'uniform' },
    'planter': { placeholderType: 'planter', assetId: 'planter', assetName: 'planter', scalingBehavior: 'uniform' },
    'rug': { placeholderType: 'rug', assetId: 'rug', assetName: 'rug', scalingBehavior: 'uniform' },
  };

  private readonly assetLibraryPath = '/assets/3d/room/';

  private readonly windowAssetPath = '/assets/3d/object/window.glb';

  private loadedAssets: Map<string, LoadedAsset> = new Map();

  private assetTemplates: Map<string, AbstractMesh> = new Map();

  private resizeListener = () => this.engine?.resize();

  // ── Floor cutaway state (ported from POC viewer.js _cutawayFloorVisibility) ──
  private cutawayWindowMeshes: Map<string, { mesh: AbstractMesh; alpha: number; transparencyMode: number }[]> = new Map();
  private cutawayWallMeshes: Map<string, { mesh: AbstractMesh; alpha: number; transparencyMode: number }[]> = new Map();
  private cutawayRoofMeshes: Map<string, AbstractMesh[]> = new Map();
  private activeCutawayFloorId: string | null = null;

  // ── Dual-view layer mask state (ported from POC viewer.js _applyRoomAndSliceLayerMasks) ──
  private activeMaskFloorId: string | null = null;
  private activeMaskRoomId: string | null = null;

  // ── Anchor-based asset placement utility (ported from POC AssetPlacement.js) ──
  private assetPlacementUtil: AssetPlacementUtil | null = null;
  constructor(

    private readonly threeIntegrationService: ThreeIntegrationService,

    private readonly buildingModelAssets: BuildingModelAssetsService,

    private readonly babylonVisualizeService: BabylonVisualizeService,

    private readonly liveUserAssetService: LiveUserAssetService,

    private readonly sessionOptionsService: SessionOptionsService
  ) { }
  ngOnInit(): void {
    this.floorHighlightSubscription = this.threeIntegrationService.floorHighlight$.subscribe(
      ({ floorId, highlight }) => {
        if (highlight) {
          this.highlightBabylonFloor(floorId);
        } else {
          this.clearBabylonFloorHighlight(floorId);
        }
      }
    );
    this.roomHighlightSubscription = this.threeIntegrationService.roomHighlight$.subscribe(
      ({ roomIds }) => {
        this.highlightBabylonRooms(roomIds || []);
        this.renderAlertAssets();
      }
    );
    this.alertFloorHighlightSubscription = this.threeIntegrationService.alertFloorHighlight$.subscribe(
      ({ floorIds }) => {
        this.highlightBabylonAlertFloors(floorIds || []);
      }
    );
    this.enableInteraction = this.sessionOptionsService.isLiveMode();
    this.enableMonitor = this.sessionOptionsService.isMonitorMode();
    this.enablePlayback = this.sessionOptionsService.isPlaybackMode();

  }

  ngAfterViewInit(): void {
    this.isDestroyed = false;
    this.threeIntegrationService.registerBabylonViewer(this);
    const canvas = this.renderCanvas.nativeElement;
    if (this.embedded || this.transparentBackground) {
      canvas.style.background = 'transparent';
    }
    if (this.transparentBackground && !this.embedded) {
      this.fitCanvasToContainer();
    }
    this.engine = new Engine(
      canvas,
      true,
      {
        preserveDrawingBuffer: true,
        stencil: true,
        alpha: this.embedded || this.transparentBackground,
        premultipliedAlpha: false,
      },
      true
    );

    // Render at higher resolution for crisp display in small containers
    this.engine.setHardwareScalingLevel(1 / Math.max(window.devicePixelRatio || 1, 1));
    this.scene = this.createScene(canvas);
    this.engine.runRenderLoop(() => {
      if (!this.scene || this.scene.isDisposed) {
        return;
      }
      if (this.orbitCamera) {
        this.scene.activeCamera = this.orbitCamera;
      }
      this.scene.render();
    });
    this.enableInteraction = this.sessionOptionsService.isLiveMode();
    this.enableMonitor = this.sessionOptionsService.isMonitorMode();
    this.enablePlayback = this.sessionOptionsService.isPlaybackMode();
    this.ensureCrispResize();
    window.addEventListener('resize', this.resizeListener);
    this.resolveAndLoadAssets();

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active']?.currentValue) {
      this.ensureCrispResize();
      if (this.scene) {
        this.renderAlertAssets();
      }
    }
    if (changes['liveUserAssetConfig']?.currentValue && this.scene) {
      this.liveUserAssetService.configure(changes['liveUserAssetConfig'].currentValue);
    }
    if (changes['roomStatusColors']) {
      this.resolvedRoomStatusHex = mergeRoomStatusColors(this.roomStatusColors ?? undefined);
      if (this.scene) {
        this.syncRoomStatusColors();
      }
    }
    if (changes['liveUserData']) {
      const incoming = changes['liveUserData'].currentValue ?? null;

      // Prevent transient empty/null API responses from clearing room colors.
      // If the new value is null or has no records and no roomStatusByRoomId, keep previous data.
      const isEmpty = !incoming || (
        (!incoming.records || incoming.records.length === 0) &&
        (!incoming.roomStatusByRoomId || Object.keys(incoming.roomStatusByRoomId).length === 0)
      );
      if (isEmpty) {
        this.latestLiveUserData = null;
      } else {
        this.latestLiveUserData = incoming;
      }
      if (this.scene) {
        this.syncLiveUserAssets();
      }
    }
    if (changes['alertRecords']) {
      if (this.scene) {
        this.syncLiveUserAssets();
      }
    }
    if (changes['assetResolutionKey'] && !changes['assetResolutionKey'].firstChange) {
      this.resolveAndLoadAssets();
    }
    if (changes['assetResolutionKeys'] && !changes['assetResolutionKeys'].firstChange) {
      this.resolveAndLoadAssets();
    }
    if (changes['mapSyncAnchor'] || changes['buildingGeoPlacements']) {
      if (this.scene) {
        this.reapplyGeoPlacements();
      }
    }
    if (changes['showBeacons'] || changes['floorMapEntity'] || changes['beacons']) {
      if (this.scene) {
        this.renderBeaconAssets();
      }
    }

  }

  ngOnDestroy(): void {
    this.setModelLoading(false);
    this.isDestroyed = true;
    if (this.syncLiveUserAssetsTimer !== null) {
      clearTimeout(this.syncLiveUserAssetsTimer);
      this.syncLiveUserAssetsTimer = null;
    }
    this.loadGeneration++;
    window.removeEventListener('resize', this.resizeListener);
    this.clearBeaconMarkers();
    this.liveUserAssetService.dispose();
    this.babylonVisualizeService.clearAlertSprites();
    this.restoreHighlightedBabylonRooms();
    if (this.floorHighlightSubscription) {
      this.floorHighlightSubscription.unsubscribe();
    }
    if (this.roomHighlightSubscription) {
      this.roomHighlightSubscription.unsubscribe();
    }
    if (this.alertFloorHighlightSubscription) {
      this.alertFloorHighlightSubscription.unsubscribe();
    }
    this.babylonVisualizeService.dispose();
    this.scene?.dispose();
    this.engine?.dispose();

  }

  private createScene(canvas: HTMLCanvasElement): Scene {
    const scene = new Scene(this.engine!);
    SceneLoader.ShowLoadingScreen = false;
    this.engine?.hideLoadingUI();
    const useTransparentBg = this.embedded || this.transparentBackground;
    if (useTransparentBg) {
      scene.clearColor = new Color4(0, 0, 0, 0);
    } else {
      scene.clearColor = new Color4(0.83, 0.83, 0.83, 1);
    }
    if (useTransparentBg && !this.embedded) {
      scene.environmentTexture = null;
      scene.environmentIntensity = 0;
      scene.imageProcessingConfiguration.toneMappingEnabled = false;
      scene.imageProcessingConfiguration.exposure = 1;
      scene.imageProcessingConfiguration.contrast = 1;
      new HemisphericLight('transparentBgLight', new Vector3(0, 1, 0), scene);
    } else {
      scene.environmentTexture = CubeTexture.CreateFromPrefilteredData(
        '/assets/environment/environment.env',
        scene
      );
      scene.environmentIntensity = 1.1;
      scene.imageProcessingConfiguration.toneMappingEnabled = true;
      scene.imageProcessingConfiguration.exposure = 0.92;
      scene.imageProcessingConfiguration.contrast = 1.08;
    }
    const camera = new ArcRotateCamera(
      'buildingCamera',
      -Math.PI / 2 + Math.PI,
      Math.PI / 2.8,
      220,
      new Vector3(0, 50, 0),
      scene
    );
    this.orbitCamera = camera;
    if (this.flipVertical) {
      camera.upVector = new Vector3(0, -1, 0);
    }
    camera.attachControl(canvas, !this.embedded);
    camera.lowerRadiusLimit = 30;
    camera.upperRadiusLimit = 1200;
    camera.wheelDeltaPercentage = 0.02;
    camera.panningSensibility = 120;
    camera.inertia = 0.75;
    camera.panningInertia = 0.85;
    camera.useNaturalPinchZoom = true;
    camera.zoomToMouseLocation = true;
    if (!this.embedded) {
      camera.useFramingBehavior = true;
      if (camera.framingBehavior) {
        camera.framingBehavior.framingTime = 500;
        camera.framingBehavior.elevationReturnTime = 1000;
        camera.framingBehavior.mode = 0;
      }
    }
    scene.activeCamera = camera;
    if (this.embedded) {
      new HemisphericLight('embeddedMapLight', new Vector3(0, 1, 0), scene);
    }

    // No custom lights - use GLB model's own lighting
    this.configureMeshPickLogging(scene);

    // Setup floor click handler
    this.setupFloorClickHandler(scene);
    return scene;

  }

  private splitGlbUrl(glbUrl: string): { root: string; file: string } {
    if (glbUrl.includes('://')) {
      try {
        const url = new URL(glbUrl);
        const lastSlashIndex = url.pathname.lastIndexOf('/');
        const root = url.origin + url.pathname.substring(0, lastSlashIndex + 1);
        const file = url.pathname.substring(lastSlashIndex + 1) + url.search;
        return { root, file };
      } catch (e) {
        const i = glbUrl.lastIndexOf('/');
        return { root: glbUrl.slice(0, i + 1), file: glbUrl.slice(i + 1) };
      }
    }
    const path = glbUrl.startsWith('/') ? glbUrl : `/${glbUrl}`;
    const i = path.lastIndexOf('/');
    if (i <= 0) {
      return { root: '/', file: path.replace(/^\//, '') };
    }
    return { root: path.slice(0, i + 1), file: path.slice(i + 1) };

  }

  private clearSceneForReload(): void {
    if (!this.scene || this.scene.isDisposed) return;
    if (this.loadedSceneNodeIds.size) {
      this.scene.meshes.forEach((mesh) => {
        if (this.loadedSceneNodeIds.has(mesh.uniqueId)) {
          mesh.dispose();
        }
      });
      this.scene.transformNodes.forEach((node: any) => {
        if (this.loadedSceneNodeIds.has(node.uniqueId)) {
          node.dispose();
        }
      });
    }
    this.loadedSceneNodeIds.clear();
    this.originalGlbMaterials.clear();

    // Clean up loaded placeholder assets
    this.cleanupLoadedAssets();

    // Reset internal state and indices
    this.parsedMetadata = { floorMeshes: {}, roomMeshes: {}, beaconMeshes: {}, unassignedMeshNames: [] };
    this.roomMeshIndex.clear();
    this.floorMaterialMap.clear();
    this.roomMaterialMap.clear();
    this.highlightedFloorMeshes.clear();
    this.alertRoomMaterialMap.clear();
    this.alertHighlightedMeshes.clear();
    this.highlightedRoomMeshes.clear();
    this.floorSliceSharedMaterial = null;

  }

  private resolveAndLoadAssets(): void {
    const generation = ++this.loadGeneration;
    this.setModelLoading(true);
    this.clearSceneForReload();

    // Handle multiple buildings (Show All Buildings mode)
    if (this.assetResolutionKeys && this.assetResolutionKeys.length > 0) {
      this.loadMultipleBuildings(generation);
      return;
    }

    // Handle single building (normal mode)
    const source$ =
      this.assetBundle != null
        ? of(this.assetBundle)
        : this.buildingModelAssets.getAssetBundle(this.assetResolutionKey ?? undefined);
    firstValueFrom(source$)
      .then((bundle) => {
        this.activeAssetBundle = bundle;
        const scene = this.scene;
        if (this.isDestroyed || generation !== this.loadGeneration || !scene || scene.isDisposed || !bundle?.glbUrl) {
          if (generation === this.loadGeneration) {
            this.setModelLoading(false);
          }
          return;
        }
        return this.loadGlbFromBundle(
          bundle,
          { resolutionKey: this.assetResolutionKey },
          generation
        ).then((): Promise<void> | undefined => {
          if (!this.isDestroyed && generation === this.loadGeneration && !scene.isDisposed) {
            return this.parseModelMetadata(scene);
          }
          if (generation === this.loadGeneration) {
            this.setModelLoading(false);
          }
          return undefined;
        });
      })
      .catch(() => {
        if (generation === this.loadGeneration) {
          this.setModelLoading(false);
        }
      });

  }

  private async loadMultipleBuildings(generation: number): Promise<void> {
    const scene = this.scene;
    if (this.isDestroyed || !scene || scene.isDisposed || !this.assetResolutionKeys || this.assetResolutionKeys.length === 0) {
      this.setModelLoading(false);
      return;
    }
    try {
      const loadPromises = this.assetResolutionKeys.map(async (resolutionKey, index) => {
        try {
          const bundle = await firstValueFrom(this.buildingModelAssets.getAssetBundle(resolutionKey));
          if (!this.isDestroyed && generation === this.loadGeneration && !scene.isDisposed && bundle?.glbUrl) {
            await this.loadGlbFromBundle(
              bundle,
              { resolutionKey, offsetX: this.hasGeoPlacement() ? 0 : (index - (this.assetResolutionKeys.length - 1) / 2) * 140 },
              generation
            );
          }
        } catch (err) {
        }
      });
      await Promise.all(loadPromises);
      if (!this.isDestroyed && generation === this.loadGeneration && !scene.isDisposed) {
        await this.parseModelMetadata(scene);
        this.hideWallMeshes(scene);
      } else {
        this.setModelLoading(false);
      }
    } catch {
      if (generation === this.loadGeneration) {
        this.setModelLoading(false);
      }
    }

  }

  private setModelLoading(loading: boolean): void {
    if (this.isModelLoading === loading) {
      return;
    }
    this.isModelLoading = loading;
    this.loadingChange.emit(loading);

  }

  /** Embedded GLB overlay on the Cesium map (full model, map-synced camera). */

  private isEmbeddedMapOverlay(): boolean {
    return this.embedded;

  }

  private hasGeoPlacement(): boolean {
    return (
      this.isEmbeddedMapOverlay() &&
      !!this.mapSyncAnchor &&
      typeof this.mapSyncAnchor.lat === 'number' &&
      typeof this.mapSyncAnchor.lon === 'number'
    );

  }

  private resolvePlacementForKey(resolutionKey?: string | null): BuildingGeoPlacement | null {
    if (!this.buildingGeoPlacements?.length) {
      return null;
    }
    if (resolutionKey) {
      return this.buildingGeoPlacements.find((placement) => placement.resolutionKey === resolutionKey) ?? null;
    }
    return this.buildingGeoPlacements.length === 1 ? this.buildingGeoPlacements[0] : null;

  }

  private reapplyGeoPlacements(): void {
    if (!this.scene || this.scene.isDisposed || !this.hasGeoPlacement()) {
      return;
    }
    this.scene.transformNodes.forEach((node) => {
      if (!node.name.startsWith('BuildingContainer_')) {
        return;
      }
      const resolutionKey = node.name.replace('BuildingContainer_', '');
      const placement = this.resolvePlacementForKey(resolutionKey);
      if (placement) {
        node.position.set(placement.x, placement.y, placement.z);
        node.computeWorldMatrix(true);
      }
    });

  }

  private loadGlbFromBundle(
    bundle: BuildingModelAssetBundle,
    options: { offsetX?: number; resolutionKey?: string | null } = {},
    generation = this.loadGeneration
  ): Promise<void> {
    if (!this.scene || this.scene.isDisposed || this.isDestroyed || generation !== this.loadGeneration) {
      return Promise.reject(new Error('No scene'));
    }
    const scene = this.scene;
    const { root, file } = this.splitGlbUrl(bundle.glbUrl);
    const containerKey = options.resolutionKey ?? String(Date.now());
    return SceneLoader.ImportMeshAsync('', root, file, scene).then((result) => {
      if (this.isDestroyed || generation !== this.loadGeneration || scene.isDisposed) {
        result.meshes.forEach((mesh) => mesh.dispose(false, true));
        result.transformNodes.forEach((node) => node.dispose(false, true));
        return;
      }
      const container = new TransformNode(`BuildingContainer_${containerKey}`, scene);
      result.transformNodes.forEach((node) => {
        if (!node.parent) {
          node.parent = container;
        }
      });
      result.meshes.forEach((mesh) => {
        if (mesh.name !== '__root__' && !mesh.parent) {
          mesh.parent = container;
        }
      });
      scene.render();
      const modelMeshes = result.meshes.filter((mesh) => mesh.name !== '__root__');

      // Improve texture and material quality for loaded meshes
      modelMeshes.forEach((mesh) => {
        if (mesh.material) {
          const material = mesh.material as any;
          if (material.diffuseTexture) {
            const sz = material.diffuseTexture.getSize();
            if (sz.width > 0 && sz.height > 0) {
              material.diffuseTexture.anisotropicFilteringLevel = 16;
              material.diffuseTexture.updateSamplingMode(3); // TRILINEAR_SAMPLINGMODE
            }
          }
          if (material.albedoTexture) {
            const sz = material.albedoTexture.getSize();
            if (sz.width > 0 && sz.height > 0) {
              material.albedoTexture.anisotropicFilteringLevel = 16;
              material.albedoTexture.updateSamplingMode(3);
            }
          }

          // Enable backface culling for cleaner edges
          material.backFaceCulling = true;
          material.twoSidedLighting = false;
        }
      });
      const placement = this.resolvePlacementForKey(options.resolutionKey);
      if (placement && this.hasGeoPlacement()) {
        container.position.set(placement.x, placement.y, placement.z);
        if (placement.footprintExtentMeters > 0 && modelMeshes.length) {
          let min = new Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
          let max = new Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
          modelMeshes.forEach((mesh) => {
            mesh.computeWorldMatrix(true);
            const boundingBox = mesh.getBoundingInfo().boundingBox;
            min = Vector3.Minimize(min, boundingBox.minimumWorld);
            max = Vector3.Maximize(max, boundingBox.maximumWorld);
          });
          const glbHalfExtent = Math.max((max.x - min.x) / 2, (max.z - min.z) / 2);
          if (glbHalfExtent > 0.001) {
            const scale = placement.footprintExtentMeters / glbHalfExtent;
            if (scale >= 0.01 && scale <= 500) {
              container.scaling.set(scale, scale, scale);
            }
          }
        }
      } else if (options.offsetX) {
        container.position.x = options.offsetX;
      }
      container.computeWorldMatrix(true);
    });

  }

  private async parseModelMetadata(scene: Scene): Promise<void> {
    try {
      const meshes = scene.meshes.filter((mesh) => !!mesh.name && mesh.name !== '__root__');
      const floorMap: Record<string, FloorMeshGroup> = {};
      const roomMap: Record<string, RoomMeshGroup> = {};
      const beaconMap: Record<string, BeaconMeshGroup> = {};
      this.loadedSceneNodeIds.clear();
      meshes.forEach((mesh) => this.loadedSceneNodeIds.add(mesh.uniqueId));
      scene.transformNodes.forEach((node) => {
        if (node.name && node.name !== '__root__') {
          this.loadedSceneNodeIds.add(node.uniqueId);
        }
      });
      this.enableInteraction = this.sessionOptionsService.isLiveMode();
      this.enableMonitor = this.sessionOptionsService.isMonitorMode();
      this.enablePlayback = this.sessionOptionsService.isPlaybackMode();

      // Capture all original materials from the GLB before any processing or styling occurs
      meshes.forEach((mesh) => {
        if (!this.originalGlbMaterials.has(mesh)) {
          this.originalGlbMaterials.set(mesh, mesh.material);
        }
      });
      const manifestFloors: string[] = [];
      const manifestFloorRooms = new Set<string>();
      scene.transformNodes.forEach((node) => {
        const nodeName = node.name || '';
        if (nodeName.startsWith('Floor_')) {
          const floorId = nodeName.replace('Floor_', '');
          if (!floorMap[floorId]) {
            floorMap[floorId] = {
              floorId,
              meshNames: [],
              floorNode: node,
            };
          }

          // Extract floor number from metadata if available
          const floorNumber = this.extractFloorNumberFromMetadata(node);
          if (floorNumber !== undefined) {
            floorMap[floorId].floorNumber = floorNumber;
          }

          // Calculate floor bounds and elevation from child meshes
          let minY = Infinity;
          let maxY = -Infinity;
          let minZ = Infinity;
          let maxZ = -Infinity;
          let minX = Infinity;
          let maxX = -Infinity;
          node.getChildMeshes().forEach((childMesh) => {
            if (childMesh.name && childMesh.name !== '__root__') {
              if (this.isFloorBandMesh(childMesh)) {
                return;
              }
              if (!floorMap[floorId].meshNames.includes(childMesh.name)) {
                floorMap[floorId].meshNames.push(childMesh.name);
              }

              // Calculate bounds
              const boundingInfo = childMesh.getBoundingInfo();
              const boundingBox = boundingInfo.boundingBox;
              const worldMatrix = childMesh.getWorldMatrix();

              // Transform bounds to world space
              const min = boundingBox.minimumWorld;
              const max = boundingBox.maximumWorld;
              minY = Math.min(minY, min.y);
              maxY = Math.max(maxY, max.y);
              minZ = Math.min(minZ, min.z);
              maxZ = Math.max(maxZ, max.z);
              minX = Math.min(minX, min.x);
              maxX = Math.max(maxX, max.x);
            }
          });

          // Store actual floor elevation and bounds
          if (minY !== Infinity && maxY !== -Infinity) {
            floorMap[floorId].elevation = minY;
            floorMap[floorId].bottomPosition = minY;
            floorMap[floorId].topPosition = maxY;
            floorMap[floorId].bounds = {
              min: new Vector3(minX, minY, minZ),
              max: new Vector3(maxX, maxY, maxZ),
            };
          }
        }
      });
      meshes.forEach((mesh) => {
        const meshName = mesh.name;
        const gltfIds = this.readGltfMeshFloorRoomIds(mesh);
        const floorId = this.extractFloorIdFromRoomMesh(mesh);
        const roomId = gltfIds.roomId || this.extractRoomId(meshName, manifestFloorRooms);
        const isBeaconMesh = this.isBeaconMesh(meshName);
        const isFloorBand = this.isFloorBandMesh(mesh);
        const isFloorPlate = meshName.toUpperCase().includes('FLOOR_PLATE') || meshName.toUpperCase().includes('_PLATE');

        // STRONG CHECK: Immediately hide any mesh with window_type or is_window_placeholder
        if (this.isWindowMesh(mesh)) {
          mesh.setEnabled(false);
          mesh.isVisible = false;
          mesh.isPickable = false;
          return; // Skip adding this mesh to any maps
        }
        if (floorId && !isFloorBand) {
          if (!floorMap[floorId]) {
            floorMap[floorId] = { floorId, meshNames: [] };
          }
          if (!floorMap[floorId].meshNames.includes(meshName)) {
            floorMap[floorId].meshNames.push(meshName);
          }
        }

        // Only add to roomMap if it's a genuine room, not a floor plate
        if (roomId && !isFloorBand && !isFloorPlate) {
          if (!roomMap[roomId]) {
            roomMap[roomId] = { roomId, floorId, meshNames: [], perimeterIds: [] };
          }
          if (!roomMap[roomId].floorId && floorId) {
            roomMap[roomId].floorId = floorId;
          }
          if (!roomMap[roomId].meshNames.includes(meshName)) {
            roomMap[roomId].meshNames.push(meshName);
          }
          const pidList = gltfIds.roomPerimeterIds || [];
          if (pidList.length) {
            if (!roomMap[roomId].perimeterIds) {
              roomMap[roomId].perimeterIds = [];
            }
            pidList.forEach((pid) => {
              if (!roomMap[roomId].perimeterIds!.includes(pid)) {
                roomMap[roomId].perimeterIds!.push(pid);
              }
            });
          }
        }
        if (isBeaconMesh) {
          const beaconKey = `beacon_${mesh.uniqueId}`;
          beaconMap[beaconKey] = {
            beaconId: beaconKey,
            floorId,
            roomId,
            meshNames: [meshName],
          };
        }
      });
      const assignedMeshes = new Set<string>();
      Object.values(floorMap).forEach((entry) => entry.meshNames.forEach((name) => assignedMeshes.add(name)));
      Object.values(roomMap).forEach((entry) => entry.meshNames.forEach((name) => assignedMeshes.add(name)));
      Object.values(beaconMap).forEach((entry) => entry.meshNames.forEach((name) => assignedMeshes.add(name)));
      const unassignedMeshNames = meshes
        .map((mesh) => mesh.name)
        .filter((name) => !assignedMeshes.has(name));
      const floorIds = Object.keys(floorMap);
      if (floorIds.length > 0) {
        const firstFloorId = floorIds[0];
        const firstFloor = floorMap[firstFloorId];
        const firstFloorMeshes = firstFloor?.meshNames?.map(name => scene.getMeshByName(name)).filter((m): m is AbstractMesh => m !== null) || [];
        if (firstFloorMeshes.length > 0) {
          const minY = Math.min(...firstFloorMeshes.map(mesh => mesh.getBoundingInfo().boundingBox.minimumWorld.y));
          const maxY = Math.max(...firstFloorMeshes.map(mesh => mesh.getBoundingInfo().boundingBox.maximumWorld.y));
        }
      }
      this.parsedMetadata = {
        floorMeshes: floorMap,
        roomMeshes: roomMap,
        beaconMeshes: beaconMap,
        unassignedMeshNames,
      };

      // Build floor mapping table with actual elevations for accurate detection
      this.floorMappingTable.clear();
      Object.entries(floorMap).forEach(([floorId, floorData]) => {
        this.floorMappingTable.set(floorId, floorData);
      });
      if (this.isDestroyed || scene.isDisposed) {
        return;
      }
      this.buildRoomMeshIndex(scene);
      this.logMeshDiagnostics();
      if (this.isEmbeddedMapOverlay()) {
        this.enhanceLoadedModelMaterials(scene);
        this.frameCameraToLoadedModel(scene);
      } else {
        this.applyFloorVisualStyle(scene);
        this.enhanceLoadedModelMaterials(scene);
        this.frameCameraToLoadedModel(scene);
        this.hideWallMeshes(scene);
      }
      this.logAvailableFloors();
      await this.renderBeaconAssets();
      if (this.isDestroyed || scene.isDisposed) {
        return;
      }
      this.renderAlertAssets();
      if (!this.isEmbeddedMapOverlay()) {
        await this.syncLiveUserAssets();
        await this.processPlaceholders();
        await this.placeAnchorBasedAssets();
        this.applyFloorLayoutRotations();
        this.hideAllWindows(scene);
        this.applyRoomAndSliceLayerMasks(null, null);
        this.syncRoomStatusColors();
        this.renderAlertAssets();
        // Reapply alert highlights after placeholder replacement to capture any
        // newly instantiated meshes that belong to alerted rooms.
        this.renderAlertAssets();
      }
      this.metadataParsed.emit(this.parsedMetadata);
    } finally {
      if (!this.isDestroyed) {
        const deferLoader = this.isBlenderFloorPresentation();
        if (!deferLoader) {
          this.setModelLoading(false);
        }
      }
    }

  }

  public renderAlertAssets(): void {
    if (!this.scene || this.scene.isDisposed || this.isDestroyed) {
      return;
    }

    this.syncRoomStatusColors();
    const seenAlertRooms = new Set<string>();
    const alertIconRecords = (this.alertRecords || []).filter((record) => {
      if (!this.shouldShowAlertIconForRecord(record)) {
        return false;
      }
      if (!record.roomId) {
        return true;
      }
      const roomKey = this.resolveCanonicalRoomToken(String(record.roomId));
      if (seenAlertRooms.has(roomKey)) {
        return false;
      }
      seenAlertRooms.add(roomKey);
      return true;
    });

    this.babylonVisualizeService.syncAlertSprites(
      this.scene,
      alertIconRecords,
      (record) => this.resolveAlertAssetPlacement(record),
      { uprightIconCorrection: this.isBlenderFloorPresentation() }
    );
  }

  /** Show icons only on rooms that receive alert orange tint (not user-status overrides). */
  private shouldShowAlertIconForRecord(record: ViewerAlertRecord): boolean {
    if (!record.roomId) {
      return !!record.floorId;
    }
    const canonical = this.resolveCanonicalRoomToken(String(record.roomId));
    const statusMap = this.latestLiveUserData?.roomStatusByRoomId;
    if (statusMap) {
      const counts =
        statusMap[canonical] ??
        statusMap[String(record.roomId)] ??
        statusMap[this.normalizeEntityId(record.roomId) || ''];
      if (counts && resolveRoomStatusTier(counts) !== 'neutral') {
        return false;
      }
    }
    return this.collectAlertHighlightAnchorMeshes(canonical).length > 0;
  }

  private restoreNeutralRoomSlices(): void {
    if (!this.scene || this.scene.isDisposed) {
      return;
    }
    const baseMaterial = this.floorSliceSharedMaterial;
    this.scene.meshes.forEach((mesh) => {
      if (!mesh || mesh.name === '__root__') {
        return;
      }
      if (this.isCustomRoomWallMesh(mesh) || this.isCustomRoomFloorMesh(mesh)) {
        return;
      }
      const extras = this.readGltfMeshFloorRoomIds(mesh);
      const roomId = extras.roomId ? this.normalizeEntityId(extras.roomId) : null;
      if (!roomId) {
        return;
      }
      const hasAlert = (this.alertRecords || []).some((alert) =>
        this.normalizeEntityId(alert.roomId) === roomId
      );
      if (!hasAlert) {
        if (mesh instanceof InstancedMesh) {
          mesh.renderOverlay = false;
        } else if (baseMaterial) {
          mesh.material = baseMaterial;
          mesh.isVisible = baseMaterial.alpha > 0;
        }
      }
    });

  }

  private async syncLiveUserAssets(): Promise<void> {
    if (!this.scene || this.scene.isDisposed || this.isDestroyed) {
      return;
    }

    // Skip if model hasn't been parsed yet — roomMeshIndex and parsedMetadata are empty.
    // Colors will be applied after model load when syncLiveUserAssets is called from the load path.
    const hasRoomMeshes = this.roomMeshIndex.size > 0 || Object.keys(this.parsedMetadata?.roomMeshes || {}).length > 0;
    if (!hasRoomMeshes) {
      return;
    }
    this.liveUserAssetService.init(this.scene, this.liveUserAssetConfig ?? undefined);
    const occupancies: RoomOccupancy[] = [];
    if (this.latestLiveUserData) {
      const byRoom = this.latestLiveUserData.byRoom || {};
      const rawStatusByRoom = this.latestLiveUserData.roomStatusByRoomId || {};
      const merged = new Map<string, RoomOccupancy>();

      for (const [roomId, records] of Object.entries(byRoom)) {
        const canonicalToken = this.resolveCanonicalRoomToken(roomId);
        if (!canonicalToken || !records.length) {
          continue;
        }

        const statusFromRaw = rawStatusByRoom[roomId];
        const existing = merged.get(canonicalToken);
        if (existing) {
          existing.count += records.length;
          if (statusFromRaw) {
            existing.statusCounts = existing.statusCounts ?? { danger: 0, safe: 0, unknown: 0 };
            existing.statusCounts.danger += statusFromRaw.danger ?? 0;
            existing.statusCounts.safe += statusFromRaw.safe ?? 0;
            existing.statusCounts.unknown += statusFromRaw.unknown ?? 0;
          }
        } else {
          merged.set(canonicalToken, {
            roomId: canonicalToken,
            count: records.length,
            statusCounts: statusFromRaw
              ? {
                danger: statusFromRaw.danger ?? 0,
                safe: statusFromRaw.safe ?? 0,
                unknown: statusFromRaw.unknown ?? 0,
              }
              : undefined,
          });
        }
      }

      occupancies.push(...merged.values());
    }
    // Always call syncOccupancy — even with an empty array — so rooms that
    // no longer have users are properly cleared (count → 0, avatars removed).
    const enrichedRoomMeshIndex = this.buildEnrichedRoomMeshIndex(occupancies);
    await this.liveUserAssetService.syncOccupancy(occupancies, enrichedRoomMeshIndex);
    this.reparentLiveUserNodes();
    this.liveUserAssetService.refreshLayouts(enrichedRoomMeshIndex);
    this.syncRoomStatusColors();
    this.renderAlertAssets();
  }

  public refreshLiveUserAssets(): void {
    this.syncLiveUserAssets();

  }
  /**
  
     * Status tints from `roomStatusByRoomId` (polling). Matches API room ids to meshes via
  
     * `normalizeEntityId` on both API keys and `group.roomId`, and via `roomMeshIndex` (same tokens as live users).
  
     * Resets every candidate mesh to neutral first so indexed-only rooms clear when API data disappears.
  
     */

  private syncRoomStatusColors(): void {
    if (!this.scene) {
      return;
    }
    this.doSyncRoomStatusColors();

  }

  private doSyncRoomStatusColors(): void {
    if (!this.scene) {
      return;
    }
    this.ensureFloorSliceSharedMaterial();
    let raw = this.latestLiveUserData?.roomStatusByRoomId;

    // Fallback: derive status counts from byRoom records if backend omits roomStatusByRoomId
    if ((!raw || Object.keys(raw).length === 0) && this.latestLiveUserData?.byRoom) {
      raw = {};
      for (const [roomId, records] of Object.entries(this.latestLiveUserData.byRoom)) {
        const counts = { danger: 0, safe: 0, unknown: 0 };
        for (const record of records as any[]) {
          const status = String(record?.status || 'unknown').toUpperCase();
          if (status === 'DANGER') counts.danger++;
          else if (status === 'SAFE') counts.safe++;
          else if (status === 'UNKNOWN') counts.unknown++;
        }
        raw[roomId] = counts;
      }
    }
    const normalized = new Map<string, ViewerRoomStatusCounts>();
    if (raw) {
      for (const [k, v] of Object.entries(raw)) {
        const nk = this.normalizeEntityId(k);
        if (nk) {
          normalized.set(nk, { danger: v.danger ?? 0, safe: v.safe ?? 0, unknown: v.unknown ?? 0 });
        }
      }
    }
    const collectAllRoomTintMeshes = (): AbstractMesh[] => {
      const seen = new Set<number>();
      const out: AbstractMesh[] = [];
      const add = (mesh: AbstractMesh | null | undefined) => {
        if (!mesh || !mesh.geometry) {
          return;
        }
        if (seen.has(mesh.uniqueId)) {
          return;
        }
        seen.add(mesh.uniqueId);
        out.push(mesh);
      };
      for (const list of this.roomMeshIndex.values()) {
        for (const m of list) {
          add(m);
        }
      }
      for (const group of Object.values(this.parsedMetadata.roomMeshes)) {
        for (const meshName of group.meshNames) {
          add(this.scene!.getMeshByName(meshName));
        }
      }
      return out;
    };
    const alertRoomTokens = this.collectAlertRoomTokens();
    const userRoomTokens = new Set(normalized.keys());
    const previouslyTintedRoomTokens = new Set<string>();
    for (const group of Object.values(this.parsedMetadata.roomMeshes)) {
      const roomId = this.normalizeEntityId(group.roomId);
      if (roomId) {
        previouslyTintedRoomTokens.add(roomId);
      }
    }
    for (const token of previouslyTintedRoomTokens) {
      if (userRoomTokens.has(token) || alertRoomTokens.has(token)) {
        continue;
      }
      for (const group of Object.values(this.parsedMetadata.roomMeshes)) {
        if (this.normalizeEntityId(group.roomId) !== token) {
          continue;
        }
        for (const meshName of group.meshNames) {
          const mesh = this.scene!.getMeshByName(meshName);
          if (mesh) {
            this.applyRoomStatusMaterial(mesh, 'neutral');
          }
          const sliceMesh = this.resolveFloorSliceMesh(String(group.floorId ?? ''), meshName);
          if (sliceMesh) {
            this.applyRoomStatusMaterial(sliceMesh, 'neutral');
          }
        }
      }
    }
    normalized.forEach((counts, token) => {
      const tier = resolveRoomStatusTier(counts);
      if (tier === 'neutral') {
        return;
      }
      this.applyRoomTintToToken(token, tier);
    });

    for (const record of this.alertRecords || []) {
      if (!record.roomId) {
        continue;
      }
      const canonical = this.resolveCanonicalRoomToken(String(record.roomId));
      const counts = normalized.get(canonical);
      const userTier = counts ? resolveRoomStatusTier(counts) : 'neutral';
      if (userTier !== 'neutral') {
        continue;
      }
      this.applyRoomTintToToken(canonical, 'alert');
    }
  }

  /** Priority: user status tier > alert orange > neutral/default. */
  private resolveRoomTintTier(
    roomToken: string,
    statusByRoom: Map<string, ViewerRoomStatusCounts>,
    alertRoomTokens: Set<string>
  ): RoomTintTier {
    const counts = statusByRoom.get(roomToken);
    const userTier = counts ? resolveRoomStatusTier(counts) : 'neutral';
    if (userTier !== 'neutral') {
      return userTier;
    }
    if (alertRoomTokens.has(roomToken)) {
      return 'alert';
    }
    return 'neutral';
  }

  private collectAlertRoomTokens(): Set<string> {
    const tokens = new Set<string>();
    for (const record of this.alertRecords || []) {
      if (!record.roomId) {
        continue;
      }
      const canonical = this.resolveCanonicalRoomToken(String(record.roomId));
      if (canonical) {
        tokens.add(canonical);
      }
      const normalized = this.normalizeEntityId(record.roomId);
      if (normalized) {
        tokens.add(normalized);
      }
    }
    return tokens;
  }

  private roomTokenMatchesGroup(token: string, group: RoomMeshGroup): boolean {
    const normalized = this.normalizeEntityId(token);
    if (!normalized) {
      return false;
    }
    const candidates = [group.roomId, ...(group.perimeterIds || [])]
      .map((id) => this.normalizeEntityId(id))
      .filter((id): id is string => !!id);
    return candidates.some(
      (candidate) =>
        candidate === normalized ||
        candidate.endsWith(normalized) ||
        normalized.endsWith(candidate)
    );
  }

  private resolveActiveRoomTintTarget(mesh: AbstractMesh): AbstractMesh {
    const sliceMesh = this.findSliceMeshForRoomMesh(mesh);
    if (sliceMesh && (sliceMesh.isEnabled() || sliceMesh.isVisible)) {
      return sliceMesh;
    }
    if (mesh.isEnabled() || mesh.isVisible || !sliceMesh) {
      return mesh;
    }
    return sliceMesh || mesh;
  }

  private applyRoomTintToToken(token: string, tier: RoomTintTier): void {
    if (!this.scene || tier === 'neutral') {
      return;
    }
    const meshes = this.resolveRoomMeshesByToken(token);
    const seen = new Set<number>();
    for (const mesh of meshes) {
      const target = this.resolveActiveRoomTintTarget(mesh);
      if (seen.has(target.uniqueId)) {
        continue;
      }
      seen.add(target.uniqueId);
      if (!target.isEnabled() || !target.isVisible) {
        target.setEnabled(true);
        target.isVisible = true;
      }
      this.applyRoomStatusMaterial(target, tier);
    }
    for (const group of Object.values(this.parsedMetadata.roomMeshes)) {
      if (!this.roomTokenMatchesGroup(token, group)) {
        continue;
      }
      for (const meshName of group.meshNames) {
        const mesh = this.scene!.getMeshByName(meshName);
        if (!mesh) {
          continue;
        }
        const target = this.resolveActiveRoomTintTarget(mesh);
        if (seen.has(target.uniqueId)) {
          continue;
        }
        seen.add(target.uniqueId);
        if (!target.isEnabled() || !target.isVisible) {
          target.setEnabled(true);
          target.isVisible = true;
        }
        this.applyRoomStatusMaterial(target, tier);
      }
    }
  }

  private findSliceMeshForRoomMesh(mesh: AbstractMesh): AbstractMesh | null {
    if (!this.scene) {
      return null;
    }
    for (const group of Object.values(this.parsedMetadata.roomMeshes)) {
      const matchesGroup = group.meshNames.some((meshName) => {
        const resolved = this.resolveSceneMeshByMetadataName(meshName);
        return resolved?.uniqueId === mesh.uniqueId || meshName === mesh.name;
      });
      if (!matchesGroup) {
        continue;
      }
      return this.resolveFloorSliceMesh(String(group.floorId ?? ''), mesh.name);
    }
    return null;
  }

  /** Resolve a metadata meshName to the scene mesh (handles path vs leaf name). */
  private resolveSceneMeshByMetadataName(meshName: string): AbstractMesh | null {
    if (!this.scene || !meshName) {
      return null;
    }
    const direct = this.scene.getMeshByName(meshName);
    if (direct) {
      return direct;
    }
    const leaf = meshName.split('/').pop() || meshName;
    const byLeaf = this.scene.getMeshByName(leaf);
    if (byLeaf) {
      return byLeaf;
    }
    return (
      this.scene.meshes.find((mesh) => (mesh.name.split('/').pop() || '') === leaf) ?? null
    );
  }

  /**
   * Floor slices are cloned as `floorSlice_${floorId}_${sourceMesh.name}`; metadata meshNames
   * may include glTF paths, so try direct, source mesh, and leaf variants.
   */
  private resolveFloorSliceMesh(floorId: string, meshName: string): AbstractMesh | null {
    if (!this.scene || !floorId || !meshName) {
      return null;
    }
    const direct = this.scene.getMeshByName(`floorSlice_${floorId}_${meshName}`);
    if (direct) {
      return direct;
    }
    const source = this.resolveSceneMeshByMetadataName(meshName);
    if (source) {
      const bySourceName = this.scene.getMeshByName(`floorSlice_${floorId}_${source.name}`);
      if (bySourceName) {
        return bySourceName;
      }
    }
    const leaf = meshName.split('/').pop() || meshName;
    return this.scene.getMeshByName(`floorSlice_${floorId}_${leaf}`);
  }

  /**
   * Same room matching as {@link highlightBabylonRooms}, but anchor icons on layout-rotated
   * floor slices when present (the meshes that receive the orange alert tint).
   */
  private collectAlertHighlightTargetMeshes(roomToken: string): AbstractMesh[] {
    if (!this.scene || !roomToken) {
      return [];
    }
    const normalizedTargets = new Set<string>();
    const normalized = this.normalizeEntityId(roomToken);
    if (normalized) {
      normalizedTargets.add(normalized);
    }
    const canonical = this.resolveCanonicalRoomToken(roomToken);
    const canonicalNormalized = this.normalizeEntityId(canonical);
    if (canonicalNormalized) {
      normalizedTargets.add(canonicalNormalized);
    }

    const anchorMeshes: AbstractMesh[] = [];
    const seen = new Set<number>();
    const addAnchor = (mesh: AbstractMesh | null | undefined) => {
      if (!mesh?.geometry || seen.has(mesh.uniqueId)) {
        return;
      }
      seen.add(mesh.uniqueId);
      anchorMeshes.push(mesh);
    };

    Object.values(this.parsedMetadata.roomMeshes).forEach((group) => {
      const groupRoomToken = this.normalizeEntityId(group.roomId);
      const perimeterTokens = (group.perimeterIds || [])
        .map((id) => this.normalizeEntityId(id))
        .filter((id): id is string => !!id);
      const matched =
        (groupRoomToken && normalizedTargets.has(groupRoomToken)) ||
        perimeterTokens.some((token) => normalizedTargets.has(token)) ||
        this.roomTokenMatchesGroup(roomToken, group);
      if (!matched) {
        return;
      }

      group.meshNames.forEach((meshName) => {
        const floorKey = String(group.floorId ?? '');
        const sliceMesh = floorKey ? this.resolveFloorSliceMesh(floorKey, meshName) : null;
        if (sliceMesh) {
          addAnchor(sliceMesh);
          return;
        }
        const mesh = this.resolveSceneMeshByMetadataName(meshName);
        if (mesh?.material) {
          addAnchor(mesh);
        }
      });
    });

    return anchorMeshes;
  }

  /**
  * Meshes that receive alert orange tint — same matching as {@link applyRoomTintToToken}.
  * Prefer visible floor slices (layout-rotated) for anchor bounds.
  */
  private collectAlertHighlightAnchorMeshes(roomToken: string): AbstractMesh[] {
    const candidates = this.collectAlertHighlightTargetMeshes(roomToken);
    const visibleSlices = candidates.filter(
      (mesh) => mesh.name.startsWith('floorSlice_') && mesh.isEnabled() && mesh.isVisible
    );
    if (visibleSlices.length) {
      return visibleSlices;
    }
    const visible = candidates.filter((mesh) => mesh.isEnabled() && mesh.isVisible);
    if (visible.length) {
      return visible;
    }
    return candidates;
  }

  /** World bounds for room meshes — same approach as {@link LiveUserAssetService} clusters. */
  private computeRoomWorldBounds(
    meshes: AbstractMesh[]
  ): { min: Vector3; max: Vector3 } | null {
    if (!meshes.length) {
      return null;
    }
    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    for (const mesh of meshes) {
      if (!mesh.geometry) {
        continue;
      }
      mesh.computeWorldMatrix(true);
      mesh.refreshBoundingInfo(true, false);
      const bb = mesh.getBoundingInfo().boundingBox;
      minX = Math.min(minX, bb.minimumWorld.x);
      minY = Math.min(minY, bb.minimumWorld.y);
      minZ = Math.min(minZ, bb.minimumWorld.z);
      maxX = Math.max(maxX, bb.maximumWorld.x);
      maxY = Math.max(maxY, bb.maximumWorld.y);
      maxZ = Math.max(maxZ, bb.maximumWorld.z);
    }
    if (minX === Infinity) {
      return null;
    }
    return { min: new Vector3(minX, minY, minZ), max: new Vector3(maxX, maxY, maxZ) };
  }

  /** Place alert icon above room top — mirrors user cluster marker vertical placement. */
  private computeAlertIconPositionAboveRoom(meshes: AbstractMesh[]): Vector3 | null {
    const bounds = this.computeRoomWorldBounds(meshes);
    if (!bounds) {
      return null;
    }
    const standY = this.resolveRoomStandWorldY(meshes, bounds);
    const center = this.clampCenterToRoomFootprint(bounds, standY);
    const clusterHeadOffset = 0.35;
    const clusterSphereRadius = 0.5;
    const iconY = bounds.max.y + clusterHeadOffset + clusterSphereRadius;
    return new Vector3(center.x, iconY, center.z);
  }

  private resolveRoomStandWorldY(
    meshes: AbstractMesh[],
    union: { min: Vector3; max: Vector3 }
  ): number {
    const unionMidY = (union.min.y + union.max.y) / 2;
    const slabs: number[] = [];
    for (const mesh of meshes) {
      if (!mesh.geometry) {
        continue;
      }
      mesh.computeWorldMatrix(true);
      mesh.refreshBoundingInfo(true, false);
      const bb = mesh.getBoundingInfo().boundingBox;
      const sx = Math.abs(bb.maximumWorld.x - bb.minimumWorld.x);
      const sy = Math.abs(bb.maximumWorld.y - bb.minimumWorld.y);
      const sz = Math.abs(bb.maximumWorld.z - bb.minimumWorld.z);
      const horiz = Math.sqrt(Math.max(sx * sz, 1e-8));
      const isFlat = sy < Math.max(horiz * 0.4, 0.08) && sy < sx && sy < sz;
      const topY = bb.maximumWorld.y;
      if (isFlat && topY <= unionMidY) {
        slabs.push(topY);
      }
    }
    if (slabs.length) {
      return Math.max(...slabs);
    }
    return union.min.y;
  }

  private clampCenterToRoomFootprint(
    bounds: { min: Vector3; max: Vector3 },
    standY: number
  ): Vector3 {
    const margin = 0.08;
    const rangeX = bounds.max.x - bounds.min.x;
    const rangeZ = bounds.max.z - bounds.min.z;
    const innerMinX = bounds.min.x + rangeX * margin;
    const innerMaxX = bounds.max.x - rangeX * margin;
    const innerMinZ = bounds.min.z + rangeZ * margin;
    const innerMaxZ = bounds.max.z - rangeZ * margin;
    const cx = (bounds.min.x + bounds.max.x) / 2;
    const cz = (bounds.min.z + bounds.max.z) / 2;
    const x = innerMinX <= innerMaxX ? Math.max(innerMinX, Math.min(innerMaxX, cx)) : cx;
    const z = innerMinZ <= innerMaxZ ? Math.max(innerMinZ, Math.min(innerMaxZ, cz)) : cz;
    return new Vector3(x, standY, z);
  }

  private ensureFloorSliceSharedMaterial(): void {
    if (this.floorSliceSharedMaterial) {
      return;
    }
    if (!this.scene) {
      return;
    }
    const config = this.visualConfig;
    const floorMaterial = new StandardMaterial('floorSliceStyleMat_lazy', this.scene);
    floorMaterial.diffuseColor = Color3.FromHexString(config.fillColorHex);
    floorMaterial.emissiveColor = Color3.FromHexString(config.emissiveColorHex);
    floorMaterial.alpha = config.fillAlpha;
    floorMaterial.transparencyMode = Material.MATERIAL_ALPHABLEND;
    floorMaterial.specularColor = Color3.Black();
    floorMaterial.backFaceCulling = false;
    floorMaterial.zOffset = -1;
    floorMaterial.zOffsetUnits = -1;
    this.floorSliceSharedMaterial = floorMaterial;

  }

  private applyRoomStatusMaterial(mesh: AbstractMesh, tier: RoomTintTier): void {
    if (!this.scene) {
      return;
    }
    const hex =
      tier === 'neutral'
        ? '#ffffff'
        : tier === 'alert'
          ? ALERT_ROOM_COLOR_HEX
          : this.resolvedRoomStatusHex[tier];

    const diffuse = Color3.FromHexString(hex);
    if (this.isCustomRoomWallMesh(mesh) || this.isCustomRoomFloorMesh(mesh)) {
      if (mesh instanceof InstancedMesh) {
        if (tier === 'neutral') {
          if (!mesh.renderOverlay) {
            mesh.renderOverlay = false;
          }
        } else {
          // Skip if overlay already set to same color
          if (mesh.renderOverlay && mesh.overlayColor && mesh.overlayColor.equals(diffuse)) {
            return;
          }
          mesh.renderOverlay = true;
          mesh.overlayColor = diffuse;
        }
        return;
      }
      // For non-instanced custom meshes, fall through to standard material logic below
    }
    const roomGroup = Object.values(this.parsedMetadata.roomMeshes).find(group =>
      group.meshNames.includes(mesh.name)
    );
    const roomId = roomGroup
      ? this.normalizeEntityId(roomGroup.roomId)
      : null;
    const current = mesh.material;
    const statusMaterialPrefix =
      tier === 'alert' ? 'room_status_alert_' : `room_status_${tier}_`;
    if (current && current.name?.startsWith(statusMaterialPrefix)) {
      return;
    }

    // For neutral tier, only skip if material is already neutral AND edges are already disabled
    if (tier === 'neutral' && current === this.floorSliceSharedMaterial && !mesh.edgesRenderer) {
      return;
    }
    if (!(mesh instanceof InstancedMesh) && current?.name?.startsWith('room_status_') && current !== this.floorSliceSharedMaterial) {
      current.dispose();
    }
    if (tier === 'neutral') {
      const original = this.originalGlbMaterials.get(mesh);
      if (mesh instanceof InstancedMesh) {
        mesh.renderOverlay = false;
      } else {
        mesh.material = this.floorSliceSharedMaterial;
      }

      // Keep edges enabled for neutral rooms so room footprints remain visible
      if (this.visualConfig.showEdges) {
        mesh.enableEdgesRendering(0.99);
        mesh.edgesWidth = this.visualConfig.edgeWidth;
        const edgeColor = Color3.FromHexString(this.visualConfig.edgeColorHex);
        mesh.edgesColor = new Color4(edgeColor.r, edgeColor.g, edgeColor.b, 1);
      }
      return;
    }
    if (mesh instanceof InstancedMesh) {

      // Skip if overlay already set to same color for this tier
      if (mesh.renderOverlay && mesh.overlayColor && mesh.overlayColor.equals(diffuse)) {
        return;
      }
      mesh.renderOverlay = true;
      mesh.overlayColor = diffuse;
      return;
    }
    const mat = new StandardMaterial(`${statusMaterialPrefix}${mesh.name}`, this.scene);
    mat.diffuseColor = diffuse;
    mat.emissiveColor =
      tier === 'alert'
        ? new Color3(0.9, 0.55, 0.25)
        : tier === 'unknown'
          ? new Color3(diffuse.r * 0.75, diffuse.g * 0.75, diffuse.b * 0.75)
          : new Color3(diffuse.r * 0.55, diffuse.g * 0.55, diffuse.b * 0.55);
    mat.alpha = tier === 'alert' ? 0.6 : 0.6;
    mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
    mat.specularColor = Color3.Black();
    mat.backFaceCulling = false;
    mat.zOffset = -2;
    mat.zOffsetUnits = -2;
    mesh.material = mat;
    if (this.visualConfig.showEdges) {
      mesh.enableEdgesRendering(0.99);
      mesh.edgesWidth = this.visualConfig.edgeWidth;
      mesh.edgesColor = new Color4(diffuse.r, diffuse.g, diffuse.b, 1);
    } else {
      mesh.disableEdgesRendering();
    }

  }

  private resolveAlertAssetPlacement(record: ViewerAlertRecord): AlertSpritePlacement | null {
    const roomToken = record.roomId ? this.resolveCanonicalRoomToken(String(record.roomId)) : '';
    if (roomToken) {
      const roomMeshes = this.collectAlertHighlightAnchorMeshes(roomToken);
      if (roomMeshes.length) {
        const position = this.computeAlertIconPositionAboveRoom(roomMeshes);
        if (!position) {
          return null;
        }
        return { meshes: roomMeshes, type: 'ROOM', position };
      }
    }
    const floorToken = this.normalizeBabylonFloorNumber(record.floorId || '');
    if (floorToken) {
      const floorMeshes: AbstractMesh[] = [];
      Object.entries(this.parsedMetadata.floorMeshes).forEach(([key, group]) => {
        if (String(this.floorSortKey(key)) !== floorToken) {
          return;
        }
        group.meshNames.forEach((meshName) => {
          const mesh = this.scene!.getMeshByName(meshName);
          if (mesh) {
            floorMeshes.push(mesh);
          }
        });
      });
      if (floorMeshes.length) {
        const position = this.computeAlertIconPositionAboveRoom(floorMeshes);
        if (!position) {
          return null;
        }
        return { meshes: floorMeshes, type: 'FLOOR', position };
      }
    }
    return null;

  }

  public logAvailableFloors(): void {
    Object.keys(this.parsedMetadata.floorMeshes).forEach(floorId => {
      const floorGroup = this.parsedMetadata.floorMeshes[floorId];
      floorGroup.meshNames.forEach(meshName => {
      });
    });
    Object.keys(this.parsedMetadata.roomMeshes).slice(0, 20).forEach(roomId => {
      const roomGroup = this.parsedMetadata.roomMeshes[roomId];
    });

  }

  public ensureCrispResize(): void {
    if (!this.engine) {
      return;
    }
    const dpr = Math.max(window.devicePixelRatio || 1, 1);
    this.fitCanvasToContainer();
    this.engine.setHardwareScalingLevel(1 / dpr);
    this.engine.resize();
    this.scene?.render();
    requestAnimationFrame(() => {
      if (!this.engine) return;
      this.fitCanvasToContainer();
      this.engine.setHardwareScalingLevel(1 / dpr);
      this.engine.resize();
      this.scene?.render();
    });
  }

  private extractFloorId(meshName: string, knownFloors: string[]): string | undefined {
    const floorMatch = meshName.match(/F\d{3}/i);
    if (floorMatch) {
      return floorMatch[0].toUpperCase();
    }
    return knownFloors.find((floor) => meshName.toUpperCase().includes(floor.toUpperCase()));

  }

  private extractRoomId(meshName: string, knownRooms: Set<string>): string | undefined {

    // Match Room_123456 pattern or R123456 pattern
    const roomMatch = meshName.match(/(?:Room_)?(\d{5,})/i);
    if (roomMatch) {
      return roomMatch[1].toUpperCase();
    }
    const upperName = meshName.toUpperCase();
    for (const roomId of knownRooms) {
      if (upperName.includes(roomId.toUpperCase())) {
        return roomId.toUpperCase();
      }
    }
    return undefined;

  }

  private isBeaconMesh(meshName: string): boolean {
    const upperName = meshName.toUpperCase();
    return upperName.includes('BEACON') || upperName.includes('ANCHOR');

  }

  private isRoomWallNameSegment(name: string | null | undefined): boolean {
    if (!name) {
      return false;
    }
    const normalized = String(name).trim();
    if (!normalized) {
      return false;
    }
    const upper = normalized.toUpperCase();
    return upper.includes('WALL_');
  }

  private isCustomRoomWallMesh(mesh: AbstractMesh | null | undefined): boolean {
    if (!mesh) {
      return false;
    }
    const matchesName = (value: string | null | undefined): boolean => {
      if (!value) {
        return false;
      }
      if (this.isRoomWallNameSegment(value)) {
        return true;
      }
      const segments = String(value).split(/[\\/]/);
      return segments.some((segment) => this.isRoomWallNameSegment(segment));
    };
    let current: any = mesh;
    while (current) {
      if (matchesName(current.name)) {
        return true;
      }
      current = current.parent;
    }
    const materialName = (mesh.material as any)?.name;
    return matchesName(materialName);
  }

  private isRoomFloorNameSegment(name: string | null | undefined): boolean {
    if (!name) {
      return false;
    }
    const normalized = String(name).trim();
    if (!normalized) {
      return false;
    }
    const candidates = normalized.split(/[\\/]/).map((segment) => segment.trim()).filter(Boolean);
    if (!candidates.length) {
      return false;
    }
    const allowedSuffixPattern = /^(PRIMITIVE|PRIM|MESH|NODE|MARKER)(\d+)?$/;
    const matchesSegment = (segment: string): boolean => {
      const upper = segment.toUpperCase();
      if (!upper.startsWith('FLOOR_')) {
        return false;
      }
      const remainder = upper.slice('FLOOR_'.length);
      if (!remainder) {
        return false;
      }
      const parts = remainder.split('_').filter(Boolean);
      const [idPart, ...suffixParts] = parts;
      if (!idPart || !/^\d+$/.test(idPart)) {
        return false;
      }
      if (suffixParts.length === 0) {
        return true;
      }
      return suffixParts.every((suffix) => allowedSuffixPattern.test(suffix));
    };
    return candidates.some(matchesSegment);
  }

  private isCustomRoomFloorMesh(mesh: AbstractMesh | null | undefined): boolean {
    if (!mesh) {
      return false;
    }
    const matchesName = (value: string | null | undefined): boolean => this.isRoomFloorNameSegment(value);
    if (matchesName(mesh.name)) {
      return true;
    }
    const materialName = (mesh.material as any)?.name;
    return matchesName(materialName);
  }

  private meshHasCustomRoomWallChildren(mesh: AbstractMesh): boolean {
    return mesh
      .getChildMeshes(false)
      .some((child) => this.isCustomRoomWallMesh(child));
  }

  private detachCustomRoomWallChildren(mesh: AbstractMesh): void {
    mesh.getChildMeshes(false).forEach((child) => {
      if (!this.isCustomRoomWallMesh(child)) {
        return;
      }
      child.computeWorldMatrix(true);
      child.setParent(null);
      this.applyRoomWallHighlight(child);
    });
  }

  private detachCustomRoomFloorChildren(mesh: AbstractMesh): void {
    mesh.getChildMeshes(false).forEach((child) => {
      if (!this.isCustomRoomFloorMesh(child)) {
        return;
      }
      child.computeWorldMatrix(true);
      child.setParent(null);
      this.applyRoomWallHighlight(child);
    });
  }

  private applyRoomWallHighlight(mesh: AbstractMesh): void {
    if (!mesh) {
      return;
    }
    const scene = mesh.getScene();
    if (!scene) {
      return;
    }
    mesh.setEnabled(true);
    mesh.isVisible = true;
    mesh.isPickable = true;

    // For InstancedMesh, use renderOverlay instead of setting material (which has no effect)
    if (mesh instanceof InstancedMesh) {
      mesh.renderOverlay = false;
      mesh.disableEdgesRendering();
      return;
    }
    let material = mesh.material as StandardMaterial | null;
    const isHighlightMaterial = material?.name?.startsWith('wall_highlight_') ?? false;
    if (!material || !isHighlightMaterial) {
      material = new StandardMaterial(`wall_highlight_${mesh.uniqueId}`, scene);
      material.backFaceCulling = false;
      material.specularColor = Color3.Black();
      mesh.material = material;
    }
    material.diffuseColor = new Color3(0, 0, 0);
    material.emissiveColor = new Color3(0, 0, 0);
    material.alpha = 0;
    mesh.disableEdgesRendering();
  }

  private refreshCustomRoomWallMeshes(): void {
    if (!this.scene) {
      return;
    }
    // Reapply current room status colors to custom wall/floor meshes instead of always applying cyan.
    // This preserves user status colors (danger/safe/unknown) during floor isolation.
    this.scene.meshes.forEach((candidate) => {
      if (this.isCustomRoomWallMesh(candidate) || this.isCustomRoomFloorMesh(candidate)) {
        // Find the room group for this mesh to determine its current status tier
        const roomGroup = Object.values(this.parsedMetadata?.roomMeshes || {}).find(group =>
          group.meshNames?.includes(candidate.name)
        );
        const roomId = roomGroup ? this.normalizeEntityId(roomGroup.roomId) : null;

        const alertRoomTokens = this.collectAlertRoomTokens();
        const statusByRoom = new Map<string, ViewerRoomStatusCounts>();
        const raw = this.latestLiveUserData?.roomStatusByRoomId;
        if (raw) {
          for (const [k, v] of Object.entries(raw)) {
            const nk = this.normalizeEntityId(k);
            if (nk) {
              statusByRoom.set(nk, { danger: v.danger ?? 0, safe: v.safe ?? 0, unknown: v.unknown ?? 0 });
            }
          }
        }
        const tier = roomId
          ? this.resolveRoomTintTier(roomId, statusByRoom, alertRoomTokens)
          : 'neutral';

        if (tier !== 'neutral') {
          this.applyRoomStatusMaterial(candidate, tier);
        } else {
          // No user status — just enable visibility without overriding with cyan
          candidate.setEnabled(true);
          candidate.isVisible = true;
        }
      }
    });
  }

  private isWallMesh(meshName: string): boolean {
    if (this.isRoomWallNameSegment(meshName)) {
      return false;
    }
    const segments = String(meshName).split(/[\\/]/);
    if (segments.some((segment) => this.isRoomWallNameSegment(segment))) {
      return false;
    }
    const upperName = meshName.toUpperCase();
    return upperName.includes('WALL');

  }

  private isRoomCutoutStructuralMesh(mesh: AbstractMesh): boolean {
    if (this.isCustomRoomWallMesh(mesh)) {
      return false;
    }
    if (this.isCustomRoomFloorMesh(mesh)) {
      return false;
    }
    const names: string[] = [];
    let current: any = mesh;
    while (current) {
      if (current.name) {
        names.push(String(current.name));
      }
      current = current.parent;
    }
    const material = mesh.material as any;
    if (material?.name) {
      names.push(String(material.name));
    }
    const combinedName = names.join(' ').toUpperCase();
    if (
      combinedName.includes('WALL') ||
      combinedName.includes('PARTITION') ||
      combinedName.includes('BOUNDARY') ||
      combinedName.includes('EXTERIOR') ||
      combinedName.includes('FACADE') ||
      combinedName.includes('CLADDING') ||
      combinedName.includes('ROOF') ||
      combinedName.includes('CEILING') ||
      combinedName.includes('SLAB') ||
      combinedName.includes('SHELL')
    ) {
      return true;
    }
    if (!mesh.geometry) {
      return false;
    }
    try {
      mesh.computeWorldMatrix(true);
      const box = mesh.getBoundingInfo().boundingBox;
      const size = box.maximumWorld.subtract(box.minimumWorld);
      const x = Math.abs(size.x);
      const y = Math.abs(size.y);
      const z = Math.abs(size.z);
      const minHorizontal = Math.min(x, z);
      const maxHorizontal = Math.max(x, z);
      const isThinVerticalWall = y > 0.5 && maxHorizontal > 0.5 && minHorizontal < Math.max(0.08, maxHorizontal * 0.08);
      const isFlatHorizontalPanel = y < Math.max(0.08, maxHorizontal * 0.05) && x > 0.5 && z > 0.5;
      return isThinVerticalWall || isFlatHorizontalPanel;
    } catch {
      return false;
    }

  }

  private isWindowMesh(meshNameOrMesh: string | AbstractMesh): boolean {
    const upperName = typeof meshNameOrMesh === 'string' ? meshNameOrMesh.toUpperCase() : meshNameOrMesh.name.toUpperCase();
    const nameMatch = upperName.includes('WINDOW') || upperName.includes('WINDOW_') || upperName.includes('GLASS');

    // Also check GLTF extras for window_type or is_window_placeholder
    if (typeof meshNameOrMesh !== 'string') {
      const extras = this.getGltfExtrasFromNodeChain(meshNameOrMesh);
      const windowType = extras['window_type'] ?? extras['windowType'];
      const isWindowPlaceholder = extras['is_window_placeholder'] ?? extras['isWindowPlaceholder'];
      const extrasMatch = String(windowType || '').toLowerCase() === 'window' || isWindowPlaceholder === true;

      // Check mesh color/material for red window elements
      let colorMatch = false;
      if (meshNameOrMesh.material) {
        const material = meshNameOrMesh.material as any;
        if (material.diffuseColor) {
          const r = material.diffuseColor.r;
          const g = material.diffuseColor.g;
          const b = material.diffuseColor.b;

          // Check for reddish colors (high red, lower green/blue)
          if (r > 0.6 && g < 0.5 && b < 0.5) {
            colorMatch = true;
          }
        }
      }
      return nameMatch || extrasMatch || colorMatch;
    }
    return nameMatch;

  }

  // ── Cutaway helpers (ported from POC viewer.js _cutawayFloorVisibility) ──

  private nameLooksLikeRoof(name: string): boolean {
    const n = String(name || '').toLowerCase();
    return n.includes('roof') || n.includes('ceiling');
  }

  private nameLooksLikeFurniture(name: string): boolean {
    const n = String(name || '').toLowerCase();
    return n.includes('furniture');
  }

  private nameLooksLikeWindow(name: string, mat: Material | null): boolean {
    const n = String(name || '').toLowerCase();
    const mn = String((mat as any)?.name || '').toLowerCase();
    return n.includes('window') || n.includes('glass') || n.includes('glazing')
      || mn.includes('window') || mn.includes('glass') || mn.includes('glazing');
  }

  /**
   * Apply cutaway visibility for a single floor:
   *  - Hide roof/ceiling meshes
   *  - Make wall meshes transparent (alpha=0) but still enabled
   *  - Keep furniture meshes opaque and visible
   *  - Make window/glass meshes translucent (alpha=0)
   * Ported from POC viewer.js _cutawayFloorVisibility.
   */
  private applyFloorCutaway(floorId: string): void {
    if (!this.scene) return;
    this.restoreFloorCutaway();
    const floorData = this.floorMappingTable.get(floorId);
    if (!floorData?.bounds) return;
    const bounds = floorData.bounds;
    const floorMinY = bounds.min.y;
    const floorMaxY = bounds.max.y;

    // Collect all meshes that belong to this floor by Y overlap
    const floorMeshes = this.scene.meshes.filter((mesh) => {
      if (!mesh.geometry || !mesh.name) return false;
      mesh.computeWorldMatrix(true);
      const bb = mesh.getBoundingInfo().boundingBox;
      const meshMinY = bb.minimumWorld.y;
      const meshMaxY = bb.maximumWorld.y;
      // Overlap check: mesh Y range intersects floor Y range
      return !(meshMaxY < floorMinY - 0.1 || meshMinY > floorMaxY + 0.1);
    });
    const wallSnapshot: { mesh: AbstractMesh; alpha: number; transparencyMode: number }[] = [];
    const windowSnapshot: { mesh: AbstractMesh; alpha: number; transparencyMode: number }[] = [];
    const roofMeshes: AbstractMesh[] = [];
    for (const mesh of floorMeshes) {
      const base = (mesh.name || '').split('/').pop() || '';
      if (base.startsWith('Room_')) continue; // Room meshes handled separately
      const isRoof = this.nameLooksLikeRoof(mesh.name);
      const isFurniture = this.nameLooksLikeFurniture(mesh.name);
      const isWindow = this.nameLooksLikeWindow(mesh.name, mesh.material);
      const isWall = this.isWallMesh(mesh.name);
      if (isRoof) {
        mesh.setEnabled(false);
        mesh.isVisible = false;
        roofMeshes.push(mesh);
        continue;
      }
      if (isWall && !isFurniture) {
        const mat = mesh.material as any;
        if (mat) {
          wallSnapshot.push({ mesh, alpha: mat.alpha ?? 1, transparencyMode: mat.transparencyMode ?? Material.MATERIAL_OPAQUE });
          mat.alpha = 0;
          mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
        }
        continue;
      }
      if (isWindow) {
        const mat = mesh.material as any;
        if (mat) {
          windowSnapshot.push({ mesh, alpha: mat.alpha ?? 1, transparencyMode: mat.transparencyMode ?? Material.MATERIAL_OPAQUE });
          mat.alpha = 0;
          mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
        }
        continue;
      }
    }
    this.cutawayWallMeshes.set(floorId, wallSnapshot);
    this.cutawayWindowMeshes.set(floorId, windowSnapshot);
    this.cutawayRoofMeshes.set(floorId, roofMeshes);
    this.activeCutawayFloorId = floorId;
  }

  /**
   * Restore all meshes modified by the cutaway to their original state.
   */
  private restoreFloorCutaway(): void {
    // Restore walls
    this.cutawayWallMeshes.forEach((entries) => {
      for (const { mesh, alpha, transparencyMode } of entries) {
        const mat = mesh.material as any;
        if (mat) {
          mat.alpha = alpha;
          mat.transparencyMode = transparencyMode;
        }
        mesh.setEnabled(true);
        mesh.isVisible = true;
      }
    });
    this.cutawayWallMeshes.clear();

    // Restore windows
    this.cutawayWindowMeshes.forEach((entries) => {
      for (const { mesh, alpha, transparencyMode } of entries) {
        const mat = mesh.material as any;
        if (mat) {
          mat.alpha = alpha;
          mat.transparencyMode = transparencyMode;
        }
        mesh.setEnabled(true);
        mesh.isVisible = true;
      }
    });
    this.cutawayWindowMeshes.clear();

    // Restore roofs
    this.cutawayRoofMeshes.forEach((meshes) => {
      for (const mesh of meshes) {
        mesh.setEnabled(true);
        mesh.isVisible = true;
      }
    });
    this.cutawayRoofMeshes.clear();
    this.activeCutawayFloorId = null;
  }

  // private hideWallMeshes(scene: Scene): void {
  //   // Walls are now made transparent at load time instead of fully hidden,
  //   // so the cutaway can later restore them to visible/opaque.
  //   scene.meshes.forEach((mesh) => {
  //     if (!mesh.name) return;
  //     const base = (mesh.name || '').split('/').pop() || '';
  //     if (base.startsWith('Room_')) return;

  //     if (this.isWallMesh(mesh.name)) {
  //       const mat = mesh.material as any;
  //       if (mat) {
  //         mat.alpha = 0;
  //         mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
  //       }
  //       mesh.isPickable = false;
  //     }

  //     if (this.isWindowMesh(mesh)) {
  //       const mat = mesh.material as any;
  //       if (mat) {
  //         mat.alpha = 0;
  //         mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
  //       }
  //       mesh.isPickable = false;
  //     }
  //   });
  // }

  private hideWallMeshes(scene: Scene): void {
    let wallCount = 0;
    let windowCount = 0;
    let floorPlateCount = 0;
    scene.meshes.forEach((mesh) => {
      if (mesh.name) {

        // Keep room meshes visible - don't hide them
        const base = (mesh.name || '').split('/').pop() || '';
        if (base.startsWith('Room_')) {
          return; // Skip room meshes entirely
        }
        if (this.isCustomRoomWallMesh(mesh)) {
          this.applyRoomWallHighlight(mesh);
          return;
        }
        if (this.isCustomRoomFloorMesh(mesh)) {
          this.applyRoomWallHighlight(mesh);
          return;
        }

        // Log all meshes to check outer boundary elements
        const extras = this.getGltfExtrasFromNodeChain(mesh);
        const windowType = extras['window_type'] ?? extras['windowType'];
        const isWindowPlaceholder = extras['is_window_placeholder'] ?? extras['isWindowPlaceholder'];
        const roomType = extras['room_type'] ?? extras['roomType'] ?? extras['roomTypeRaw'];

        // Log mesh color info
        let colorInfo = null;
        if (mesh.material) {
          const material = mesh.material as any;
          if (material.diffuseColor) {
            colorInfo = {
              r: material.diffuseColor.r,
              g: material.diffuseColor.g,
              b: material.diffuseColor.b
            };
          }
        }

        // Hide all walls (including room walls)
        if (this.isWallMesh(mesh.name)) {
          mesh.setEnabled(false);
          mesh.isVisible = false;
          mesh.isPickable = false;
          wallCount++;
        }
        if (this.isWindowMesh(mesh)) {
          mesh.setEnabled(false);
          mesh.isVisible = false;
          mesh.isPickable = false;
          windowCount++;
        }

        // Make floor plates transparent and non-clickable
        const isFloorPlate = mesh.name.toUpperCase().includes('FLOOR_PLATE') || mesh.name.toUpperCase().includes('_PLATE');
        const isRoof = mesh.name.toUpperCase().includes('ROOF');
      }
    });

  }

  private hideAllWindows(scene: Scene): void {
    let hiddenCount = 0;
    scene.meshes.forEach((mesh) => {
      if (mesh.name) {
        const upperName = mesh.name.toUpperCase();

        // Hide all window-related meshes
        if (upperName.includes('WINDOW') || upperName.includes('GLASS') || upperName.includes('WINDOWPLACEHOLDER')) {
          mesh.setEnabled(false);
          mesh.isVisible = false;
          mesh.isPickable = false;
          hiddenCount++;

          // Also hide all child meshes
          mesh.getChildren().forEach((child) => {
            if (child instanceof AbstractMesh) {
              child.setEnabled(false);
              child.isVisible = false;
              child.isPickable = false;
              hiddenCount++;
            }
          });
        }
      }
    });

  }

  private applyFloorVisualStyle(scene: Scene): void {
    const config = this.visualConfig;
    const roomMaterial = new StandardMaterial('floorSliceStyleMat', scene);
    roomMaterial.diffuseColor = Color3.Black();
    roomMaterial.emissiveColor = Color3.FromHexString('#a9a9a9');
    roomMaterial.alpha = config.fillAlpha;
    roomMaterial.transparencyMode = Material.MATERIAL_ALPHABLEND;
    roomMaterial.specularColor = Color3.Black();
    roomMaterial.backFaceCulling = false;
    roomMaterial.zOffset = -1;
    roomMaterial.zOffsetUnits = -1;
    this.floorSliceSharedMaterial = roomMaterial;

    // Floor base material - medium grey, fully opaque
    const floorBaseMaterial = new StandardMaterial('floorBaseStyleMat', scene);
    floorBaseMaterial.diffuseColor = Color3.Black();
    floorBaseMaterial.emissiveColor = Color3.FromHexString('#bfbfbf');
    floorBaseMaterial.alpha = 1.0;
    floorBaseMaterial.transparencyMode = Material.MATERIAL_OPAQUE;
    floorBaseMaterial.specularColor = Color3.Black();
    floorBaseMaterial.backFaceCulling = false;
    const roomMeshes = scene.meshes.filter((mesh) => {
      const base = (mesh.name || '').split('/').pop() || '';
      if (mesh.name === '__root__' || !mesh.geometry || !base.toLowerCase().startsWith('room_')) {
        return false;
      }
      return !this.isFloorBandMesh(mesh);
    });

    // Create floor slices from room meshes (thin plates at floor level)
    if (roomMeshes.length) {
      this.createFloorSlicesFromRoomMeshes(scene, roomMeshes, roomMaterial, floorBaseMaterial, config);
    }

  }

  private createFloorSlicesFromRoomMeshes(scene: Scene, roomMeshes: AbstractMesh[], material: StandardMaterial, floorBaseMaterial: StandardMaterial, config: any): void {

    // Clear existing floor slices and layout groups
    this.floorSlices.forEach((slices) => {
      slices.forEach((slice) => {
        slice.dispose();
      });
    });
    this.floorSlices.clear();
    this.floorLayoutGroups.forEach((group) => group.dispose(true, true));
    this.floorLayoutGroups.clear();

    // Group room meshes by floor
    const floorGroups = new Map<string, AbstractMesh[]>();
    roomMeshes.forEach((mesh) => {
      const floorId = this.extractFloorIdFromRoomMesh(mesh);
      if (floorId) {
        if (!floorGroups.has(floorId)) {
          floorGroups.set(floorId, []);
        }
        floorGroups.get(floorId)!.push(mesh);
      }
    });

    // Create a thin floor slice for each floor, but hide room meshes
    floorGroups.forEach((meshes, floorId) => {
      const floorSliceMeshes: AbstractMesh[] = [];

      // Calculate floor bounds
      let minY = Number.POSITIVE_INFINITY;
      let maxY = Number.NEGATIVE_INFINITY;
      meshes.forEach((mesh) => {
        mesh.computeWorldMatrix(true);
        const bbox = mesh.getBoundingInfo().boundingBox;
        minY = Math.min(minY, bbox.minimumWorld.y);
        maxY = Math.max(maxY, bbox.maximumWorld.y);
      });

      // Compute the full floor height (slab) and keep a thin floor plate.
      const slabH = Math.max(0.05, maxY - minY);
      const plateH = Math.min(0.16, Math.max(0.06, slabH * 0.04));
      const yBottom = minY + 0.02;
      const roomVolumeH = Math.max(0.05, slabH - 0.04);
      // Create a common layout group for this floor so the floor and its assets
      // can be rotated together as one unit around the floor center.
      const floorCenter = new Vector3(0, 0, 0);
      meshes.forEach((mesh) => {
        mesh.computeWorldMatrix(true);
        floorCenter.addInPlace(mesh.getBoundingInfo().boundingBox.centerWorld);
      });
      floorCenter.scaleInPlace(1 / meshes.length);
      const floorLayoutGroup = new TransformNode(`floorLayout_${floorId}`, scene);
      floorLayoutGroup.position = floorCenter;
      this.floorLayoutGroups.set(floorId, floorLayoutGroup);

      // Create slice mesh by cloning and flattening room meshes
      meshes.forEach((mesh) => {
        if (mesh instanceof InstancedMesh) return;
        if (this.isCustomRoomFloorMesh(mesh)) {
          this.applyRoomWallHighlight(mesh);
          mesh.setEnabled(true);
          mesh.isVisible = true;
          mesh.isPickable = true;
          return;
        }
        const slice = mesh.clone(`floorSlice_${floorId}_${mesh.name}`, null, false);
        if (slice) {

          // Render the floor plate opaque, room volumes as the shared room material
          const isFloorPlate = mesh.name.toUpperCase().includes('FLOOR_PLATE') || mesh.name.toUpperCase().includes('_PLATE');
          slice.material = isFloorPlate ? floorBaseMaterial : material;
          slice.parent = null;
          slice.isPickable = false; // Make floor slices non-pickable so clicks pass through to furniture

          // Match the source mesh's world transform (rotation is applied to the common group)
          mesh.computeWorldMatrix(true);
          const worldMatrix = mesh.getWorldMatrix();
          const pos = new Vector3();
          const rot = new Quaternion();
          const sc = new Vector3();
          worldMatrix.decompose(sc, rot, pos);
          slice.position = pos;
          slice.rotationQuaternion = rot;
          slice.scaling = sc;

          // Hide original room mesh (rooms are hidden in floor view)
          const hasCustomFloorChildren = this.isCustomRoomFloorMesh(mesh) || mesh.getChildMeshes(false).some((child) => this.isCustomRoomFloorMesh(child));
          this.detachCustomRoomWallChildren(mesh);
          this.detachCustomRoomFloorChildren(mesh);
          if (!hasCustomFloorChildren) {
            mesh.setEnabled(false);
            mesh.isVisible = false;
          }

          // Scale clones down to a thin floor plate so they lie on the floor
          slice.computeWorldMatrix(true);
          let bbox = slice.getBoundingInfo().boundingBox;
          const originalHeight = bbox.maximumWorld.y - bbox.minimumWorld.y;
          const targetH = isFloorPlate ? plateH : roomVolumeH;
          if (originalHeight > 0) {
            slice.scaling.y = targetH / originalHeight;
          }

          // Snap the scaled clone's bottom to the floor base; lift room plates slightly above the global floor to avoid z-fighting
          slice.computeWorldMatrix(true);
          bbox = slice.getBoundingInfo().boundingBox;
          const currentBottom = bbox.minimumWorld.y;
          slice.position.y += yBottom - currentBottom;

          // Ensure slice is visible
          slice.setEnabled(true);
          slice.isVisible = true;
          if (config.showEdges) {
            slice.enableEdgesRendering(0.99);
            slice.edgesWidth = config.edgeWidth;
            const edgeColor = Color3.FromHexString(config.edgeColorHex);
            slice.edgesColor = new Color4(edgeColor.r, edgeColor.g, edgeColor.b, 1);
          } else {
            slice.disableEdgesRendering();
          }
          slice.computeWorldMatrix(true);

          // Parent the slice to the common floor layout group while the group is still unrotated
          slice.setParent(floorLayoutGroup, true);
          floorSliceMeshes.push(slice);
        }
      });

      // Store floor slices for this floor
      this.floorSlices.set(floorId, floorSliceMeshes);
    });

  }

  private applyFloorLayoutRotations(): void {
    if (!this.scene) {
      return;
    }

    // Parent loaded assets under their matching floor layout group while the groups are still unrotated
    this.loadedAssets.forEach((asset) => {
      if (!asset?.mesh || !asset.floor) {
        return;
      }
      const group = this.floorLayoutGroups.get(asset.floor);
      if (!group) {
        return;
      }
      asset.mesh.setParent(group, true);
    });

    // Parent live-user avatar containers to the same floor layout group so they rotate with the floor
    this.reparentLiveUserNodes();

    // Rotate each floor layout group 180° around its center to align with the building footprint
    this.floorLayoutGroups.forEach((group) => {
      group.rotationQuaternion = null;
      group.rotation = new Vector3(0, Math.PI, 0);
      group.computeWorldMatrix(true);
    });
  }

  private reparentLiveUserNodes(): void {
    if (!this.scene) {
      return;
    }

    this.scene.transformNodes.forEach((node) => {
      const match = node.name.match(/^liveUser_room_(.+)$/);
      if (!match) {
        return;
      }

      const roomId = match[1];
      const roomMeshes = this.resolveRoomMeshesByToken(roomId);
      if (!roomMeshes?.length) {
        return;
      }

      const floorId = this.extractFloorIdFromRoomMesh(roomMeshes[0]);
      if (!floorId) {
        return;
      }

      const group = this.floorLayoutGroups.get(floorId);
      if (!group || node.parent === group) {
        return;
      }

      // Parent the room's avatar container to the floor group while keeping the existing
      // world positions of the avatars. The container is offset by the negative floor center
      // so the avatar world coordinates rotate around the floor center with the group.
      node.parent = group;
      const floorCenter = group.position.clone();
      node.position = floorCenter.negate();
      node.rotationQuaternion = null;
      node.rotation = Vector3.Zero();
      node.scaling = new Vector3(1, 1, 1);
      node.computeWorldMatrix(true);
    });
  }

  private frameCameraToLoadedModel(scene: Scene): void {
    const camera = scene.activeCamera instanceof ArcRotateCamera ? scene.activeCamera : null;
    if (!camera) {
      return;
    }
    const meshes = scene.meshes.filter((mesh) =>
      mesh.name !== '__root__' &&
      !!mesh.geometry &&
      mesh.isEnabled() &&
      mesh.isVisible &&
      this.loadedSceneNodeIds.has(mesh.uniqueId)
    );
    if (!meshes.length) {
      return;
    }
    let min = new Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
    let max = new Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
    meshes.forEach((mesh) => {
      mesh.computeWorldMatrix(true);
      const boundingBox = mesh.getBoundingInfo().boundingBox;
      min = Vector3.Minimize(min, boundingBox.minimumWorld);
      max = Vector3.Maximize(max, boundingBox.maximumWorld);
    });
    const size = max.subtract(min);
    const center = min.add(size.scale(0.5));
    const maxDimension = Math.max(size.x, size.y, size.z);
    if (!Number.isFinite(maxDimension) || maxDimension <= 0) {
      return;
    }
    const aspect = this.engine ? this.engine.getRenderWidth() / this.engine.getRenderHeight() : 1;
    const verticalDistance = size.y / (2 * Math.tan(camera.fov / 2));
    const horizontalFov = 2 * Math.atan(Math.tan(camera.fov / 2) * Math.max(aspect, 1));
    const horizontalDistance = Math.max(size.x, size.z) / (2 * Math.tan(horizontalFov / 2));
    const radius = Math.max(verticalDistance, horizontalDistance, maxDimension) * 1.35;
    camera.setTarget(center);
    camera.radius = Math.max(radius, 40);
    camera.lowerRadiusLimit = Math.max(radius * 0.08, 10);
    camera.upperRadiusLimit = Math.max(radius * 6, 1200);
    this.initialCameraState = {
      alpha: camera.alpha,
      beta: camera.beta,
      radius: camera.radius,
      target: camera.target.clone(),
    };

  }

  public resetCamera(): void {
    if (!this.scene || !this.initialCameraState) {
      return;
    }
    const camera = this.scene.activeCamera instanceof ArcRotateCamera ? this.scene.activeCamera : null;
    if (!camera) {
      return;
    }
    camera.alpha = this.initialCameraState.alpha;
    camera.beta = this.initialCameraState.beta;
    camera.radius = this.initialCameraState.radius;
    camera.setTarget(this.initialCameraState.target.clone());

  }

  private enhanceLoadedModelMaterials(scene: Scene): void {
    const seenMaterials = new Set<Material>();
    scene.meshes.forEach((mesh) => {
      if (!this.loadedSceneNodeIds.has(mesh.uniqueId) || !mesh.material || seenMaterials.has(mesh.material)) {
        return;
      }
      seenMaterials.add(mesh.material);
      const material = mesh.material as any;
      if (material.getClassName?.() === 'PBRMaterial') {
        material.environmentIntensity = 1.15;
        material.directIntensity = 1.0;
        material.emissiveIntensity = material.emissiveIntensity ?? 0.0;
        material.microSurface = Math.max(material.microSurface ?? 0.85, 0.9);
        material.roughness = material.roughness != null ? Math.min(material.roughness, 0.24) : 0.18;
      } else if (material instanceof StandardMaterial) {
        material.specularColor = new Color3(0.9, 0.9, 0.9);
        material.specularPower = Math.max(material.specularPower || 64, 128);
      }
    });

  }

  private detectVerticalAxis(meshes: AbstractMesh[]): 'y' | 'z' {
    if (!meshes.length) {
      return 'y';
    }
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;
    meshes.forEach((mesh) => {
      const info = mesh.getBoundingInfo();
      const min = info.boundingBox.minimumWorld;
      const max = info.boundingBox.maximumWorld;
      minY = Math.min(minY, min.y);
      maxY = Math.max(maxY, max.y);
      minZ = Math.min(minZ, min.z);
      maxZ = Math.max(maxZ, max.z);
    });
    const ySpan = maxY - minY;
    const zSpan = maxZ - minZ;
    return zSpan > ySpan ? 'z' : 'y';

  }

  private closeInterFloorVerticalGaps(scene: Scene, roomMeshes: AbstractMesh[]): void {
    const floorMeshes = new Map<string, AbstractMesh[]>();
    roomMeshes.forEach((mesh) => {
      const floorId = this.extractFloorIdFromRoomMesh(mesh);
      if (!floorMeshes.has(floorId)) {
        floorMeshes.set(floorId, []);
      }
      floorMeshes.get(floorId)!.push(mesh);
    });
    const sortedFloorIds = Array.from(floorMeshes.keys()).sort((a, b) => this.floorSortKey(a) - this.floorSortKey(b));
    if (sortedFloorIds.length < 2) {
      return;
    }
    const maxPasses = Math.max(12, sortedFloorIds.length * 3);
    const minGap = 0.001;
    for (let pass = 0; pass < maxPasses; pass += 1) {
      let adjusted = false;
      const floorBounds = this.computeFloorBounds(floorMeshes);
      for (let i = 0; i < sortedFloorIds.length - 1; i += 1) {
        const lower = sortedFloorIds[i];
        const upper = sortedFloorIds[i + 1];
        const lowerB = floorBounds.get(lower);
        const upperB = floorBounds.get(upper);
        if (!lowerB || !upperB) {
          continue;
        }
        const gap = upperB.minY - lowerB.maxY;
        if (gap > minGap) {
          this.translateFloorSubtreeBy(scene, floorMeshes.get(upper) || [], upper, -gap);
          adjusted = true;
        }
      }
      if (!adjusted) {
        break;
      }
    }

  }

  private computeFloorBounds(floorMeshes: Map<string, AbstractMesh[]>): Map<string, { minY: number; maxY: number }> {
    const bounds = new Map<string, { minY: number; maxY: number }>();
    floorMeshes.forEach((meshes, floorId) => {
      let minY = Number.POSITIVE_INFINITY;
      let maxY = Number.NEGATIVE_INFINITY;
      meshes.forEach((mesh) => {
        mesh.computeWorldMatrix(true);
        const bb = mesh.getBoundingInfo().boundingBox;
        minY = Math.min(minY, bb.minimumWorld.y);
        maxY = Math.max(maxY, bb.maximumWorld.y);
      });
      bounds.set(floorId, { minY, maxY });
    });
    return bounds;

  }

  private translateFloorSubtreeBy(scene: Scene, roomMeshes: AbstractMesh[], floorId: string, deltaY: number): void {
    if (!deltaY) {
      return;
    }
    const roots = new Set<any>();
    roomMeshes.forEach((mesh) => {
      let node: any = mesh;
      while (node && node.parent) {
        const parentName = node.parent.name || '';
        if (parentName.startsWith('Floor_')) {
          roots.add(node.parent);
          break;
        }
        node = node.parent;
      }
    });
    const wanted = `Floor_${floorId}`;
    scene.transformNodes.forEach((node: any) => {
      const name = node?.name || '';
      if (name === wanted || name.endsWith(`/${wanted}`)) {
        roots.add(node);
      }
    });
    if (roots.size > 0) {
      roots.forEach((root: any) => {
        root.position.y += deltaY;
      });
      return;
    }
    roomMeshes.forEach((mesh) => {
      mesh.position.y += deltaY;
    });

  }

  /**

   * glTF `extras` are authored on glTF **nodes** in this export (e.g. `burjk/building.glb`: 3938 nodes with extras,

   * 0 meshes with extras). Babylon often attaches them to the transform node; the ray may hit a **child mesh**

   * with empty `metadata.gltf.extras`. Walk the picked node and parents until we find the first non-empty extras.

   */

  private getGltfExtrasFromNodeChain(mesh: AbstractMesh, maxDepth = 20): Record<string, unknown> {
    let n: any = mesh;
    for (let d = 0; n && d < maxDepth; d++) {
      const ex = n.metadata?.gltf?.extras;
      if (ex && typeof ex === 'object' && Object.keys(ex).length > 0) {
        return ex as Record<string, unknown>;
      }
      n = n.parent;
    }
    return {};

  }

  private isFloorBandMesh(mesh: AbstractMesh): boolean {
    const extras = this.getGltfExtrasFromNodeChain(mesh);
    const roomType = extras['room_type'] ?? extras['roomType'] ?? extras['roomTypeRaw'];
    return String(roomType || '').toLowerCase() === 'floor_band';

  }

  private extractFloorIdFromRoomMesh(mesh: AbstractMesh): string {
    const extras = this.getGltfExtrasFromNodeChain(mesh);
    const fid = extras['floor_id'] ?? extras['floorId'];
    if (fid !== undefined && fid !== null && String(fid).trim() !== '') {
      return String(fid).trim();
    }
    let p: any = mesh.parent;
    while (p) {
      const nm = p.name || '';
      if (nm.startsWith('Floor_')) {
        return nm.replace('Floor_', '');
      }
      p = p.parent;
    }
    const roomId = ((mesh.name || '').split('/').pop() || '').replace(/^Room_/i, '');
    if (roomId.includes('_')) {
      return roomId.split('_')[0];
    }
    if (/^\d{6}$/.test(roomId)) {
      const n = parseInt(roomId.slice(0, 3), 10);
      return Number.isNaN(n) ? '1' : String(n);
    }
    if (roomId.length > 2) {
      return roomId.slice(0, -2);
    }
    return '1';

  }

  private extractFloorNumberFromMetadata(node: TransformNode): number | undefined {
    const metadata = node.metadata as any;
    if (metadata) {
      const floorNumber = metadata.floorNumber || metadata.floor_number;
      if (floorNumber !== undefined && !isNaN(floorNumber)) {
        return Number(floorNumber);
      }

      // Try to extract from extras in metadata
      const extras = metadata.extras || {};
      const fnRaw = extras['floorNumber'] ?? extras['floor_number'];
      if (fnRaw != null && String(fnRaw).trim() !== '') {
        const num = parseInt(String(fnRaw).trim(), 10);
        if (!isNaN(num)) {
          return num;
        }
      }
    }
    return undefined;

  }

  /**

   * Get floor by elevation for accurate touch detection

   * Uses actual GLB floor positions instead of hardcoded heights

   */

  private getFloorByElevation(yPosition: number): FloorMeshGroup | null {
    let closestFloor: FloorMeshGroup | null = null;
    let minDistance = Infinity;
    this.floorMappingTable.forEach((floorData) => {
      if (floorData.bottomPosition !== undefined && floorData.topPosition !== undefined) {

        // Check if the Y position is within this floor's vertical range
        if (yPosition >= floorData.bottomPosition && yPosition <= floorData.topPosition) {
          const distance = Math.abs(yPosition - floorData.elevation!);
          if (distance < minDistance) {
            minDistance = distance;
            closestFloor = floorData;
          }
        }
      }
    });
    return closestFloor;

  }

  /**

   * Isolate and focus camera on selected floor in 3D view

   * Hides non-selected floors and positions camera to focus on the selected floor

   */

  private isolateAndFocusFloor(floorId: string): void {
    if (!this.scene || this.isEmbeddedMapOverlay()) {
      this.finishBlenderFloorPresentation();
      return;
    }
    const floorData = this.floorMappingTable.get(floorId);
    if (!floorData) {
      this.finishBlenderFloorPresentation();
      return;
    }

    // Apply floor cutaway: hide roofs, make walls transparent, keep furniture opaque
    this.applyFloorCutaway(floorId);

    // Apply dual-view layer masks: hide meshes/assets from other floors
    this.applyRoomAndSliceLayerMasks(floorId, null);

    // Show floor slices only for the selected floor (room footprints)
    const targetFloorNorm = this.normalizeBabylonFloorNumber(floorId);
    this.floorSlices.forEach((slices, fid) => {
      const sliceFloor = this.normalizeBabylonFloorNumber(fid);
      const isVisible = sliceFloor === targetFloorNorm;
      slices.forEach((slice) => {
        slice.setEnabled(isVisible);
        slice.isVisible = isVisible;
      });
    });

    // Hide all floors except the selected one
    this.floorMappingTable.forEach((data, id) => {
      if (id !== floorId && data.floorNode) {
        data.floorNode.setEnabled(false);
      }
    });

    // Show the selected floor
    if (floorData.floorNode) {
      floorData.floorNode.setEnabled(true);
    }

    // Hide assets from other floors (like viewer.js setFloorVisibility)
    this.loadedAssets.forEach((asset) => {
      const assetFloor = this.normalizeBabylonFloorNumber(asset.floor || '');
      const targetFloor = this.normalizeBabylonFloorNumber(floorId);
      const isVisible = assetFloor === targetFloor;
      if (asset.mesh) {
        asset.mesh.setEnabled(isVisible);
        asset.mesh.isVisible = isVisible;
      }
    });

    // Hide GLB floor meshes from parsedMetadata except the selected one

    // Object.entries(this.parsedMetadata.floorMeshes).forEach(([key, floorGroup]) => {

    //   const isVisible = key === floorId;

    //   floorGroup.meshNames.forEach((meshName) => {

    //     const mesh = this.scene?.getMeshByName(meshName);

    //     if (mesh) {

    //       mesh.setEnabled(isVisible);
    //       mesh.isVisible = isVisible;

    //     }
    //   });

    // });

    // Focus camera on the selected floor
    if (floorData.bounds && this.scene.activeCamera) {
      const camera = this.scene.activeCamera as ArcRotateCamera;
      const center = floorData.bounds.min.add(floorData.bounds.max).scale(0.5);
      const height = floorData.bounds.max.y - floorData.bounds.min.y;

      // Set camera target to floor center
      camera.setTarget(center);

      // Adjust camera radius based on floor size
      const floorSize = Math.max(
        floorData.bounds.max.x - floorData.bounds.min.x,
        floorData.bounds.max.z - floorData.bounds.min.z
      );
      camera.radius = Math.max(floorSize * 2, height * 3);

      // Adjust camera angle for better floor view
      camera.beta = Math.PI / 4; // 45 degrees from horizontal
      camera.alpha = -Math.PI / 4; // 45 degrees from front
    }
    this.finishBlenderFloorPresentation();
    this.refreshCustomRoomWallMeshes();
    // Re-sync live user assets so avatars/clusters are re-rendered on the newly visible floor
    this.syncLiveUserAssets();
    // Re-apply room status colors after floor isolation so colors are visible on the selected floor
    this.renderAlertAssets();
    this.renderBeaconAssets();
  }

  /**

   * Restore all floors to visibility (undo isolation)

   */

  private restoreAllFloors(): void {
    if (!this.scene) return;
    // Restore cutaway-modified meshes (roofs, walls, windows)
    this.restoreFloorCutaway();

    // Clear layer masks: show all floors
    this.applyRoomAndSliceLayerMasks(null, null);

    // Show all floor slices
    this.floorSlices.forEach((slices) => {
      slices.forEach((slice) => {
        slice.setEnabled(true);
        slice.isVisible = true;
      });
    });
    this.floorMappingTable.forEach((data) => {
      if (data.floorNode) {
        data.floorNode.setEnabled(true);
      }
    });

    // Show all assets
    this.loadedAssets.forEach((asset) => {
      if (asset.mesh) {
        asset.mesh.setEnabled(true);
        asset.mesh.isVisible = true;
      }
    });

    // Reset camera to initial state if available
    if (this.initialCameraState && this.scene.activeCamera) {
      const camera = this.scene.activeCamera as ArcRotateCamera;
      camera.alpha = this.initialCameraState.alpha;
      camera.beta = this.initialCameraState.beta;
      camera.radius = this.initialCameraState.radius;
      camera.setTarget(this.initialCameraState.target);
    }
    this.refreshCustomRoomWallMeshes();
    this.syncRoomStatusColors();

  }

  /**

   * Same as POC `roomUiFromMesh`: stack id from viewer rules; human storey from glTF extras (node chain).

   */

  private readRoomUiFromMesh(mesh: AbstractMesh): {
    floorStackId: string;
    floorNumber: string | null;
    displayName: string;
    roomId: string;
    roomType?: string;
    metadata: Record<string, unknown>;
  } {
    const extras = this.getGltfExtrasFromNodeChain(mesh);
    const metaIds = this.readGltfMeshFloorRoomIds(mesh);
    const base = (mesh.name || '').split('/').pop() || '';

    // Prioritize metadata roomId over name-based extraction
    const roomId = metaIds.roomId || (base.toLowerCase().startsWith('room_') ? base.replace(/^Room_/i, '') : base);
    const fnRaw = extras['floorNumber'] ?? extras['floor_number'];
    const floorNumber =
      fnRaw != null && String(fnRaw).trim() !== '' ? String(fnRaw).trim() : null;
    const displayName = String(extras['display_name'] ?? extras['displayName'] ?? `Room ${roomId}`);
    const roomTypeRaw = extras['property_type'] ?? extras['propertyType'] ?? extras['room_type'] ?? extras['roomType'] ?? extras['type'];

    // Normalize room type to match the asset file names used by the viewer
    let normalizedRoomType: string | undefined;
    if (roomTypeRaw != null) {
      const typeStr = String(roomTypeRaw).toLowerCase().trim();
      if (typeStr.includes('kitchen')) {
        normalizedRoomType = 'Kitchen';
      } else if (typeStr.includes('bedroom')) {
        normalizedRoomType = 'Bedroom';
      } else if (typeStr.includes('laundry') || typeStr.includes('laundary')) {
        normalizedRoomType = 'Laundry Room';
      } else if (typeStr.includes('store') || typeStr.includes('storeroom')) {
        normalizedRoomType = 'Store Room';
      } else {
        normalizedRoomType = String(roomTypeRaw);
      }
    }
    return {
      floorStackId: this.extractFloorIdFromRoomMesh(mesh),
      floorNumber,
      displayName,
      roomId,
      roomType: normalizedRoomType,
      metadata: extras,
    };

  }

  /** Walk up until a node is identified as a room by name or glTF metadata. */

  private resolveMeshForRoomPick(mesh: AbstractMesh): AbstractMesh | null {
    let m: any = mesh;
    const leaf = ((m.name || '').split('/').pop() || '');
    let hierarchy: any = mesh;
    let depth = 0;
    while (hierarchy && depth < 5) {
      hierarchy = hierarchy.parent;
      depth++;
    }

    // Handle furniture meshes - find the associated room from loadedAssets
    if (leaf.toLowerCase().startsWith('furniture_')) {
      // Traverse up the hierarchy to find the root furniture mesh stored in loadedAssets
      let current: any = mesh;
      let foundAsset = null;
      let foundKey = null;
      let furnitureDepth = 0;
      while (current && furnitureDepth < 10) {
        for (const [key, asset] of this.loadedAssets) {
          if (asset.mesh === current) {
            foundAsset = asset;
            foundKey = key;
            break;
          }
        }
        if (foundAsset) break;
        current = current.parent;
        furnitureDepth++;
      }
      if (foundAsset && foundAsset.room) {
        // Create a synthetic mesh with the room metadata for the click handler
        const syntheticMesh = mesh.clone(`room_${foundAsset.room}`, null, false);
        if (syntheticMesh) {

          // Set metadata to match room structure
          syntheticMesh.metadata = {
            gltf: {
              extras: {
                roomId: foundAsset.room,
                room_id: foundAsset.room
              }
            }
          };
          syntheticMesh.setEnabled(false); // Don't actually show it
          syntheticMesh.isVisible = false;
          return syntheticMesh;
        }
      }
      return null;
    }

    // Handle floor slice meshes - they should not be treated as rooms

    // Floor slices are for floor visualization, not room selection
    if (leaf.toLowerCase().startsWith('floorslice_')) {
      return null;
    }

    // Exclude floor infrastructure meshes - they are not individual rooms

    // But allow actual rooms that happen to have FLOOR_PLATE in their ID
    const floorInfrastructurePatterns = [
      'floorband', 'ground_entrance', 'ground_exit',
      'ground_step', 'ground_threshold', 'ground_pad', 'ground_side',
      'entrancecanopy', 'entranceframe', 'entrancedoor', 'entrancestep'
    ];

    // Special check: if mesh name is exactly like "Room_<ID>_FLOOR_PLATE_primitiveX"

    // and the ID is in our known room list, treat it as a room
    const roomMatch = leaf.match(/^Room_(\d+)_FLOOR_PLATE/i);
    if (roomMatch) {
      const roomId = roomMatch[1];
      // Check if this ID is in our parsed room metadata
      if (this.parsedMetadata.roomMeshes[roomId]) {
        return m as AbstractMesh;
      }
      return null;
    }
    for (const pattern of floorInfrastructurePatterns) {
      if (leaf.toLowerCase().includes(pattern)) {
        return null;
      }
    }

    // First, check if the clicked mesh itself is a room
    if (leaf.toLowerCase().startsWith('room_')) {
      return m as AbstractMesh;
    }

    // Check if clicked mesh has room metadata
    const meta = this.readGltfMeshFloorRoomIds(m as AbstractMesh);
    if (meta.roomId) {
      return m as AbstractMesh;
    }

    // Only walk up the parent chain if the clicked mesh itself is not a room

    // This prevents finding the wrong room in the hierarchy
    while (m) {
      const name = (m.name || '').toLowerCase();

      // Exclude floor infrastructure at any level
      for (const pattern of floorInfrastructurePatterns) {
        if (name.includes(pattern)) {
          return null;
        }
      }
      if (name.includes('room_')) {
        return m as AbstractMesh;
      }
      const ancestorMeta = this.readGltfMeshFloorRoomIds(m as AbstractMesh);
      if (ancestorMeta.roomId) {
        return m as AbstractMesh;
      }
      m = m.parent;
    }
    return null;

  }

  /** True when pick is a floor slice / Floor_* hierarchy / mesh extras.floor_id (no Room_ required). */

  private isFloorLikePick(mesh: AbstractMesh): boolean {
    let n: any = mesh;
    for (let d = 0; n && d < 14; d++) {
      const nm = n.name || '';
      if (nm.includes('Floor_')) {
        return true;
      }
      const ex = (n as any).metadata?.gltf?.extras;
      if (ex && (ex.floor_id != null || ex.floorId != null) && String(ex.floor_id ?? ex.floorId).trim() !== '') {
        return true;
      }
      n = n.parent;
    }
    return false;

  }

  private floorSortKey(fid: string): number {
    const s = String(fid ?? '').trim();
    if (!s) {
      return 0;
    }
    const u = s.toUpperCase();
    const tail = u.startsWith('F') ? u.slice(1) : u;
    const m = tail.match(/^0*(\d+)$/);
    if (m) {
      return parseInt(m[1], 10);
    }
    const n = parseInt(s, 10);
    return Number.isNaN(n) ? 0 : n;

  }

  private configureMeshPickLogging(scene: Scene): void {
    scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type !== PointerEventTypes.POINTERPICK) {
        return;
      }
      const pickedMesh = pointerInfo.pickInfo?.pickedMesh;
      if (!pickedMesh || pickedMesh.name === '__root__' || this.isLiveUserMesh(pickedMesh)) {
        return;
      }
      this.logPickedMeshInfo(pickedMesh);
    });

  }

  private logPickedMeshInfo(mesh: AbstractMesh): void {
    mesh.computeWorldMatrix(true);
    const bb = mesh.getBoundingInfo()?.boundingBox;
    const min = bb?.minimumWorld;
    const max = bb?.maximumWorld;
    const parentFloorNode = this.findParentFloorNode(mesh);
    const baseName = (mesh.name || '').split('/').pop() || '';
    const roomToken = baseName.startsWith('Room_') ? baseName.replace(/^Room_/i, '') : '';
    const floorFromName = this.extractFloorTokenFromName(mesh.name || '');

  }

  private extractFloorTokenFromName(name: string): string {
    const floorMatch = name.match(/F\d{3}/i);
    if (floorMatch) {
      return floorMatch[0].toUpperCase();
    }
    const parentFloor = this.findParentFloorNodeByName(name);
    return parentFloor ? parentFloor.replace(/^Floor_/i, '').toUpperCase() : '';

  }

  private findParentFloorNode(mesh: AbstractMesh): string {
    let node: any = mesh;
    while (node?.parent) {
      const parentName = node.parent.name || '';
      if (parentName.startsWith('Floor_')) {
        return parentName;
      }
      node = node.parent;
    }
    return '';

  }

  private findParentFloorNodeByName(name: string): string {
    const parts = String(name || '').split('/');
    const floorPart = parts.find((p) => p.startsWith('Floor_'));
    return floorPart || '';

  }

  public async renderBeaconAssets(): Promise<void> {
    if (!this.scene || this.scene.isDisposed || this.isDestroyed) {
      return;
    }
    this.clearBeaconMarkers();

    if (!this.showBeacons) {
      return;
    }

    const beaconsList = this.getBeaconsList();
    if (!beaconsList.length) {
      return;
    }

    // Try to load the beacon 3D template if available
    let beaconTemplate: AbstractMesh | null = null;
    try {
      beaconTemplate = await this.loadAssetTemplate('beacon');
    } catch {
      beaconTemplate = null;
    }

    // Group beacons by room/perimeter to arrange them nicely if multiple beacons exist in the same room
    const beaconsByRoom = new Map<string, any[]>();
    beaconsList.forEach((beacon) => {
      const roomId = String(
        beacon.perimeter?.id ||
        beacon.perimeter?.perimeterId ||
        beacon.perimeter?.arcAngelId ||
        beacon.roomId ||
        'default'
      );
      if (!beaconsByRoom.has(roomId)) {
        beaconsByRoom.set(roomId, []);
      }
      beaconsByRoom.get(roomId)!.push(beacon);
    });

    for (const [roomId, beacons] of beaconsByRoom.entries()) {
      let roomMeshes: AbstractMesh[] = [];
      if (roomId !== 'default') {
        roomMeshes = this.collectAlertHighlightAnchorMeshes(roomId);
        if (!roomMeshes.length) {
          roomMeshes = this.resolveRoomMeshesByToken(roomId);
        }
      }

      let bounds: { min: Vector3; max: Vector3 } | null = null;
      let standY = 0;
      let center = Vector3.Zero();

      if (roomMeshes.length > 0) {
        bounds = this.computeRoomWorldBounds(roomMeshes);
        if (bounds) {
          standY = this.resolveRoomStandWorldY(roomMeshes, bounds);
          center = new Vector3(
            (bounds.min.x + bounds.max.x) * 0.5,
            standY,
            (bounds.min.z + bounds.max.z) * 0.5
          );
        }
      }

      // If no room bounds found, check if scene has floor bounds
      if (!bounds) {
        const floorBoundsMap = this.getFloorBoundsMap();
        const firstFloor = floorBoundsMap.values().next().value;
        if (firstFloor) {
          standY = firstFloor.minY;
          center = new Vector3(
            (firstFloor.minX + firstFloor.maxX) * 0.5,
            standY,
            (firstFloor.minZ + firstFloor.maxZ) * 0.5
          );
          bounds = {
            min: new Vector3(firstFloor.minX, firstFloor.minY, firstFloor.minZ),
            max: new Vector3(firstFloor.maxX, firstFloor.maxY, firstFloor.maxZ)
          };
        }
      }

      const totalBeaconsInRoom = beacons.length;
      const roomRadius = bounds
        ? Math.min(Math.abs(bounds.max.x - bounds.min.x), Math.abs(bounds.max.z - bounds.min.z)) * 0.25
        : 0.8;

      beacons.forEach((beacon, index) => {
        let posX = center.x;
        let posZ = center.z;

        if (totalBeaconsInRoom > 1) {
          const angle = (2 * Math.PI * index) / totalBeaconsInRoom;
          posX += roomRadius * Math.cos(angle);
          posZ += roomRadius * Math.sin(angle);
        }

        const beaconId = String(beacon.id || beacon.name || `beacon_${index}`);

        // Calculate floating elevation so the beacon hovers high on top above user clusters and room ceilings
        const roomTopY = bounds ? bounds.max.y : (standY + 2.8);
        const floatHeight = Math.max(roomTopY - standY + 2.5, 5.2);

        // 1. Create Beacon Root Node at floor location
        const beaconRoot = new TransformNode(`beacon_node_${beaconId}_${index}`, this.scene!);
        beaconRoot.position = new Vector3(posX, standY, posZ);

        // 2. Vertical guide beam from floor to floating marker
        const stem = MeshBuilder.CreateCylinder(`beacon_stem_${beaconId}`, {
          height: floatHeight,
          diameter: 0.03,
          tessellation: 12,
        }, this.scene!);
        stem.parent = beaconRoot;
        stem.position = new Vector3(0, floatHeight / 2, 0);
        stem.isPickable = false;
        const stemMat = new StandardMaterial(`beacon_stem_mat_${beaconId}`, this.scene!);
        stemMat.diffuseColor = new Color3(0, 0.6, 1.0);
        stemMat.emissiveColor = new Color3(0, 0.4, 0.9);
        stemMat.alpha = 0.45;
        stem.material = stemMat;
        this.beaconMarkers.push(stem);

        // 3. Floating 3D Beacon Model / Device on Top
        let beaconMesh: AbstractMesh | null = null;
        if (beaconTemplate) {
          try {
            const clone = beaconTemplate.clone(`beacon_mesh_${beaconId}_${index}`, beaconRoot, false);
            if (clone) {
              this.enableHierarchy(clone);
              const scale = 1.0;
              clone.scaling = new Vector3(scale, scale, scale);
              clone.position = new Vector3(0, floatHeight, 0);
              clone.setEnabled(true);
              clone.isVisible = true;
              clone.isPickable = false;
              this.beaconMarkers.push(clone);
              beaconMesh = clone;
            }
          } catch (e) {
            console.warn('Failed to clone beacon template:', e);
          }
        } else {
          // Fallback floating 3D hardware beacon disc
          const disc = MeshBuilder.CreateCylinder(`beacon_disc_${beaconId}`, {
            diameter: 0.5,
            height: 0.15,
            tessellation: 24,
          }, this.scene!);
          disc.parent = beaconRoot;
          disc.position = new Vector3(0, floatHeight, 0);
          disc.isPickable = false;
          const discMat = new StandardMaterial(`beacon_disc_mat_${beaconId}`, this.scene!);
          discMat.diffuseColor = new Color3(0.1, 0.5, 1.0);
          discMat.emissiveColor = new Color3(0.05, 0.3, 0.8);
          disc.material = discMat;
          this.beaconMarkers.push(disc);
          beaconMesh = disc;
        }

        // Floating bobbing animation on the 3D beacon model
        if (beaconMesh) {
          const floatAnim = new Animation(
            `beacon_float_${beaconId}`,
            'position.y',
            30,
            Animation.ANIMATIONTYPE_FLOAT,
            Animation.ANIMATIONLOOPMODE_CYCLE
          );
          floatAnim.setKeys([
            { frame: 0, value: floatHeight },
            { frame: 30, value: floatHeight + 0.18 },
            { frame: 60, value: floatHeight },
          ]);
          beaconMesh.animations = [floatAnim];
          this.scene!.beginAnimation(beaconMesh, 0, 60, true);
        }

        // 4. Ground Bluetooth Pulse Ring on the floor
        const groundRing = MeshBuilder.CreateDisc(`beacon_ground_ring_${beaconId}`, {
          radius: 0.65,
          tessellation: 32,
        }, this.scene!);
        groundRing.parent = beaconRoot;
        groundRing.rotation.x = Math.PI / 2;
        groundRing.position = new Vector3(0, 0.03, 0);
        groundRing.isPickable = false;
        const ringMat = new StandardMaterial(`beacon_ring_mat_${beaconId}`, this.scene!);
        ringMat.diffuseColor = new Color3(0.1, 0.5, 1.0);
        ringMat.emissiveColor = new Color3(0.1, 0.4, 0.9);
        ringMat.alpha = 0.4;
        ringMat.backFaceCulling = false;
        groundRing.material = ringMat;

        // Subtle pulsation animation for ground ring
        const ringAnim = new Animation(
          `beacon_pulse_${beaconId}`,
          'scaling',
          30,
          Animation.ANIMATIONTYPE_VECTOR3,
          Animation.ANIMATIONLOOPMODE_CYCLE
        );
        ringAnim.setKeys([
          { frame: 0, value: new Vector3(1, 1, 1) },
          { frame: 30, value: new Vector3(1.3, 1, 1.3) },
          { frame: 60, value: new Vector3(1, 1, 1) },
        ]);
        groundRing.animations = [ringAnim];
        this.scene!.beginAnimation(groundRing, 0, 60, true);
        this.beaconMarkers.push(groundRing);

        this.beaconMarkers.push(beaconRoot);
      });
    }
  }

  public clearBeaconMarkers(): void {
    this.beaconMarkers.forEach((node) => {
      try {
        if (node instanceof AbstractMesh) {
          if (node.material) {
            if ((node.material as any).diffuseTexture) {
              (node.material as any).diffuseTexture.dispose();
            }
            if ((node.material as any).opacityTexture && (node.material as any).opacityTexture !== (node.material as any).diffuseTexture) {
              (node.material as any).opacityTexture.dispose();
            }
            node.material.dispose();
          }
          node.dispose(false, true);
        } else if (node instanceof TransformNode) {
          node.dispose(false, true);
        }
      } catch {
        // ignore
      }
    });
    this.beaconMarkers = [];
  }

  private getBeaconsList(): any[] {
    if (this.beacons && this.beacons.length > 0) {
      return this.beacons;
    }
    const beacons: any[] = [];
    const sourcePerimeters = this.floorMapEntity?.perimeters;
    if (sourcePerimeters) {
      const perimList = Array.isArray(sourcePerimeters)
        ? sourcePerimeters
        : Object.values(sourcePerimeters);

      perimList.forEach((perimeter: any) => {
        if (!perimeter || perimeter.perimeterType === 'FLOOR' || perimeter.type === 'FLOOR') {
          return;
        }
        const bSource = perimeter.beacons;
        if (!bSource) {
          return;
        }
        const bList = Array.isArray(bSource) ? bSource : Object.values(bSource);
        bList.forEach((b: any) => {
          if (!b) return;
          const actualBeacon = (typeof b === 'object' && (b.id || b.name || b.label))
            ? b
            : (Object.values(b)[0] as any) || b;
          if (actualBeacon && typeof actualBeacon === 'object') {
            beacons.push({
              ...actualBeacon,
              perimeter,
            });
          }
        });
      });
    }
    return beacons;
  }

  private buildRoomMeshIndex(scene: Scene): void {
    this.roomMeshIndex.clear();
    scene.meshes.forEach((mesh) => {
      if (!mesh.geometry || mesh.name === '__root__' || this.isFloorBandMesh(mesh)) {
        return;
      }
      const base = (mesh.name || '').split('/').pop() || '';
      if (!base.startsWith('Room_')) {
        return;
      }
      const tokens = new Set(this.extractMeshIdTokens(base));
      const metaIds = this.readGltfMeshFloorRoomIds(mesh);
      if (metaIds.roomId) {
        this.extractMeshIdTokens(`Room_${metaIds.roomId}`).forEach((token) => tokens.add(token));
        const normalizedRoomId = this.normalizeEntityId(metaIds.roomId);
        if (normalizedRoomId) {
          tokens.add(normalizedRoomId);
        }
      }
      metaIds.roomPerimeterIds?.forEach((perimeterId) => {
        const normalized = this.normalizeEntityId(perimeterId);
        if (normalized) {
          tokens.add(normalized);
        }
      });
      tokens.forEach((token) => {
        const list = this.roomMeshIndex.get(token) || [];
        if (!list.includes(mesh)) {
          list.push(mesh);
        }
        this.roomMeshIndex.set(token, list);
      });
    });

  }

  private resolveRoomMeshesByToken(token: string): AbstractMesh[] {
    const normalized = this.normalizeEntityId(token);
    if (!normalized) {
      return [];
    }

    const fromIndex = this.roomMeshIndex.get(normalized);
    if (fromIndex?.length) {
      return fromIndex;
    }

    const fromMetadata: AbstractMesh[] = [];
    for (const group of Object.values(this.parsedMetadata.roomMeshes || {})) {
      const groupToken = this.normalizeEntityId(group.roomId);
      const perimeterTokens = (group.perimeterIds || [])
        .map((id) => this.normalizeEntityId(id))
        .filter((id): id is string => !!id);
      const matchesDirect =
        groupToken === normalized ||
        perimeterTokens.includes(normalized) ||
        perimeterTokens.some(
          (candidate) =>
            candidate.endsWith(normalized) || normalized.endsWith(candidate)
        );
      if (!matchesDirect) {
        continue;
      }
      group.meshNames.forEach((meshName) => {
        const mesh = this.scene?.getMeshByName(meshName);
        if (mesh && !fromMetadata.includes(mesh)) {
          fromMetadata.push(mesh);
        }
      });
    }
    if (fromMetadata.length) {
      return fromMetadata;
    }

    for (const [key, list] of this.roomMeshIndex.entries()) {
      if (key === normalized || key.endsWith(normalized) || normalized.endsWith(key)) {
        return list;
      }
    }

    return [];
  }

  /** Map API / mesh room ids to one canonical token used for clusters and mesh lookup. */
  private resolveCanonicalRoomToken(rawRoomId: string): string {
    const normalized = this.normalizeEntityId(rawRoomId);
    if (!normalized) {
      return String(rawRoomId ?? '').trim();
    }

    if (this.resolveRoomMeshesByToken(normalized).length) {
      return normalized;
    }

    for (const group of Object.values(this.parsedMetadata.roomMeshes || {})) {
      const groupToken = this.normalizeEntityId(group.roomId);
      if (groupToken === normalized || this.roomTokenMatchesGroup(normalized, group)) {
        return groupToken || normalized;
      }
      for (const meshName of group.meshNames) {
        const base = (meshName || '').split('/').pop() || '';
        const tokens = this.extractMeshIdTokens(base);
        if (tokens.includes(normalized)) {
          return groupToken || normalized;
        }
      }
    }

    for (const key of this.roomMeshIndex.keys()) {
      if (key === normalized || key.endsWith(normalized) || normalized.endsWith(key)) {
        return key;
      }
    }

    return normalized;
  }

  private buildEnrichedRoomMeshIndex(occupancies: RoomOccupancy[]): Map<string, AbstractMesh[]> {
    const enriched = new Map<string, AbstractMesh[]>(this.roomMeshIndex);
    for (const occ of occupancies) {
      if (enriched.has(occ.roomId) && enriched.get(occ.roomId)!.length > 0) {
        continue;
      }
      const meshes = this.resolveRoomMeshesByToken(occ.roomId);
      if (meshes.length) {
        enriched.set(occ.roomId, meshes);
      }
    }
    return enriched;
  }

  private logMeshDiagnostics(): void {
    const ids = Array.from(this.roomMeshIndex.keys());

    // Floor duplication detection
    if (this.scene && !this.scene.isDisposed) {
      let originalRoomMeshes = 0;
      let originalRoomVisible = 0;
      let originalRoomEnabled = 0;
      let floorSliceMeshes = 0;
      let floorSliceVisible = 0;
      let floorSliceEnabled = 0;
      const duplicateFloorPairs: string[] = [];
      for (const mesh of this.scene.meshes) {
        if (!mesh.name || mesh.name === '__root__' || !mesh.geometry) continue;
        const isOriginal = mesh.name.startsWith('Room_') && !mesh.name.startsWith('floorSlice_');
        const isSlice = mesh.name.startsWith('floorSlice_');
        if (isOriginal) {
          originalRoomMeshes++;
          if (mesh.isVisible) originalRoomVisible++;
          if (mesh.isEnabled()) originalRoomEnabled++;
        }
        if (isSlice) {
          floorSliceMeshes++;
          if (mesh.isVisible) floorSliceVisible++;
          if (mesh.isEnabled()) floorSliceEnabled++;
        }

        // Detect if both original and its clone are visible
        if (isOriginal && mesh.isVisible && mesh.isEnabled()) {
          const floorId = this.extractFloorIdFromRoomMesh(mesh);
          const sliceName = `floorSlice_${floorId}_${mesh.name}`;
          const slice = this.scene.getMeshByName(sliceName);
          if (slice && slice.isVisible && slice.isEnabled()) {
            duplicateFloorPairs.push(mesh.name);
          }
        }
      }
    }

  }

  private extractMeshIdTokens(baseName: string): string[] {
    const tokens = new Set<string>();
    const roomRaw = baseName.replace(/^Room_/i, '');
    const roomNorm = this.normalizeEntityId(roomRaw);
    if (roomNorm) {
      tokens.add(roomNorm);
    }
    const matches = roomRaw.match(/\d{5,}/g) || [];
    matches.forEach((m) => {
      tokens.add(this.normalizeEntityId(m));
      if (m.length === 9) {
        tokens.add(this.normalizeEntityId(m.slice(0, 8)));
      }
    });
    return Array.from(tokens).filter(Boolean);

  }

  private pickBestRoomMeshCandidate(candidates: AbstractMesh[]): AbstractMesh | null {
    if (!candidates.length) {
      return null;
    }
    if (candidates.length === 1) {
      return candidates[0];
    }
    return candidates.reduce((best, mesh) => {
      const bestVolume = this.meshWorldVolume(best);
      const meshVolume = this.meshWorldVolume(mesh);
      return meshVolume < bestVolume ? mesh : best;
    });

  }

  private meshWorldVolume(mesh: AbstractMesh): number {
    const bb = mesh.getBoundingInfo()?.boundingBox;
    if (!bb) {
      return Number.POSITIVE_INFINITY;
    }
    const size = bb.maximumWorld.subtract(bb.minimumWorld);
    return Math.max(0.000001, size.x * size.y * size.z);

  }

  private normalizeEntityId(raw?: string): string {
    if (!raw) {
      return '';
    }
    const value = String(raw).trim().toUpperCase();
    if (!value) {
      return '';
    }
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      return value;
    }
    return String(parseInt(digits, 10));

  }

  public highlightBabylonFloor(floorId: string): void {
    if (!ThreeIntegrationService.FLOOR_HIGHLIGHT_ENABLED) {
      return;
    }
    if (!this.scene || !floorId) {
      return;
    }
    const rawRef = String(floorId).trim();
    const canonicalKey = this.resolveCanonicalFloorKeyForHighlight(rawRef);
    if (!canonicalKey) {
      return;
    }
    if (this.highlightedFloorId && this.highlightedFloorId !== canonicalKey) {
      this.clearBabylonFloorHighlight(this.highlightedFloorId);
    }
    const floorGroup = this.parsedMetadata.floorMeshes[canonicalKey];
    if (!floorGroup?.meshNames?.length) {
      return;
    }
    const floorOwnerId = String(floorGroup.floorId ?? canonicalKey).trim();
    const meshesToHighlight: AbstractMesh[] = [];
    const pushMesh = (meshName: string): void => {
      const mesh = this.scene!.getMeshByName(meshName);
      if (mesh?.material && !meshesToHighlight.includes(mesh)) {
        meshesToHighlight.push(mesh);
      }
    };
    floorGroup.meshNames.forEach(pushMesh);
    Object.values(this.parsedMetadata.roomMeshes).forEach((roomGroup) => {
      const rf = String(roomGroup.floorId ?? '').trim();
      if (rf !== floorOwnerId && rf !== canonicalKey) {
        return;
      }
      roomGroup.meshNames.forEach(pushMesh);
    });
    if (meshesToHighlight.length === 0) {
      return;
    }
    const HIGHLIGHT_COLOR = new Color3(1, 0.6, 0);
    const EDGE_COLOR = new Color4(1, 0.6, 0, 1);
    const EMISSIVE_COLOR = new Color3(0.3, 0.15, 0);
    meshesToHighlight.forEach((mesh) => {
      this.applyBabylonFloorHighlight(mesh, canonicalKey, HIGHLIGHT_COLOR, EMISSIVE_COLOR, EDGE_COLOR);
    });
    this.highlightedFloorId = canonicalKey;
    this.floorHighlighted.emit({ floorId: canonicalKey, highlighted: true });

  }

  private applyBabylonFloorHighlight(
    mesh: AbstractMesh,
    floorNumber: string,
    highlightColor = new Color3(1, 0.6, 0),
    emissiveColor = new Color3(0.3, 0.15, 0),
    edgeColor = new Color4(1, 0.6, 0, 1)
  ): void {
    if (!this.scene || !mesh.material) {
      return;
    }
    if (!this.floorMaterialMap.has(mesh)) {
      this.floorMaterialMap.set(mesh, mesh.material);
    }
    this.highlightedFloorMeshes.add(mesh);
    if (mesh instanceof InstancedMesh) {
      mesh.renderOverlay = true;
      mesh.overlayColor = highlightColor;
      return;
    }
    const highlightMaterial = new StandardMaterial(`highlight_${floorNumber}_${mesh.name}`, this.scene);
    highlightMaterial.diffuseColor = highlightColor;
    highlightMaterial.emissiveColor = emissiveColor;
    highlightMaterial.alpha = 0.9;
    highlightMaterial.specularColor = Color3.Black();
    mesh.material = highlightMaterial;
    mesh.enableEdgesRendering(0.99);
    mesh.edgesWidth = 4.0;
    mesh.edgesColor = edgeColor;

  }

  private restoreHighlightedBabylonMeshes(): void {
    const edgeColor = Color3.FromHexString(this.visualConfig.edgeColorHex);
    this.highlightedFloorMeshes.forEach((mesh) => {
      if (mesh instanceof InstancedMesh) {
        mesh.renderOverlay = false;
      } else {
        const currentMaterial = mesh.material;
        const hasOriginalMaterial = this.floorMaterialMap.has(mesh);
        const originalMaterial = hasOriginalMaterial ? this.floorMaterialMap.get(mesh) ?? null : null;
        if (hasOriginalMaterial) {
          mesh.material = originalMaterial;
        }
        if (currentMaterial?.name?.startsWith('highlight_') && currentMaterial !== originalMaterial) {
          currentMaterial.dispose();
        }
      }
      if (this.visualConfig.showEdges) {
        mesh.enableEdgesRendering(0.99);
        mesh.edgesWidth = this.visualConfig.edgeWidth;
        mesh.edgesColor = new Color4(edgeColor.r, edgeColor.g, edgeColor.b, 1);
      } else {
        mesh.disableEdgesRendering();
      }
    });
    this.highlightedFloorMeshes.clear();
    this.floorMaterialMap.clear();

  }

  public clearBabylonFloorHighlight(floorId?: string): void {
    if (!this.scene) {
      return;
    }
    const raw = floorId?.trim();
    if (!raw) {
      if (!this.highlightedFloorId && this.highlightedFloorMeshes.size === 0) {
        return;
      }
      this.restoreHighlightedBabylonMeshes();
      this.highlightedFloorId = null;
      return;
    }
    const hi = this.highlightedFloorId;
    const clearCanon = this.resolveCanonicalFloorKeyForHighlight(raw);
    const clearFallback = clearCanon ?? raw;
    const hiCanon = hi ? this.resolveCanonicalFloorKeyForHighlight(hi) ?? hi : null;
    const targetsSameFloor =
      !!hi &&
      (hi === clearFallback ||
        clearCanon === hi ||
        (hiCanon !== null && (clearCanon === hiCanon || clearFallback === hiCanon)));
    if (!targetsSameFloor) {
      return;
    }
    this.restoreHighlightedBabylonMeshes();
    this.highlightedFloorId = null;

  }

  public highlightBabylonAlertFloors(floorIds: string[]): void {
    if (!this.scene) {
      return;
    }
    const camera = this.scene.activeCamera instanceof ArcRotateCamera ? this.scene.activeCamera : null;
    this.babylonVisualizeService.highlightByFloorIds(
      this.scene,
      camera,
      floorIds || [],
      this.parsedMetadata.floorMeshes,
      this.parsedMetadata.roomMeshes,
      (rawFloorId: string) => String(this.floorSortKey(rawFloorId))
    );

  }

  private syncAlertRoomHighlights(roomIds: string[]): void {
    if (!this.scene || this.scene.isDisposed) {
      return;
    }

    const normalizedTargets = new Set(
      (roomIds || [])
        .map(id => this.normalizeEntityId(id))
        .filter(id => !!id)
    );

    const nextAlertMeshes = new Set<AbstractMesh>();

    // Find meshes belonging to currently alerted rooms
    Object.values(this.parsedMetadata.roomMeshes || {}).forEach(group => {
      const roomToken = this.normalizeEntityId(group.roomId);
      const perimeterTokens = (group.perimeterIds || [])
        .map(id => this.normalizeEntityId(id))
        .filter(id => !!id);
      const matched =
        normalizedTargets.has(roomToken) ||
        perimeterTokens.some(token => normalizedTargets.has(token));
      if (!matched) {
        return;
      }
      group.meshNames.forEach(meshName => {
        const mesh = this.scene!.getMeshByName(meshName);
        if (mesh) {
          nextAlertMeshes.add(mesh);
        }
      });
    });

    // -----------------------------------------
    // 1. REMOVE alert tint from old alert rooms
    // -----------------------------------------
    Array.from(this.alertHighlightedMeshes).forEach(mesh => {
      if (nextAlertMeshes.has(mesh)) {
        return;
      }
      if (mesh instanceof InstancedMesh) {
        mesh.renderOverlay = false;
      } else {
        const currentMaterial = mesh.material;
        const originalMaterial =
          this.alertRoomMaterialMap.get(mesh) ?? null;
        mesh.material = originalMaterial;
        if (
          currentMaterial?.name?.startsWith('alert_room_') &&
          currentMaterial !== originalMaterial
        ) {
          currentMaterial.dispose();
        }
      }
      this.alertHighlightedMeshes.delete(mesh);
      this.alertRoomMaterialMap.delete(mesh);
    });

    // -----------------------------------------
    // 2. APPLY alert tint to current alert rooms
    // -----------------------------------------
    nextAlertMeshes.forEach(mesh => {
      if (!this.alertHighlightedMeshes.has(mesh)) {
        // Save whatever material exists BEFORE alert tint
        this.alertRoomMaterialMap.set(mesh, mesh.material);
        this.alertHighlightedMeshes.add(mesh);
      }
      if (mesh instanceof InstancedMesh) {
        mesh.renderOverlay = true;
        mesh.overlayColor = new Color3(1, 0, 0);
        return;
      }

      // Already has our alert material
      if (mesh.material?.name?.startsWith('alert_room_')) {
        return;
      }
      const alertMaterial = new StandardMaterial(
        `alert_room_${mesh.uniqueId}`,
        this.scene!
      );
      alertMaterial.diffuseColor = new Color3(1, 0, 0);
      alertMaterial.emissiveColor = new Color3(0.8, 0, 0);
      alertMaterial.alpha = 0.8;
      alertMaterial.specularColor = Color3.Black();
      alertMaterial.backFaceCulling = false;
      alertMaterial.zOffset = -2;
      alertMaterial.zOffsetUnits = -2;
      mesh.material = alertMaterial;
      mesh.enableEdgesRendering(0.99);
      mesh.edgesWidth = 10;
      mesh.edgesColor = new Color4(1, 1, 1, 1);
    });

  }
  public highlightBabylonRooms(roomIds: string[], userRoomTokens?: Set<string>): void {
    if (!this.scene) {
      return;
    }

    // this.restoreHighlightedBabylonRooms(userRoomTokens);
    this.restoreHighlightedBabylonRooms();
    if (!roomIds?.length) {
      return;
    }
    const normalizedTargets = new Set(
      roomIds
        .map((id) => this.normalizeEntityId(id))
        .filter((id) => !!id)
    );
    if (!normalizedTargets.size) {
      return;
    }
    const targetMeshes: AbstractMesh[] = [];
    Object.values(this.parsedMetadata.roomMeshes).forEach((group) => {
      const roomToken = this.normalizeEntityId(group.roomId);
      const perimeterTokens = (group.perimeterIds || [])
        .map((id) => this.normalizeEntityId(id))
        .filter((id) => !!id);
      const matched =
        normalizedTargets.has(roomToken) ||
        perimeterTokens.some((token) => normalizedTargets.has(token));
      if (!matched) {
        return;
      }
      group.meshNames.forEach((meshName) => {
        const mesh = this.scene!.getMeshByName(meshName);
        if (mesh && mesh.material && !targetMeshes.includes(mesh)) {
          targetMeshes.push(mesh);
        }
        const sliceMesh = this.scene!.getMeshByName(`floorSlice_${group.floorId}_${meshName}`);
        if (sliceMesh && !targetMeshes.includes(sliceMesh)) {
          targetMeshes.push(sliceMesh);
        }
      });
    });
    targetMeshes.forEach((mesh) => {
      this.applyBabylonRoomHighlight(mesh);
    });

  }

  private applyBabylonRoomHighlight(mesh: AbstractMesh): void {
    if (!this.scene) {
      return;
    }
    if (!this.roomMaterialMap.has(mesh)) {
      this.roomMaterialMap.set(mesh, mesh.material);
    }
    this.highlightedRoomMeshes.add(mesh);
    if (mesh instanceof InstancedMesh) {

      // Skip if overlay already set to red
      if (mesh.renderOverlay && mesh.overlayColor && mesh.overlayColor.equals(new Color3(1, 0, 0))) {
        return;
      }
      mesh.renderOverlay = true;
      mesh.overlayColor = new Color3(1, 0, 0); // Red
      return;
    }

    // Skip if mesh already has a highlight_room_ material — avoid recreate/dispose cycle
    const currentMat = mesh.material as StandardMaterial | null;
    if (currentMat?.name?.startsWith('highlight_room_')) {
      return;
    }
    const highlightMaterial = new StandardMaterial(`highlight_room_${mesh.name}`, this.scene);
    highlightMaterial.diffuseColor = new Color3(1, 0.78, 0.48);   // soft orange fill
    highlightMaterial.emissiveColor = new Color3(0.9, 0.55, 0.25); // warm ember glow
    highlightMaterial.alpha = 0.5;
    highlightMaterial.specularColor = Color3.Black();
    highlightMaterial.backFaceCulling = false;
    highlightMaterial.zOffset = -2;
    highlightMaterial.zOffsetUnits = -2;
    mesh.material = highlightMaterial;
    mesh.enableEdgesRendering(0.99);
    mesh.edgesWidth = 10.0; // Thick edges
    mesh.edgesColor = new Color4(1, 1, 1, 1); // White edges for contrast

  }

  private createRoomBoundingBoxHighlight(roomBoundaryMeshes: AbstractMesh[]): void {
    if (!this.scene || roomBoundaryMeshes.length === 0) {
      return;
    }

    // Remove previous highlight box if exists
    // if (this.roomHighlightBox) {
    //   this.roomHighlightBox.dispose();
    //   this.roomHighlightBox = null;
    // }

    // Calculate combined bounding box from all room boundary meshes
    const bounds = roomBoundaryMeshes
      .filter((mesh) => mesh.getBoundingInfo())
      .map((mesh) => mesh.getBoundingInfo().boundingBox);
    if (bounds.length === 0) {
      return;
    }
    const min = bounds.reduce((acc, box) => Vector3.Minimize(acc, box.minimumWorld), bounds[0].minimumWorld.clone());
    const max = bounds.reduce((acc, box) => Vector3.Maximize(acc, box.maximumWorld), bounds[0].maximumWorld.clone());
    const size = max.subtract(min);
    const center = min.add(size.scale(0.5));

    // Create wireframe box
    const box = MeshBuilder.CreateBox('room_highlight_box', {
      width: size.x,
      height: size.y,
      depth: size.z
    }, this.scene);
    box.position = center;
    box.isPickable = false;

    // Create translucent cyan material
    const highlightMaterial = new StandardMaterial('room_box_highlight', this.scene);
    highlightMaterial.diffuseColor = new Color3(1, 0.2, 0.2);
    highlightMaterial.emissiveColor = new Color3(0.5, 0, 0);
    highlightMaterial.alpha = 0.45;
    highlightMaterial.backFaceCulling = false;
    highlightMaterial.zOffset = -2;
    highlightMaterial.zOffsetUnits = -2;
    highlightMaterial.specularColor = Color3.Black();
    highlightMaterial.wireframe = false; // Solid mode
    box.material = highlightMaterial;
    this.roomHighlightBoxes.push(box);

  }

  private restoreHighlightedBabylonRooms(): void {
    // Dispose of the bounding box highlight
    if (this.roomHighlightBox) {
      this.roomHighlightBox.dispose();
      this.roomHighlightBox = null;
    }
    this.highlightedRoomMeshes.forEach((mesh) => {
      if (mesh instanceof InstancedMesh) {
        mesh.renderOverlay = false;
      } else {
        const originalMaterial = this.roomMaterialMap.get(mesh) ?? null;
        const currentMaterial = mesh.material;
        if (originalMaterial) {
          mesh.material = originalMaterial;
        }
        if (currentMaterial?.name?.startsWith('highlight_room_') && currentMaterial !== originalMaterial) {
          currentMaterial.dispose();
        }
      }
      if (this.visualConfig.showEdges) {
        const edgeColor = Color3.FromHexString(this.visualConfig.edgeColorHex);
        mesh.enableEdgesRendering(0.99);
        mesh.edgesWidth = this.visualConfig.edgeWidth;
        mesh.edgesColor = new Color4(edgeColor.r, edgeColor.g, edgeColor.b, 1);
      } else {
        mesh.disableEdgesRendering();
      }
    });
    this.highlightedRoomMeshes.clear();
    this.roomMaterialMap.clear();
  }

  private normalizeBabylonFloorNumber(floorId: string): string {
    const numericMatch = String(floorId || '').match(/\d+/);
    return numericMatch ? String(Number(numericMatch[0])) : '';

  }

  private looksLikeOpaqueEntityId(id: string): boolean {
    const s = String(id ?? '').trim();
    if (s.length >= 22) {
      return true;
    }
    return /^[0-9a-f]{8}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f]{12}$/i.test(s);

  }

  private pickFloorRoomFromMetadataObject(obj: any): {
    floorId?: string;
    roomId?: string;

    /** Human / building floor index from GLB extras (e.g. floorNumber: "3") — use for UI list, not perimeter id digits */
    displayFloorLevel?: number;
  } {
    if (!obj || typeof obj !== 'object') {
      return {};
    }
    const isRoomExtras =
      !!(obj.roomId ?? obj.room_id ?? obj.original_id) ||
      String(obj.room_type || '').toLowerCase() === 'room';
    const floorRaw =
      obj.floorId ??
      obj.floor_id ??
      obj.FloorId ??
      obj.FLOOR_ID;
    const roomRaw =
      obj.roomId ??
      obj.room_id ??
      obj.original_id ??
      (isRoomExtras ? obj.perimeterId ?? obj.perimeter_id : undefined) ??
      obj.RoomId ??
      obj.ROOM_ID;
    const floorNumRaw = obj.floorNumber ?? obj.floor_number ?? obj.FloorNumber;
    let displayFloorLevel: number | undefined;
    if (floorNumRaw !== undefined && floorNumRaw !== null && String(floorNumRaw).trim() !== '') {
      const n = Number(String(floorNumRaw).trim());
      if (Number.isFinite(n) && n >= -200 && n <= 500) {
        displayFloorLevel = n;
      }
    }
    const floorId =
      floorRaw !== undefined && floorRaw !== null && String(floorRaw).trim() !== ''
        ? String(floorRaw).trim()
        : undefined;
    const roomId =
      roomRaw !== undefined && roomRaw !== null && String(roomRaw).trim() !== ''
        ? String(roomRaw).trim()
        : undefined;
    return { floorId, roomId, displayFloorLevel };

  }

  /** Room-level perimeter ids from glTF extras (for resolving API ids → floor via roomMeshes). */

  private collectRoomPerimeterIdsFromExtrasBundle(obj: any, into: Set<string>): void {
    if (!obj || typeof obj !== 'object') {
      return;
    }
    const isRoomExtras =
      !!(obj.roomId ?? obj.room_id ?? obj.original_id) ||
      String(obj.room_type || '').toLowerCase() === 'room';
    if (!isRoomExtras) {
      return;
    }
    const p = obj.perimeterId ?? obj.perimeter_id;
    if (p !== undefined && p !== null && String(p).trim() !== '') {
      into.add(String(p).trim());
    }

  }

  /** Reads room / perimeter / display hints from glTF extras along ancestor chain (not stack floorId — use {@link extractFloorIdFromRoomMesh}). */

  private readGltfMeshFloorRoomIds(mesh: AbstractMesh): {
    roomId?: string;
    roomPerimeterIds?: string[];

    /** From extras `floorNumber` — human storey for UI list */
    displayFloorLevel?: number;
  } {
    const acc: {
      roomId?: string;
      roomPerimeterIds?: string[];
      displayFloorLevel?: number;
    } = {};
    const perimeterAcc = new Set<string>();
    let node: any = mesh;
    let depth = 0;
    while (node && depth < 14) {
      const meta = node.metadata;
      if (meta && typeof meta === 'object') {

        /** Prefer glTF `extras` over root `metadata` — Babylon may duplicate stale keys on `meta`. */
        const bundles: object[] = [];
        if (meta.gltf?.extras && typeof meta.gltf.extras === 'object') {
          bundles.push(meta.gltf.extras);
        }
        if (meta.extras && typeof meta.extras === 'object') {
          bundles.push(meta.extras);
        }
        bundles.push(meta);
        for (const bundle of bundles) {
          const { roomId, displayFloorLevel } = this.pickFloorRoomFromMetadataObject(bundle);
          if (roomId && !acc.roomId) {
            acc.roomId = roomId;
          }
          if (displayFloorLevel !== undefined && acc.displayFloorLevel === undefined) {
            acc.displayFloorLevel = displayFloorLevel;
          }
          this.collectRoomPerimeterIdsFromExtrasBundle(bundle, perimeterAcc);
        }
      }
      node = node.parent;
      depth += 1;
    }
    if (perimeterAcc.size) {
      acc.roomPerimeterIds = Array.from(perimeterAcc);
    }
    return acc;

  }

  /**

   * Single canonical key into {@link parsedMetadata.floorMeshes} — no substring / loose numeric overlap.

   * Uses GLB-parse indices only (floor mesh keys, room → floor.parent_id chain, optional numeric match when unique).

   */

  private resolveCanonicalFloorKeyForHighlight(ref: string, depth = 0): string | null {
    const raw = String(ref ?? '').trim();
    if (!raw || depth > 8) {
      return null;
    }
    const fm = this.parsedMetadata.floorMeshes;
    if (fm[raw]) {
      return raw;
    }
    for (const [key, grp] of Object.entries(fm)) {
      const gid = String(grp.floorId ?? '').trim();
      if (gid === raw || String(key).trim() === raw) {
        return fm[key] ? key : null;
      }
    }
    const opaque = this.looksLikeOpaqueEntityId(raw);
    for (const [rk, rg] of Object.entries(this.parsedMetadata.roomMeshes)) {
      const tokens = new Set<string>([rk, ...(rg.perimeterIds || [])]);
      let matchedRoom = false;
      for (const t of tokens) {
        const tt = String(t).trim();
        if (!tt) {
          continue;
        }
        if (tt === raw) {
          matchedRoom = true;
          break;
        }
        if (!opaque) {
          const nr = this.normalizeEntityId(raw);
          const nt = this.normalizeEntityId(tt);
          if (nr && nt && nr === nt) {
            matchedRoom = true;
            break;
          }
        }
      }
      if (matchedRoom && rg.floorId) {
        return this.resolveCanonicalFloorKeyForHighlight(String(rg.floorId).trim(), depth + 1);
      }
    }
    if (!opaque) {
      const numStr = this.normalizeBabylonFloorNumber(raw);
      if (numStr) {
        const candidates = new Set<string>();
        for (const key of Object.keys(fm)) {
          if (this.normalizeBabylonFloorNumber(key) === numStr) {
            candidates.add(key);
          }
          const gid = String(fm[key].floorId ?? '').trim();
          if (gid && this.normalizeBabylonFloorNumber(gid) === numStr) {
            candidates.add(key);
          }
        }
        if (candidates.size === 1) {
          return [...candidates][0];
        }
      }
      const sk = this.floorSortKey(raw);
      if (sk !== 0) {
        const skHits = Object.keys(fm).filter((k) => this.floorSortKey(k) === sk);
        if (skHits.length === 1) {
          return skHits[0];
        }
      }
    }
    return null;

  }

  private getFloorClickNumericHint(
    floorKey: string,
    opts?: { extrasFloorNumber?: string | null; displayFloorLevelMerge?: number }
  ): number | undefined {
    const ex = opts?.extrasFloorNumber;
    if (ex != null && String(ex).trim() !== '') {
      const n = Number(String(ex).trim());
      if (Number.isFinite(n) && n >= -200 && n <= 500) {
        return n;
      }
    }
    if (opts?.displayFloorLevelMerge !== undefined && Number.isFinite(opts.displayFloorLevelMerge)) {
      return opts.displayFloorLevelMerge;
    }
    const fk = String(floorKey ?? '').trim();
    if (!fk) {
      return undefined;
    }
    if (this.looksLikeOpaqueEntityId(fk)) {
      return undefined;
    }

    /** Perimeter-style numeric ids (e.g. "6631") must not drive list level via floorSortKey → wrong floor */
    if (/^\d+$/.test(fk) && fk.length >= 4) {
      const asNum = Number(fk);
      if (asNum > 200) {
        return undefined;
      }
    }
    const sk = this.floorSortKey(fk);
    if (sk !== 0) {
      return sk;
    }
    const n = this.normalizeBabylonFloorNumber(fk);
    return n ? Number(n) : undefined;

  }

  // Add debug method to log floor hierarchy

  public logFloorHierarchy(): void {

    // Log all transform nodes that might be floors
    const floorNodes = this.scene?.transformNodes.filter(node =>
      node.name?.includes('Floor') || node.name?.includes('FLOOR')
    );
    if (floorNodes && floorNodes.length > 0) {
    }

    // Log all meshes that might be floors
    const floorMeshes = this.scene?.meshes.filter(mesh =>
      mesh.name?.includes('Floor') ||
      mesh.name?.includes('FLOOR') ||
      (mesh.parent?.name?.includes('Floor'))
    );
    if (floorMeshes && floorMeshes.length > 0) {
    }

  }

  // Update the setupFloorClickHandler method to call the Three.js service

  private setupFloorClickHandler(scene: Scene): void {
    if (this.isRoomClickObserverSetup) {
      return;
    }
    this.isRoomClickObserverSetup = true;
    scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type !== PointerEventTypes.POINTERDOWN) {
        return;
      }
      const pickResult = pointerInfo.pickInfo;
      if (!pickResult || !pickResult.hit || !pickResult.pickedMesh) {
        return;
      }
      const pickedMesh = pickResult.pickedMesh;
      if (this.isLiveUserMesh(pickedMesh)) {
        const userId = this.extractUserIdFromMesh(pickedMesh);
        const roomId = this.extractRoomIdFromMesh(pickedMesh);
        this.userClicked.emit({ userId, roomId, mesh: pickedMesh });
        return;
      }
      const roomNode = this.resolveMeshForRoomPick(pickedMesh);
      const isFloorLike = this.isFloorLikePick(pickedMesh);
      const targetMesh =
        roomNode ?? (isFloorLike ? pickedMesh : null);
      if (!targetMesh) {
        return;
      }

      // Check if this is a room click (not floor click)
      const roomUi = this.readRoomUiFromMesh(targetMesh);
      const metaIds = this.readGltfMeshFloorRoomIds(targetMesh);
      const knownRoomIds = new Set(Object.keys(this.parsedMetadata.roomMeshes || {}));
      const roomId = metaIds.roomId || this.extractRoomId(targetMesh.name, knownRoomIds);
      if (roomId && roomNode) {

        // This is a room click - emit roomClicked event
        const floorId = roomUi.floorStackId || '';
        const roomCoords = this.getRoomCoordinates(roomNode);

        // Log all rooms on this floor with coordinates
        this.logAllRoomsWithCoordinates(floorId);
        requestAnimationFrame(() => {
          this.roomClicked.emit({
            roomId,
            floorId,
            mesh: pickedMesh,
            roomName: roomUi.displayName,
            roomType: roomUi.roomType,
            metadata: roomUi.metadata,
          });
        });
        return; // Don't process as floor click
      }

      // Use actual floor elevation for accurate detection
      const pickedPoint = pickResult.pickedPoint;
      let modelFloorId: string | null = null;
      if (pickedPoint) {

        // Get floor by actual Y position from GLB geometry
        const floorByElevation = this.getFloorByElevation(pickedPoint.y);
        if (floorByElevation) {
          modelFloorId = floorByElevation.floorId;
        }
      }

      // Fallback to mesh-based detection if elevation detection fails
      if (!modelFloorId) {
        modelFloorId = roomUi.floorStackId;
      }
      if (modelFloorId) {
        const roomUi = this.readRoomUiFromMesh(targetMesh);
        const metaIds = this.readGltfMeshFloorRoomIds(targetMesh);
        const floorNumberHint = this.getFloorClickNumericHint(modelFloorId, {
          extrasFloorNumber: roomUi.floorNumber,
          displayFloorLevelMerge: metaIds.displayFloorLevel,
        });
        const floorId = modelFloorId;
        requestAnimationFrame(() => {
          this.threeIntegrationService.onBabylonFloorClick(floorId, pickedMesh, floorNumberHint);
          const payload: { floorId: string; mesh: AbstractMesh; floorNumber?: number } = {
            floorId,
            mesh: pickedMesh,
          };
          if (floorNumberHint !== undefined) {
            payload.floorNumber = floorNumberHint;
          }
          this.floorClicked.emit(payload);
          this.highlightBabylonFloor(floorId);
        });
      }
    });

  }

  private getRoomCoordinates(mesh: AbstractMesh): { center: { x: number; y: number; z: number }; bounds: { min: any; max: any } } | null {
    if (!mesh.getBoundingInfo()) return null;
    const bi = mesh.getBoundingInfo();
    const center = bi.boundingBox.centerWorld;
    const min = bi.boundingBox.minimumWorld;
    const max = bi.boundingBox.maximumWorld;
    return {
      center: { x: center.x, y: center.y, z: center.z },
      bounds: { min: { x: min.x, y: min.y, z: min.z }, max: { x: max.x, y: max.y, z: max.z } },
    };

  }

  private logAllRoomsWithCoordinates(floorId: string): void {
    if (!this.parsedMetadata || !this.parsedMetadata.roomMeshes) {
      return;
    }
    const allRooms = Object.entries(this.parsedMetadata.roomMeshes)
      .filter(([_, room]: [string, any]) => room.floorId === floorId || !room.floorId)
      .map(([roomId, room]: [string, any]) => {

        // Find mesh for this room to get coordinates
        const roomMesh = this.scene?.meshes.find((m) => {
          const meta = this.readGltfMeshFloorRoomIds(m);
          return meta.roomId === roomId || m.name === roomId || room.meshNames.includes(m.name);
        });
        return {
          roomId,
          meshNames: room.meshNames || [],
          floorId: room.floorId,
          coordinates: roomMesh ? this.getRoomCoordinates(roomMesh) : null,
        };
      });
  }

  private isLiveUserMesh(mesh: AbstractMesh): boolean {
    let node: any = mesh;
    while (node) {
      const name = node.name || '';
      if (name.startsWith('liveUser') || name.startsWith('cluster')) {
        return true;
      }
      node = node.parent;
    }
    return false;

  }

  private extractUserIdFromMesh(mesh: AbstractMesh): string {
    let node: any = mesh;
    while (node) {
      const name = node.name || '';
      if (name.startsWith('liveUser_')) {
        const parts = name.split('_');
        if (parts.length >= 3) {
          const roomId = parts[1];
          const index = parseInt(parts[2], 10);

          // Find the user record from liveUserData for this room and index
          const normalizedRoomId = this.normalizeEntityId(roomId);
          const roomUsers = this.latestLiveUserData?.byRoom?.[roomId] || this.latestLiveUserData?.byRoom?.[normalizedRoomId] || [];
          if (roomUsers[index]) {
            return roomUsers[index].userId || '';
          }
        }
      }
      node = node.parent;
    }
    return '';

  }

  private extractRoomIdFromMesh(mesh: AbstractMesh): string {
    let node: any = mesh;
    while (node) {
      const name = node.name || '';
      if (name.startsWith('cluster_label_')) {
        return name.replace('cluster_label_', '');
      }
      if (name.startsWith('cluster_')) {
        return name.replace('cluster_', '');
      }
      if (name.startsWith('liveUser_room_')) {
        return name.replace('liveUser_room_', '');
      }
      if (name.startsWith('liveUser_')) {
        const parts = name.split('_');
        if (parts.length >= 2) {
          return parts[1];
        }
      }
      node = node.parent;
    }
    return '';

  }

  private getFloorIdFromMesh(mesh: AbstractMesh): string | null {
    const roomNode = this.resolveMeshForRoomPick(mesh);
    const target = roomNode ?? (this.isFloorLikePick(mesh) ? mesh : null);
    if (!target) {
      return null;
    }
    const stack = this.extractFloorIdFromRoomMesh(target);
    return stack === '' ? null : stack;

  }

  // ==================== Placeholder-to-Asset Mapping ====================
  /**
    * Resolve an asset ID to a full GLB URL.
    * - Known library assets (human, beacon, tv, etc.) use fixed paths.
    * - Simple names (lamp, bed, chair, etc.) resolve to objects/<id>/model.glb.
    * - Paths containing '/' are used directly (e.g. 'room/office', 'object/bed').
    */
  private resolveAssetUrl(assetId: string): string {
    const libraryMap: Record<string, string> = {
      'tv': '/assets/3d/electronics/tv.glb',
      'female1': '/assets/3d/humans/female1.glb',
      'female2': '/assets/3d/humans/female2.glb',
      'female3': '/assets/3d/humans/female3.glb',
      'male1': '/assets/3d/humans/male1.glb',
      'male2': '/assets/3d/humans/male2.glb',
      'male3': '/assets/3d/humans/male3.glb',
      'male4': '/assets/3d/humans/male4.glb',
      'male5': '/assets/3d/humans/male5.glb',
      'male6': '/assets/3d/humans/male6.glb',
      'human': '/assets/3d/humans/male1.glb',
      'beacon': '/assets/3d/beacon.glb',
      'window': '/assets/3d/object/window.glb',
      'school_rooftop': '/assets/3d/object/school_rooftop.glb',
    };
    if (libraryMap[assetId]) return libraryMap[assetId];
    const lowerAssetId = assetId.toLowerCase();
    const libraryMatch = Object.keys(libraryMap).find((k) => k.toLowerCase() === lowerAssetId);
    if (libraryMatch) return libraryMap[libraryMatch];
    if (assetId.includes('/')) return `${this.assetLibraryPath}${assetId}.glb`;

    // Normalize the asset ID to match our folder names (case-insensitive, spaces preserved)
    const canonicalId = this.normalizeObjectAssetId(assetId);
    return `/assets/objects/${canonicalId}/model.glb`;
  }

  /**
   * Normalize an asset ID (from selected_asset metadata) to a folder name under objects/.
   * Tries exact match first, then case-insensitive / underscore match.
   */
  private normalizeObjectAssetId(assetId: string): string {
    if (!assetId) return assetId;
    const objectIdToFolder: Record<string, string> = {
      'air_purifier': 'air_purifier',
      'bed': 'bed',
      'bookshelf': 'Bookshelf',
      'cabinet': 'cabinet',
      'chair': 'chair',
      'counter': 'counter',
      'desk': 'desk',
      'fixture': 'fixture',
      'glass_table': 'glass_table',
      'lamp': 'lamp',
      'marble_table': 'marble_table',
      'marble_tea_table': 'marble_tea_table',
      'meeting_table': 'meeting_table',
      'modern_purple_glass_table': 'modern_purple_glass_table',
      'nightstand': 'nightstand',
      'planter': 'planter',
      'rug': 'rug',
      'sofa': 'sofa',
      'spiral_cabinet': 'Spiral Cabinet',
      'storage_rack': 'storage_rack',
      'tripod_lamp': 'Tripod Lamp',
      'wardrobe': 'wardrobe',
      'wood_lamp': 'Wood Lamp',
      'wood_sofa': 'Wood Sofa',
    };
    const lower = assetId.toLowerCase().replace(/\s+/g, ' ').trim();
    if (objectIdToFolder[lower]) return objectIdToFolder[lower];
    if (objectIdToFolder[lower.replace(/_/g, ' ')]) return objectIdToFolder[lower.replace(/_/g, ' ')];
    return assetId;
  }

  /**

   * Load an asset template for measurement and instantiation

   */

  private async loadAssetTemplate(assetId: string): Promise<AbstractMesh | null> {
    if (this.assetTemplates.has(assetId)) {
      return this.assetTemplates.get(assetId) || null;
    }
    if (!this.scene || this.scene.isDisposed) {
      return null;
    }

    // const url = assetId === 'window' ? this.windowAssetPath : `${this.assetLibraryPath}${assetId}.glb`;
    const url = this.resolveAssetUrl(assetId);
    try {
      const mesh = await this.importGlbFromUrl(url, assetId);
      if (mesh) return mesh;

      // const { root, file } = this.splitGlbUrl(url);

      // const result = await SceneLoader.ImportMeshAsync('', root, file, this.scene);

      // if (result.meshes.length === 0) {
      //   return null;

      // }

      // const rootMesh = result.meshes[0];

      // rootMesh.name = `asset-template-${assetId}`;
      // // Hide the template mesh and all children - it's only for measurement

      // rootMesh.setEnabled(false);
      // rootMesh.isVisible = false;

      // rootMesh.isPickable = false;

      // result.meshes.forEach((m) => {

      //   m.setEnabled(false);
      //   m.isVisible = false;

      //   m.isPickable = false;

      // });

      // this.assetTemplates.set(assetId, rootMesh);
      // return rootMesh;
    } catch (error) {
    }

    // Fallback for objects/ catalog: some folders used <id>.glb instead of model.glb
    if (!assetId.includes('/') && !this.assetLibraryPathIncludes(assetId)) {
      const fallbackUrl = `/assets/objects/${assetId}/${assetId}.glb`;
      try {
        const mesh = await this.importGlbFromUrl(fallbackUrl, assetId);
        if (mesh) return mesh;
      } catch (error) {
      }
    }
    return null;
  }

  private assetLibraryPathIncludes(assetId: string): boolean {
    return ['tv', 'beacon', 'window', 'school_rooftop', 'human'].includes(assetId) ||
      ['female', 'male'].some((p) => assetId.startsWith(p));
  }

  private async importGlbFromUrl(url: string, assetId: string): Promise<AbstractMesh | null> {
    if (!this.scene || this.scene.isDisposed) return null;
    const { root, file } = this.splitGlbUrl(url);
    const result = await SceneLoader.ImportMeshAsync('', root, file, this.scene);
    if (result.meshes.length === 0) return null;
    const rootMesh = result.meshes[0];
    rootMesh.name = `asset-template-${assetId}`;
    // Hide the template mesh and all children - it's only for measurement
    rootMesh.setEnabled(false);
    rootMesh.isVisible = false;
    rootMesh.isPickable = false;
    result.meshes.forEach((m) => {
      m.setEnabled(false);
      m.isVisible = false;
      m.isPickable = false;
    });
    this.assetTemplates.set(assetId, rootMesh);
    return rootMesh;
  }

  /**

   * Enable visibility on every node (clone can inherit disabled template state)

   */

  private enableHierarchy(root: any): void {
    if (!root) return;
    const stack = [root];
    while (stack.length) {
      const n = stack.pop();
      if (n.setEnabled) n.setEnabled(true);
      if (typeof n.isVisible === 'boolean') n.isVisible = true;
      if (typeof n.isPickable === 'boolean') n.isPickable = true; // Make furniture pickable
      const kids = n.getChildren ? n.getChildren() : [];
      for (const c of kids) stack.push(c);
    }

  }

  /**

   * Create an instance of an asset at a specific location

   */

  private async instantiateAsset(assetId: string, instanceId: string): Promise<AbstractMesh | null> {
    const template = await this.loadAssetTemplate(assetId);
    if (!template) {
      return null;
    }
    if (!this.scene) {
      return null;
    }

    // Deep clone the root node (doNotCloneChildren = false for deep clone)
    const instance = template.clone(`${instanceId}_${assetId}`, null, false);
    if (instance) {

      // Enable visibility on entire hierarchy
      this.enableHierarchy(instance);
    } else {
    }
    return instance;

  }

  /**

   * Get world bounds of a node hierarchy

   */

  private getNodeWorldBounds(root: AbstractMesh | TransformNode): { min: Vector3; max: Vector3; size: Vector3; center: Vector3 } | null {
    if (!root) return null;
    const meshes = root.getChildMeshes ? root.getChildMeshes(false) : [];
    const validMeshes = meshes.filter((mesh) => mesh?.geometry) as AbstractMesh[];
    if (!validMeshes.length && root instanceof AbstractMesh && root.geometry) {
      validMeshes.push(root);
    }
    if (!validMeshes.length) return null;
    validMeshes.forEach((mesh) => mesh.computeWorldMatrix(true));
    const bounds = Mesh.MinMax(validMeshes);
    return {
      min: bounds.min.clone(),
      max: bounds.max.clone(),
      size: bounds.max.subtract(bounds.min),
      center: bounds.min.add(bounds.max).scale(0.5),
    };

  }
  /**
   * Get the XZ footprint of a room by its roomId.
   * Detects the actual room floor/boundary by picking the largest, flattest mesh
   * in the room group. This avoids walls, furniture placeholders, and props
   * that would otherwise expand the bounds beyond the room.
   */
  private getRoomFootprint(roomId: string): { minX: number; maxX: number; minZ: number; maxZ: number } | null {
    if (!this.scene || this.scene.isDisposed || !roomId) return null;
    const roomGroup = this.parsedMetadata?.roomMeshes?.[roomId];
    if (!roomGroup || !roomGroup.meshNames.length) return null;
    let bestBounds: { minX: number; maxX: number; minZ: number; maxZ: number } | null = null;
    let bestArea = -1;
    let found = false;
    for (const meshName of roomGroup.meshNames) {
      const mesh = this.scene.getMeshByName(meshName);
      if (!mesh || !mesh.geometry) continue;
      mesh.computeWorldMatrix(true);
      const bbox = mesh.getBoundingInfo().boundingBox;
      const sizeY = Math.abs(bbox.maximumWorld.y - bbox.minimumWorld.y);
      const sizeX = Math.abs(bbox.maximumWorld.x - bbox.minimumWorld.x);
      const sizeZ = Math.abs(bbox.maximumWorld.z - bbox.minimumWorld.z);
      const areaXZ = sizeX * sizeZ;

      // Pick the biggest flat mesh as the room floor. If the room boundary is explicitly
      // named Room_, prefer it. Otherwise, use the largest flat mesh that is likely the floor.
      const base = meshName.split('/').pop() || '';
      const isExplicitRoom = base.toLowerCase().startsWith('room_');
      const isFlat = sizeY < 0.5 && areaXZ > 1;
      const score = isExplicitRoom ? areaXZ * 1000 : areaXZ;
      if (isExplicitRoom || isFlat) {
        if (score > bestArea) {
          bestArea = score;
          bestBounds = {
            minX: bbox.minimumWorld.x,
            maxX: bbox.maximumWorld.x,
            minZ: bbox.minimumWorld.z,
            maxZ: bbox.maximumWorld.z,
          };
          found = true;
        }
      }
    }
    if (!found) return null;
    return bestBounds;
  }

  private getPlaceholderFitBounds(mesh: AbstractMesh): { min: Vector3; max: Vector3; size: Vector3; center: Vector3; bottomY: number } {
    mesh.computeWorldMatrix(true);
    const bounds = mesh.getBoundingInfo().boundingBox;
    const localSize = bounds.maximum.subtract(bounds.minimum);
    const scale = mesh.absoluteScaling;
    const size = new Vector3(
      Math.max(Math.abs(localSize.x * scale.x), 0.001),
      Math.max(Math.abs(localSize.y * scale.y), 0.001),
      Math.max(Math.abs(localSize.z * scale.z), 0.001)
    );
    const center = bounds.centerWorld.clone();
    const bottomY = bounds.minimumWorld.y;
    const min = center.subtract(size.scale(0.5));
    const max = center.add(size.scale(0.5));
    return { min, max, size, center, bottomY };

  }

  /**

   * Detect if a mesh is a furniture placeholder

   */

  private isFurniturePlaceholder(mesh: AbstractMesh): boolean {
    const metadata = mesh.metadata as any;
    const name = mesh.name || '';
    return this.extractFurnitureType(mesh) !== null;

  }

  /**

   * Detect if a mesh is a window placeholder

   */

  private isWindowPlaceholder(mesh: AbstractMesh): boolean {
    const baseName = (mesh.name || '').split('/').pop() || '';
    const metadataRaw = mesh.metadata as any;
    const metadata = metadataRaw?.gltf?.extras || metadataRaw || {};
    if (metadata.is_window_placeholder || metadata.window_type === 'window') {
      return true;
    }
    if (baseName.startsWith('WindowPlaceholder_')) {
      return true;
    }
    return false;

  }

  /**

   * Detect if a mesh is a rooftop placeholder

   */

  private isRooftopPlaceholder(mesh: AbstractMesh): boolean {
    const metadata = mesh.metadata as any;
    const name = mesh.name || '';

    // Check metadata flags
    if (metadata?.is_rooftop_placeholder || metadata?.rooftop_type === 'school_rooftop') {
      return true;
    }

    // Check naming pattern
    if (name.startsWith('RooftopPlaceholder_')) {
      return true;
    }
    return false;

  }

  /**

   * Extract furniture type from mesh metadata or name

   */

  private extractFurnitureType(mesh: AbstractMesh): string | null {
    const metadata = mesh.metadata as any;
    const name = mesh.name || '';
    const baseName = (name || '').split('/').pop() || '';
    const metadataExtras = metadata?.gltf?.extras || metadata || {};
    const knownTypes = Object.keys(this.furnitureAssetMapping).sort((a, b) => b.length - a.length);
    const normalizeFurnitureType = (value: string): string | null => {
      const normalized = value.replace(/^Furniture_/i, '').replace(/^FURNITURE_/i, '').replace(/_\d+$/, '');
      const normalizedLower = normalized.toLowerCase();

      // const roomTypes = knownTypes.filter((type) => type === 'placeholder' || type.startsWith('placeholder_'));

      // return roomTypes.find((type) => normalizedLower === type.toLowerCase() || normalizedLower.startsWith(`${type.toLowerCase()}_`)) || null;
      return knownTypes.find((type) => normalizedLower === type.toLowerCase() || normalizedLower.startsWith(`${type.toLowerCase()}_`)) || null;
    };
    const fromMetadata = metadataExtras.furniture_type;
    if (fromMetadata) {
      // If selected_asset is present, return the furniture_type as-is —
      // it may be a size variant like "placeholder_xxl" that doesn't match
      // furnitureAssetMapping, but we'll use selected_asset for the GLB.
      if (metadataExtras.selected_asset) {
        return String(fromMetadata);
      }
      return normalizeFurnitureType(String(fromMetadata));
    }
    // Check is_placeholder_furniture flag
    if (metadataExtras.is_placeholder_furniture === true) {
      // Try to extract type from the mesh name
      if (baseName.toLowerCase().startsWith('furniture_')) {
        return normalizeFurnitureType(baseName) || baseName.replace(/^Furniture_/i, '').replace(/^FURNITURE_/i, '');
      }
    }
    if (!baseName.toLowerCase().startsWith('furniture_')) return null;
    return normalizeFurnitureType(baseName);

  }

  /**
   * Replace a furniture placeholder with the actual asset.
   * Uses metadata fields (selected_asset, placement_position, placement_rotation_degrees,
   * max_width, max_depth) when available for precise placement.
   * Falls back to placeholder bounds-based scaling for room-scale placeholders.
   */

  private async replaceFurniturePlaceholder(placeholderMesh: AbstractMesh): Promise<void> {
    const furnitureType = this.extractFurnitureType(placeholderMesh);
    if (!furnitureType) return;
    const metadataRaw = placeholderMesh.metadata as any;
    const metadata = metadataRaw?.gltf?.extras || metadataRaw || {};

    // Determine which asset to load:
    // - If selected_asset is in metadata, use it directly (e.g. "lamp", "bed", "sofa")
    // - Otherwise use the furnitureAssetMapping
    let assetId: string | null = null;
    if (metadata.selected_asset) {
      assetId = String(metadata.selected_asset).replace(/\.glb$/i, '').trim();
    } else {
      const mapping = this.furnitureAssetMapping[furnitureType];
      if (mapping) {
        assetId = mapping.assetId;
      }
    }
    if (!assetId) {
      return;
    }

    // Room GLBs are disabled; keep the existing room geometry and do not instantiate a duplicate room model.
    if (assetId.toLowerCase().startsWith('room/')) {
      return;
    }
    // Room-scale placeholders are the ones that should fill the entire room volume.
    // Size categories like placeholder_xxl/xl/l/m/s are individual furniture markers.
    // const roomPlaceholderTypes = [
    //   'placeholder',
    //   'placeholder_lab',
    //   'placeholder_common_room',
    //   'placeholder_storage',
    //   'placeholder_storeroom',
    //   'placeholder_office',
    //   'placeholder_bedroom',
    //   'placeholder_kitchen',
    //   'placeholder_laundry',
    //   'placeholder_vr_gallery',
    // ];
    // const isRoomPlaceholder = roomPlaceholderTypes.includes(furnitureType);
    const isRoomPlaceholder = false;
    placeholderMesh.computeWorldMatrix(true);
    const placeholderBounds = placeholderMesh.getBoundingInfo().boundingBox;
    const placeholderMin = placeholderBounds.minimumWorld.clone();
    const placeholderMax = placeholderBounds.maximumWorld.clone();
    const placeholderSize = placeholderMax.subtract(placeholderMin);
    const placeholderCenter = placeholderMin.add(placeholderMax).scale(0.5);
    const placeholderBottomY = placeholderMin.y;
    const placeholderTopY = placeholderMax.y;

    // Load and instantiate asset

    // const assetRoot = await this.instantiateAsset(mapping.assetId, `furniture_${placeholderMesh.uniqueId}`);

    // if (!assetRoot) {

    //   return;

    // }
    const instanceId = `furniture_${placeholderMesh.uniqueId}`;
    const assetRoot = await this.instantiateAsset(assetId, instanceId);
    if (!assetRoot) {
      return;
    }

    // Reset asset transform
    assetRoot.parent = null;
    assetRoot.position = Vector3.Zero();
    assetRoot.rotationQuaternion = null;
    assetRoot.rotation = Vector3.Zero();
    assetRoot.scaling = Vector3.One();
    assetRoot.computeWorldMatrix(true);

    // Get initial asset bounds
    const initialBounds = this.getNodeWorldBounds(assetRoot);
    if (!initialBounds) {
      assetRoot.dispose();
      return;
    }
    const safeAssetSize = new Vector3(
      Math.max(initialBounds.size.x, 0.001),
      Math.max(initialBounds.size.y, 0.001),
      Math.max(initialBounds.size.z, 0.001)
    );

    // Scaling: use max_width/max_depth from metadata if available, otherwise placeholder bounds
    const targetWidth = metadata.max_width ? Number(metadata.max_width) : placeholderSize.x;
    const targetDepth = metadata.max_depth ? Number(metadata.max_depth) : placeholderSize.z;
    if (isRoomPlaceholder) {
      // Room-scale placeholders: non-uniform scaling to fill the entire room volume
      assetRoot.scaling = new Vector3(
        placeholderSize.x / safeAssetSize.x,
        placeholderSize.y / safeAssetSize.y,
        placeholderSize.z / safeAssetSize.z
      );
    } else {
      // Individual furniture: scale uniformly to fit within the footprint (max_width x max_depth).
      // Preserve natural 3D height — do not flatten to the small placeholder height.
      const scaleX = targetWidth / safeAssetSize.x;
      const scaleZ = targetDepth / safeAssetSize.z;
      const uniformScale = Math.min(scaleX, scaleZ);
      assetRoot.scaling = new Vector3(uniformScale, uniformScale, uniformScale);
    }

    // assetRoot.rotation = placeholderMesh.absoluteRotationQuaternion

    //   ? placeholderMesh.absoluteRotationQuaternion.toEulerAngles()

    //   : placeholderMesh.rotation.clone();

    // assetRoot.rotation.x += Math.PI;
    // Rotation: use placement_rotation_degrees from metadata if available.
    // Add a 180° Y rotation so all individual furniture assets inside rooms face the
    // opposite direction (matching the requested room orientation).
    const rotationDeg = metadata.placement_rotation_degrees != null
      ? Number(metadata.placement_rotation_degrees)
      : null;
    if (rotationDeg != null) {
      const rotRad = (rotationDeg * Math.PI) / 180;
      assetRoot.rotation = isRoomPlaceholder
        ? new Vector3(Math.PI, rotRad, 0)
        : new Vector3(0, rotRad + Math.PI, 0);
    } else {
      assetRoot.rotation = placeholderMesh.absoluteRotationQuaternion
        ? placeholderMesh.absoluteRotationQuaternion.toEulerAngles()
        : placeholderMesh.rotation.clone();
      if (isRoomPlaceholder) {
        assetRoot.rotation.x += Math.PI;
      } else {
        assetRoot.rotation.y += Math.PI;
      }
    }
    assetRoot.computeWorldMatrix(true);

    // Center the asset after scaling
    const fittedBounds = this.getNodeWorldBounds(assetRoot);
    if (!fittedBounds) {
      assetRoot.dispose();
      return;
    }
    const fittedCenter = fittedBounds.center;
    assetRoot.position.x += placeholderCenter.x - fittedCenter.x;
    assetRoot.position.z += placeholderCenter.z - fittedCenter.z;

    // assetRoot.position.y += placeholderBottomY - fittedBounds.min.y;
    assetRoot.position.y += placeholderTopY - fittedBounds.min.y;
    assetRoot.computeWorldMatrix(true);

    // Hide placeholder
    placeholderMesh.setEnabled(false);
    placeholderMesh.isVisible = false;
    placeholderMesh.isPickable = false; // Make placeholder non-pickable
    assetRoot.setEnabled(true);

    // Store loaded asset info
    const floorId = metadata.floor_id ? String(metadata.floor_id) : (this.getFloorIdFromMesh(placeholderMesh) || 'unknown');
    const roomId = metadata.room_id ? String(metadata.room_id) : null;
    this.loadedAssets.set(instanceId, {
      id: instanceId,
      mesh: assetRoot,
      anchor: null,
      asset: assetId,
      category: 'furniture',
      floor: floorId,
      room: roomId,
    });
  }

  /**

   * Replace a window placeholder with tiled window assets

   */

  private async replaceWindowPlaceholder(placeholderMesh: AbstractMesh): Promise<void> {
    const metadataRaw = placeholderMesh.metadata as any;
    const metadata = metadataRaw?.gltf?.extras || metadataRaw || {};
    const name = placeholderMesh.name || '';
    placeholderMesh.computeWorldMatrix(true);
    const placeholderBounds = placeholderMesh.getBoundingInfo().boundingBox;
    const placeholderMin = placeholderBounds.minimumWorld.clone();
    const placeholderMax = placeholderBounds.maximumWorld.clone();
    const placeholderSize = placeholderMax.subtract(placeholderMin);
    const placeholderCenter = placeholderMin.add(placeholderMax).scale(0.5);

    // Parse metadata for dimensions
    const parseMeta = (key: string): number | null => {
      const v = metadata[key];
      if (v == null) return null;
      const n = parseFloat(v);
      return Number.isNaN(n) ? null : n;
    };
    const targetWidth = parseMeta('window_width') || placeholderSize.x;
    const targetHeight = parseMeta('window_height') || placeholderSize.y;
    const targetDepth = parseMeta('window_depth') || placeholderSize.z;

    // Load window asset template for measurement
    const templateRoot = await this.loadAssetTemplate('window');
    if (!templateRoot) {
      return;
    }

    // Clone template for measurement
    const measureRoot = templateRoot.clone('window-measure', null, false);
    if (!measureRoot) {
      return;
    }
    const enableHierarchy = (node: any) => {
      if (!node) return;
      if (node.setEnabled) node.setEnabled(true);
      if (typeof node.isVisible === 'boolean') node.isVisible = true;
      const kids = node.getChildren ? node.getChildren() : [];
      for (const c of kids) enableHierarchy(c);
    };
    enableHierarchy(measureRoot);
    measureRoot.parent = null;
    measureRoot.position = Vector3.Zero();
    measureRoot.rotationQuaternion = null;
    measureRoot.rotation = Vector3.Zero();
    measureRoot.scaling = Vector3.One();
    measureRoot.computeWorldMatrix(true);
    const initialBounds = this.getNodeWorldBounds(measureRoot);
    measureRoot.dispose();
    if (!initialBounds) {
      return;
    }
    const nativeWidth = Math.max(initialBounds.size.x, 0.001);
    const nativeHeight = Math.max(initialBounds.size.y, 0.001);
    const nativeDepth = Math.max(initialBounds.size.z, 0.001);

    // Scale to the placeholder height; tile horizontally to preserve aspect ratio
    const yScale = targetHeight / nativeHeight;
    const tileCount = Math.max(1, Math.round(targetWidth / (nativeWidth * yScale)));
    const tileWidth = targetWidth / tileCount;
    const gap = Math.max(0.10, tileWidth * 0.15); // 15% spacing, at least 10 cm
    const modelWidth = Math.max(0.01, tileWidth - gap);
    const xScale = modelWidth / nativeWidth;
    const zScale = targetDepth / nativeDepth;

    // Get the right vector from placeholder's world matrix for horizontal distribution
    const right = Vector3.TransformNormal(new Vector3(1, 0, 0), placeholderMesh.getWorldMatrix()).normalize();
    const floorId = this.getFloorIdFromMesh(placeholderMesh) || 'unknown';
    let replaced = 0;
    for (let i = 0; i < tileCount; i++) {
      const instanceId = `window_${placeholderMesh.uniqueId}_${i}`;
      const tileRoot = await this.instantiateAsset('window', instanceId);
      if (!tileRoot) continue;

      // Reset transform
      tileRoot.parent = null;
      tileRoot.position = Vector3.Zero();
      tileRoot.rotationQuaternion = null;
      tileRoot.rotation = placeholderMesh.absoluteRotationQuaternion
        ? placeholderMesh.absoluteRotationQuaternion.toEulerAngles()
        : placeholderMesh.rotation.clone();
      tileRoot.scaling = new Vector3(xScale, yScale, zScale);
      tileRoot.computeWorldMatrix(true);
      const fittedBounds = this.getNodeWorldBounds(tileRoot);
      if (!fittedBounds) {
        tileRoot.dispose();
        continue;
      }

      // Calculate position with horizontal distribution
      const offset = (i - (tileCount - 1) / 2) * tileWidth;
      const targetPos = placeholderCenter.add(right.scale(offset));
      tileRoot.position.x += targetPos.x - fittedBounds.center.x;
      tileRoot.position.y += targetPos.y - fittedBounds.center.y;
      tileRoot.position.z += targetPos.z - fittedBounds.center.z;
      tileRoot.computeWorldMatrix(true);
      tileRoot.setEnabled(true);
      this.loadedAssets.set(instanceId, {
        id: instanceId,
        mesh: tileRoot,
        anchor: null,
        asset: 'window',
        category: 'window',
        floor: floorId,
        room: null,
      });
      replaced++;
    }

    // Hide placeholder
    placeholderMesh.setEnabled(false);
    placeholderMesh.isVisible = false;

  }

  /**

   * Replace a rooftop placeholder with the rooftop asset

   */

  private async replaceRooftopPlaceholder(placeholderMesh: AbstractMesh): Promise<void> {
    const metadata = placeholderMesh.metadata as any;
    const name = placeholderMesh.name || '';
    const placeholderFit = this.getPlaceholderFitBounds(placeholderMesh);
    const placeholderSize = placeholderFit.size;
    const placeholderCenter = placeholderFit.center;

    // Parse metadata for dimensions
    const parseMeta = (key: string): number | null => {
      const v = metadata[key];
      if (v == null) return null;
      const n = parseFloat(v);
      return Number.isNaN(n) ? null : n;
    };
    const targetWidth = parseMeta('rooftop_width') || placeholderSize.x;
    const targetDepth = parseMeta('rooftop_depth') || placeholderSize.y; // Note: reference uses Y for depth
    const targetHeight = parseMeta('rooftop_height') || placeholderSize.z; // Note: reference uses Z for height

    // Load and instantiate asset
    const instanceId = `rooftop_${placeholderMesh.uniqueId}`;
    const assetRoot = await this.instantiateAsset('school_rooftop', instanceId);
    if (!assetRoot) {
      return;
    }

    // Reset asset transform
    assetRoot.parent = null;
    assetRoot.position = Vector3.Zero();
    assetRoot.rotationQuaternion = null;
    assetRoot.rotation = Vector3.Zero();
    assetRoot.scaling = Vector3.One();
    assetRoot.computeWorldMatrix(true);

    // Get initial asset bounds
    const initialBounds = this.getNodeWorldBounds(assetRoot);
    if (!initialBounds) {
      assetRoot.dispose();
      return;
    }
    const safeAssetSize = new Vector3(
      Math.max(initialBounds.size.x, 0.001),
      Math.max(initialBounds.size.y, 0.001),
      Math.max(initialBounds.size.z, 0.001)
    );

    // Scale the rooftop model to the footprint while preserving vertical proportions
    const xScale = targetWidth / safeAssetSize.x;
    const zScale = targetDepth / safeAssetSize.z;
    const yScale = Math.min(xScale, zScale);
    assetRoot.scaling = new Vector3(xScale, yScale, zScale);

    // Apply rotation from placeholder plus Math.PI offset (asset is exported upside-down)
    const baseRotation = placeholderMesh.absoluteRotationQuaternion
      ? placeholderMesh.absoluteRotationQuaternion.toEulerAngles()
      : placeholderMesh.rotation.clone();
    assetRoot.rotation = new Vector3(baseRotation.x + Math.PI, baseRotation.y, baseRotation.z);
    assetRoot.computeWorldMatrix(true);

    // Center the asset after scaling
    const fittedBounds = this.getNodeWorldBounds(assetRoot);
    if (!fittedBounds) {
      assetRoot.dispose();
      return;
    }
    const fittedCenter = fittedBounds.center;
    const modelBottom = fittedBounds.min.y;
    assetRoot.position.x += placeholderCenter.x - fittedCenter.x;
    assetRoot.position.y += placeholderFit.bottomY - modelBottom;
    assetRoot.position.z += placeholderCenter.z - fittedCenter.z;
    assetRoot.computeWorldMatrix(true);

    // Hide placeholder
    placeholderMesh.setEnabled(false);
    placeholderMesh.isVisible = false;
    assetRoot.setEnabled(true);

    // Store loaded asset info
    const floorId = this.getFloorIdFromMesh(placeholderMesh) || 'unknown';
    this.loadedAssets.set(instanceId, {
      id: instanceId,
      mesh: assetRoot,
      anchor: null,
      asset: 'school_rooftop',
      category: 'rooftop',
      floor: floorId,
      room: null,
    });
  }

  /**

   * Process all placeholders in the scene and replace them with assets

   */

  private async processPlaceholders(): Promise<void> {
    if (!this.scene || this.scene.isDisposed) {
      return;
    }
    const placeholders: AbstractMesh[] = [];
    const furnitureCount = { detected: 0, names: [] as string[] };
    const windowCount = { detected: 0, names: [] as string[] };
    const rooftopCount = { detected: 0, names: [] as string[] };

    // Find all placeholder meshes
    this.scene.meshes.forEach((mesh) => {
      if (mesh.name === '__root__' || !mesh.geometry) {
        return;
      }
      if (this.isFurniturePlaceholder(mesh)) {
        placeholders.push(mesh);
        furnitureCount.detected++;
        furnitureCount.names.push(mesh.name);
      } else if (this.isWindowPlaceholder(mesh)) {
        placeholders.push(mesh);
        windowCount.detected++;
        windowCount.names.push(mesh.name);
      } else if (this.isRooftopPlaceholder(mesh)) {
        placeholders.push(mesh);
        rooftopCount.detected++;
        rooftopCount.names.push(mesh.name);
      }
    });
    if (placeholders.length === 0) {
      // Log sample mesh names for debugging
      const sampleNames = this.scene.meshes
        .filter(m => m.name && m.name !== '__root__' && m.geometry)
        .slice(0, 20)
        .map(m => m.name);
      return;
    }

    // Replace placeholders in batches to avoid overwhelming the scene
    const batchSize = 5;
    let replacedCount = 0;
    let failedCount = 0;
    for (let i = 0; i < placeholders.length; i += batchSize) {
      const batch = placeholders.slice(i, i + batchSize);
      await Promise.all(batch.map(async (placeholder) => {
        try {
          if (this.isFurniturePlaceholder(placeholder)) {
            await this.replaceFurniturePlaceholder(placeholder);
            replacedCount++;
          } else if (this.isWindowPlaceholder(placeholder)) {
            await this.replaceWindowPlaceholder(placeholder);
            replacedCount++;
          } else if (this.isRooftopPlaceholder(placeholder)) {
            await this.replaceRooftopPlaceholder(placeholder);
            replacedCount++;
          }
        } catch (error) {
          failedCount++;
        }
      }));
    }
  }

  // ── Dual-view layer masks (ported from POC viewer.js _applyRoomAndSliceLayerMasks) ──

  /**
   * Apply per-floor visibility masks to room meshes, floor slices, and loaded assets.
   * When activeMaskFloorId is set, only meshes/assets on that floor are visible.
   * When activeMaskRoomId is also set, only assets in that room are visible.
   * Ported from POC viewer.js _applyRoomAndSliceLayerMasks.
   */
  public applyRoomAndSliceLayerMasks(floorId: string | null, roomId: string | null = null): void {
    if (!this.scene) return;
    this.activeMaskFloorId = floorId;
    this.activeMaskRoomId = roomId;
    const showAllFloors = floorId == null || floorId === '';
    const activeFloor = showAllFloors ? null : this.normalizeBabylonFloorNumber(floorId);
    const showAllRooms = roomId == null || roomId === '';
    const activeRoom = showAllRooms ? null : this.normalizeEntityId(roomId);

    // Helper: resolve floor ID from a mesh by walking parent hierarchy
    const floorFromParent = (node: any): string | null => {
      let cur = node;
      while (cur?.parent) {
        const nm = String(cur.parent.name || '');
        if (nm.startsWith('Floor_')) return nm.replace('Floor_', '');
        cur = cur.parent;
      }
      return null;
    };

    // Apply masks to scene meshes
    for (const m of this.scene.meshes || []) {
      const base = (m.name || '').split('/').pop() || '';

      // Skip live user avatar meshes — managed separately by LiveUserAssetService
      if (base.startsWith('liveUser_') || base.startsWith('cluster_')) {
        continue;
      }
      let meshFloorId: string | null = null;
      if (base.startsWith('Room_')) {
        meshFloorId = this.extractFloorIdFromRoomMesh(m);
      } else {
        meshFloorId = floorFromParent(m);
      }
      const meshFloor = meshFloorId ? this.normalizeBabylonFloorNumber(meshFloorId) : null;
      const isVisible = activeFloor === null || meshFloor === activeFloor;
      m.setEnabled(isVisible);
      m.isVisible = isVisible;
    }

    // Apply masks to floor slices - keep all floor slices visible for room footprints
    for (const [fid, slices] of this.floorSlices) {
      const sliceFloor = this.normalizeBabylonFloorNumber(fid);
      const isVisible = true;
      for (const slice of slices) {
        slice.setEnabled(isVisible);
        slice.isVisible = isVisible;
      }
    }

    // Apply masks to loaded assets (furniture, windows, rooftops)
    for (const entry of this.loadedAssets.values()) {
      if (!entry?.mesh) continue;
      const assetFloor = this.normalizeBabylonFloorNumber(entry.floor || '');
      const floorOk = activeFloor === null || assetFloor === activeFloor;
      const roomOk = activeRoom === null || !entry.room || this.normalizeEntityId(entry.room) === activeRoom;
      const isVisible = floorOk && roomOk;
      this.setHierarchyVisible(entry.mesh, isVisible);
    }

    // Handle user meshes from live user asset service
    const userAnchor = this.scene.getTransformNodeByName('__incident_user_anchor');
    if (userAnchor) {
      for (const child of userAnchor.getChildren()) {
        if (child.getChildMeshes) {
          const userFloorId = (child as any).metadata?.floorId || null;
          const userFloor = userFloorId ? this.normalizeBabylonFloorNumber(userFloorId) : null;
          const isVisible = activeFloor === null || userFloor === activeFloor;
          for (const mesh of child.getChildMeshes(false)) {
            mesh.setEnabled(isVisible);
            mesh.isVisible = isVisible;
          }
        }
      }
    }
  }

  private setHierarchyVisible(root: AbstractMesh | TransformNode, enabled: boolean): void {
    if (!root) return;
    if (root.setEnabled) root.setEnabled(!!enabled);
    if (typeof (root as any).isVisible === 'boolean') (root as any).isVisible = !!enabled;
    const childMeshes = root.getChildMeshes ? root.getChildMeshes(false) : [];
    for (const m of childMeshes) {
      if (m.setEnabled) m.setEnabled(!!enabled);
      if (typeof m.isVisible === 'boolean') m.isVisible = !!enabled;
    }
  }

  // ── Anchor-based asset placement (ported from POC AssetPlacement.js) ──

  /**
   * Build a FloorBounds map from the current floorMappingTable.
   */
  private getFloorBoundsMap(): Map<string, FloorBounds> {
    return AssetPlacementUtil.buildFloorBoundsMap(this.floorMappingTable);
  }

  /**
   * Ensure the AssetPlacementUtil instance is initialised for the current scene.
   */
  private ensureAssetPlacementUtil(): AssetPlacementUtil | null {
    if (!this.scene) return null;
    if (!this.assetPlacementUtil) {
      this.assetPlacementUtil = new AssetPlacementUtil(this.scene);
    }
    const boundsMap = this.getFloorBoundsMap();
    this.assetPlacementUtil.calibrate(boundsMap);
    return this.assetPlacementUtil;
  }

  /**
   * Place an asset mesh at an anchor node using category-based Y positioning,
   * scale-to-floor-height, and bottom snapping.
   *
   * @param meshRoot - The asset mesh/transform node to place
   * @param anchorId - The anchor ID (e.g. ANCHOR_F001_R06_WALL_001)
   * @param itemFloor - Optional explicit floor override
   * @param scaleMultiplier - Optional scale multiplier (e.g. 2 for beacons)
   * @returns true if placement succeeded
   */
  public placeAnchorAsset(
    meshRoot: AbstractMesh | TransformNode,
    anchorId: string,
    itemFloor?: string | null,
    scaleMultiplier?: number
  ): boolean {
    if (!this.scene || !meshRoot) return false;
    const util = this.ensureAssetPlacementUtil();
    if (!util) return false;
    const anchorNode = util.findAnchorNode(anchorId);
    if (!anchorNode) {
      return false;
    }
    const category = util.categoryFromAnchorId(anchorId);
    const boundsMap = this.getFloorBoundsMap();
    const floorId = util.resolveFloorForAsset(anchorNode, anchorId, itemFloor ?? null, boundsMap);
    const ok = util.place(meshRoot, anchorNode, category, {
      floorBoundsMap: boundsMap,
      floorId,
      scaleMultiplier: scaleMultiplier ?? 1,
    });
    if (ok) {
      // Store in loadedAssets for layer mask visibility tracking
      const roomId = util.resolveRoomForAsset({ room: null }, anchorId);
      const placementId = `anchor_${anchorId}_${Date.now()}`;
      this.loadedAssets.set(placementId, {
        id: placementId,
        mesh: meshRoot as AbstractMesh,
        anchor: anchorNode instanceof TransformNode ? anchorNode : null,
        asset: anchorId,
        category: 'furniture' as any,
        floor: floorId,
        room: roomId,
      });
    }
    return ok;
  }

  /**
   * Find all anchor nodes in the scene (nodes whose name starts with ANCHOR_).
   */
  public findSceneAnchors(): { id: string; node: AbstractMesh | TransformNode; category: AnchorCategory }[] {
    if (!this.scene) return [];
    const util = this.ensureAssetPlacementUtil();
    if (!util) return [];
    const anchors: { id: string; node: AbstractMesh | TransformNode; category: AnchorCategory }[] = [];
    const seen = new Set<string>();
    for (const tn of this.scene.transformNodes) {
      const name = tn.name || '';
      if (name.toUpperCase().startsWith('ANCHOR_') && !seen.has(name)) {
        seen.add(name);
        anchors.push({ id: name, node: tn, category: util.categoryFromAnchorId(name) });
      }
    }
    for (const m of this.scene.meshes) {
      const name = m.name || '';
      const base = name.split('/').pop() || '';
      if (base.toUpperCase().startsWith('ANCHOR_') && !seen.has(base)) {
        seen.add(base);
        anchors.push({ id: base, node: m, category: util.categoryFromAnchorId(base) });
      }
    }
    return anchors;
  }

  /**
   * Map an anchor category to a default asset ID for template loading.
   * This can be overridden by anchor naming conventions (e.g. ANCHOR_F001_R06_BEACON).
   */
  private assetIdForAnchor(anchorId: string, category: AnchorCategory): string | null {
    const upper = anchorId.toUpperCase();
    if (upper.includes('BEACON')) return 'beacon';
    // if (upper.includes('_CENTER_')) return 'human';
    // For wall/ceiling/floor anchors without a specific asset hint,
    // return null — these are typically used for spatial reference only
    return null;
  }

  /**
   * Scan the scene for ANCHOR_ nodes and place assets at each one using
   * the AssetPlacementUtil. This is the illum-live equivalent of the POC's
   * `assetManager.placeAssets(buildingId, null, floorBounds, scene)`.
   *
   * Called during parseModelMetadata after the floor mapping table is built.
   */
  private async placeAnchorBasedAssets(): Promise<void> {
    if (!this.scene || this.scene.isDisposed) return;
    const anchors = this.findSceneAnchors();
    if (anchors.length === 0) return;
    const util = this.ensureAssetPlacementUtil();
    if (!util) return;
    const boundsMap = this.getFloorBoundsMap();
    let placed = 0;
    for (const { id: anchorId, node: anchorNode, category } of anchors) {
      const assetId = this.assetIdForAnchor(anchorId, category);
      if (!assetId) continue;
      const placementId = `anchor_${anchorId}`;
      if (this.loadedAssets.has(placementId)) continue;
      try {
        const template = await this.loadAssetTemplate(assetId);
        if (!template) continue;
        const clone = template.clone(`${assetId}_clone_${anchorId}`, null, false);
        if (!clone) continue;
        this.enableHierarchy(clone);
        const floorId = util.resolveFloorForAsset(anchorNode, anchorId, null, boundsMap);
        const scaleMultiplier = assetId === 'beacon' ? this.visualConfig.beaconModelScale / 0.25 : 1;
        const ok = util.place(clone, anchorNode, category, {
          floorBoundsMap: boundsMap,
          floorId,
          scaleMultiplier,
        });
        if (ok) {
          const roomId = util.resolveRoomForAsset({ room: null }, anchorId);
          this.loadedAssets.set(placementId, {
            id: placementId,
            mesh: clone as AbstractMesh,
            anchor: anchorNode instanceof TransformNode ? anchorNode : null,
            asset: assetId,
            category: 'furniture',
            floor: floorId,
            room: roomId,
          });
          placed++;
        } else {
          clone.dispose();
        }
      } catch (e) {
      }
    }
  }

  /**

   * Clean up loaded assets when scene is reloaded

   */

  private cleanupLoadedAssets(): void {
    this.loadedAssets.forEach((asset) => {
      if (asset.mesh) {
        asset.mesh.dispose();
      }
    });
    this.loadedAssets.clear();
    this.assetTemplates.forEach((template) => {
      template.dispose();
    });
    this.assetTemplates.clear();
    this.assetPlacementUtil = null;
    this.restoreFloorCutaway();
    this.activeMaskFloorId = null;
    this.activeMaskRoomId = null;

  }

}

export type { ViewerRoomStatusColors } from '../../../core/utils/room-status-color.resolver';